// The main module: the matter as a practice management system holds it.
//
// **Smokeball's structure, Provey's look, Fluent's manners.** The layout is the
// one every conveyancer already reads — a matter header with the stage strip,
// tabs across the top, Matter details as a list of rows with a `+` where
// nothing is filled, the widget column on the right, a search-first picker for
// every party. The palette and type are Provey's (`T`, Inter). Spacing,
// control shapes and dialog anatomy follow Microsoft's Fluent 2 — a 4px grid,
// square-ish inputs, OK / Cancel on the right — so it sits beside Outlook and
// Teams without feeling like a different product.
//
// **Everything about what a matter holds is the middleware's.** The rows, the
// sections and their tabs, the fields only the practice record keeps, the
// roles, the stages: all of it arrives on `GET /practice/layout`. A field the
// contract-field registry already has is that field — it saves as a contract
// correction through `onSaveContractFields`, which is App.js's own save, so
// the Section 32 module and this screen are editing one value. See the
// middleware's docs/decisions/practice-core.md.
//
// `api` is App.js's `apiFetch`, handed in rather than imported, so the access
// key is attached in exactly one place.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { T, fieldInputProps, applyBrand, brandTokensFor, formatOpenedDate } from "./shared";

// ── What works yet, and what is still coming ────────────────────────────────
//
// **A part that does not work yet is shown, greyed, and says so** — rather
// than a button that looks ready and does half of what it suggests. Email
// only opens the mail app and files nothing; tasks have no real assignee and
// no reminders; events reach nobody's calendar. `?preview=on` in the address turns them on
// for the tab (remembered, like `?ui=`), for demos and testing; `?preview=off`
// turns them off again.
export const UNFINISHED = ["orderSearches", "email", "tasks", "events", "intake", "emails", "messages", "activity", "time", "insights", "newDocument", "precedent", "scan", "phone", "billing", "timeline", "workflows"];
const PREVIEW_KEY = "provey.preview";
export const readPreview = () => {
  try {
    const said = (new URLSearchParams(window.location.search).get("preview") || "").trim().toLowerCase();
    if (["on", "1", "true", "all"].includes(said)) sessionStorage.setItem(PREVIEW_KEY, "on");
    else if (["off", "0", "false"].includes(said)) sessionStorage.removeItem(PREVIEW_KEY);
    return sessionStorage.getItem(PREVIEW_KEY) === "on";
  } catch { return false; }
};
export const PREVIEW_ON = readPreview();
// The one rule for "is this part on": finished, or the tab asked for preview.
export const featureReady = (feature, preview = PREVIEW_ON) => preview || !UNFINISHED.includes(feature);
const SOON = "Coming soon — not working yet";

// ── Fluent-shaped primitives, in the firm's colour ─────────────────────────
//
// The brand is `T.brand` — a CSS variable the firm's setting fills, Outlook
// blue by default — and it is used for very little: the primary button, the
// selected tab, links, and the stage. Everything else is Fluent's neutrals.

const FONT = "Inter, sans-serif";
const R = 4; // control radius: Fluent's "medium"
const SHADOW = "0 0 8px rgba(0,0,0,.12), 0 32px 64px rgba(0,0,0,.14)"; // Fluent's shadow64, for dialogs

const inputStyle = {
  width: "100%", boxSizing: "border-box", height: 32, padding: "0 10px", fontSize: 14, fontFamily: FONT,
  color: T.fg1, background: T.card, border: `1px solid ${T.strokeInput}`, borderBottomColor: T.fg3,
  borderRadius: R, outline: "none",
};
const labelStyle = { fontSize: 13, color: T.fg3, fontFamily: FONT };

const Button = ({ children, onClick, primary = false, subtle = false, disabled = false, type = "button", title, label }) => (
  <button type={type} onClick={onClick} disabled={disabled} title={title} aria-label={label}
    style={{
      display: "inline-flex", alignItems: "center", gap: 6, height: 32, padding: subtle ? "0 8px" : "0 12px",
      fontSize: 14, fontFamily: FONT, fontWeight: subtle ? 400 : 600, borderRadius: R,
      cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.45 : 1, whiteSpace: "nowrap",
      background: primary ? T.brand : subtle ? "transparent" : T.card,
      color: primary ? T.onBrand : subtle ? T.fg2 : T.fg1,
      border: `1px solid ${primary ? T.brand : subtle ? "transparent" : T.strokeInput}`,
    }}>
    {children}
  </button>
);

const LinkButton = ({ children, onClick, danger = false }) => (
  <button type="button" onClick={onClick}
    style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 13, fontFamily: FONT,
      color: danger ? T.danger : T.brand }}>
    {children}
  </button>
);

const Icon = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {(Array.isArray(d) ? d : [d]).map(p => <path key={p} d={p} />)}
  </svg>
);
const ICON = {
  plus: ["M12 5v14", "M5 12h14"],
  chevron: "M9 6l6 6-6 6",
  down: "M6 9l6 6 6-6",
  close: ["M18 6 6 18", "M6 6l12 12"],
  person: ["M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2", "M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8"],
  building: ["M3 21h18", "M5 21V7l7-4 7 4v14", "M9 9h1", "M14 9h1", "M9 13h1", "M14 13h1", "M10 21v-4h4v4"],
  pin: ["M12 17v5", "M9 3h6l-1 7 4 3H6l4-3z"],
  compile: ["M21 12a9 9 0 1 1-3-6.7L21 8", "M21 3v5h-5"],
  stop: "M6 6h12v12H6z",
  send: ["M22 2 11 13", "M22 2l-7 20-4-9-9-4z"],
  edit: ["M12 20h9", "M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"],
  list: ["M8 6h13", "M8 12h13", "M8 18h13", "M3 6h.01", "M3 12h.01", "M3 18h.01"],
  eye: ["M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z", "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6"],
  print: ["M6 9V2h12v7", "M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2", "M6 14h12v8H6z"],
  memo: ["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z", "M14 2v6h6", "M8 13h8", "M8 17h5"],
  trash: ["M3 6h18", "M8 6V4h8v2", "M19 6l-1 14H6L5 6"],
  mail: ["M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z", "M3 7l9 6 9-6"],
  popout: ["M15 3h6v6", "M10 14 21 3", "M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5"],
  folder: "M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z",
  import: ["M12 3v12", "M7 10l5 5 5-5", "M5 21h14"],
  check: "M5 12l5 5 9-11",
  message: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
  scan: ["M3 7V5a2 2 0 0 1 2-2h2", "M17 3h2a2 2 0 0 1 2 2v2", "M21 17v2a2 2 0 0 1-2 2h-2", "M7 21H5a2 2 0 0 1-2-2v-2", "M7 12h10"],
  phone: "M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z",
  activity: "M22 12h-4l-3 9L9 3l-3 9H2",
  more: ["M5 12h.01", "M12 12h.01", "M19 12h.01"],
  sign: ["M3 17c2-2 4-4 6-2s1 4 3 4 3-6 5-6 2 2 4 2", "M3 21h18"],
  people: ["M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2", "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z", "M23 21v-2a4 4 0 0 0-3-3.9", "M16 3.1a4 4 0 0 1 0 7.8"],
  dollar: ["M12 2v20", "M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"],
  clock: ["M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z", "M12 6v6l4 2"],
  file: ["M6 2h9l5 5v15H6z", "M14 2v6h6"],
  search: ["M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z", "M20 20l-3.5-3.5"],
  calendar: ["M8 2v4", "M16 2v4", "M3 10h18", "M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"],
  duties: ["M3 21h18", "M4 10h16", "M12 3l8 5H4z", "M6 10v8", "M10 10v8", "M14 10v8", "M18 10v8"],
  gear: ["M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z", "M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"],
};

// A white card on the grey page, Fluent's "card" with its soft shadow.
const Card = ({ children, style, label }) => (
  <section aria-label={label} style={{ background: T.card, borderRadius: 8, boxShadow: T.shadowCard, minWidth: 0, ...style }}>{children}</section>
);
const CardHead = ({ title, count, action }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 16px 6px" }}>
    <h2 style={{ margin: 0, flex: 1, fontSize: 14, fontWeight: 600, color: T.fg1 }}>
      {title}{count !== undefined && count !== null && <span style={{ color: T.fg4, fontWeight: 400, marginLeft: 6 }}>{count}</span>}
    </h2>
    {action}
  </div>
);

// A small rounded tag. Colour only where it needs attention.
const PILL = {
  neutral: { color: T.fg2, background: T.card, border: T.stroke },
  brand: { color: T.brandPressed, background: T.brandTint, border: T.brandStroke },
  warn: { color: T.warn, background: T.warnTint, border: T.warnStroke },
  danger: { color: T.danger, background: T.dangerTint, border: T.dangerStroke },
};
const Pill = ({ tone = "neutral", children }) => {
  const c = PILL[tone] || PILL.neutral;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", height: 20, padding: "0 8px", borderRadius: 10,
      fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", color: c.color, background: c.background, border: `1px solid ${c.border}` }}>
      {children}
    </span>
  );
};

// A dialog: title, scrolling body, actions bottom-right with the primary last.
// Escape and a press outside close it, the way a Fluent dialog does.
const Dialog = ({ title, onClose, footer, width = 760, children, label }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return createPortal((
    <div onMouseDown={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 400, display: "flex",
        alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div role="dialog" aria-label={label || title} onMouseDown={e => e.stopPropagation()}
        style={{ background: T.card, borderRadius: 8, boxShadow: SHADOW, width: "100%", maxWidth: width,
          maxHeight: "calc(100vh - 32px)", display: "flex", flexDirection: "column", fontFamily: FONT }}>
        <div style={{ display: "flex", alignItems: "center", padding: "20px 24px 8px" }}>
          <h2 style={{ margin: 0, flex: 1, fontSize: 20, lineHeight: "28px", fontWeight: 600, color: T.fg1 }}>{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{ background: "none", border: "none", cursor: "pointer", color: T.fg2, padding: 4 }}>
            <Icon d={ICON.close} size={18} />
          </button>
        </div>
        <div style={{ padding: "4px 24px 8px", overflowY: "auto", flex: 1 }}>{children}</div>
        {footer && (
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, padding: "16px 24px 20px" }}>{footer}</div>
        )}
      </div>
    </div>
  ), document.body);
};

// **One row that scrolls sideways**, never two: wrapped, the second row stood
// a whole tab's padding under the first, and on a phone that was a band of
// white between Matter… and Messages….
const Tabs = ({ tabs, active, onChange, size = 14, wrap = false }) => (
  <div role="tablist" style={{ display: "flex", gap: wrap ? "0 22px" : 22, flexWrap: wrap ? "wrap" : "nowrap", overflowX: wrap ? "visible" : "auto", scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
    {tabs.map(([key, label, count, soon]) => (
      <button key={key} role="tab" type="button" aria-selected={active === key} onClick={() => !soon && onChange(key)}
        aria-disabled={soon || undefined} title={soon ? SOON : undefined}
        style={{ background: "none", border: "none", padding: "10px 0 12px", cursor: soon ? "not-allowed" : "pointer", fontSize: size,
          fontFamily: FONT, fontWeight: active === key ? 600 : 400, color: soon ? T.fg4 : active === key ? T.fg1 : T.fg3,
          position: "relative", whiteSpace: "nowrap", flexShrink: 0, boxShadow: active === key ? `inset 0 -3px 0 ${T.brand}` : "none" }}>
        {label}{count ? <span style={{ color: T.fg4, fontSize: 12, marginLeft: 4, fontWeight: 400 }}>{count}</span> : null}
        {soon && <span style={{ fontSize: 10, fontWeight: 600, color: T.fg4, border: `1px solid ${T.stroke}`, borderRadius: 8, padding: "0 5px", marginLeft: 6, lineHeight: "14px" }}>Soon</span>}
      </button>
    ))}
  </div>
);

// A titled section inside a dialog: the heading and a hairline running out.
const Fieldset = ({ legend, children }) => (
  <fieldset style={{ border: "none", padding: 0, margin: "0 0 16px", minWidth: 0 }}>
    {legend && (
      <legend style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: 0, margin: "8px 0 8px",
        fontSize: 14, fontWeight: 600, color: T.fg1 }}>
        {legend}<span style={{ flex: 1, height: 1, background: T.stroke }} />
      </legend>
    )}
    {children}
  </fieldset>
);

// ── Values, read and written ────────────────────────────────────────────────

export const formatDay = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};

// "20/10/2026" → "2026-10-20", or null for anything that is not a real day.
// Day first, always: this is an Australian office, and a browser set to US
// English must not turn 03/04 into the fourth of March.
export const isoFromDmy = (text) => {
  const m = /^\s*(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})\s*$/.exec(String(text ?? ""));
  if (!m) return null;
  const [d, mo, y] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
  return `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

// **A date box that reads dd/mm/yyyy whatever the browser's language.** A
// native date input draws itself in the browser's locale, so a machine set to
// US English showed mm/dd/yyyy. This is a text box that takes the day first,
// with the browser's own calendar one press away. The value in and out is
// ISO (YYYY-MM-DD), as the middleware stores it; a half-typed date is shown
// as typed and not sent until it is a real day.
const DateInput = ({ id, value, onChange, label, width }) => {
  const [text, setText] = useState(() => formatDay(value));
  const picker = useRef(null);
  useEffect(() => {
    setText(prev => (isoFromDmy(prev) === (value || null) || (!value && prev.trim() === "") ? prev : formatDay(value)));
  }, [value]);
  const bad = text.trim() !== "" && !isoFromDmy(text);
  const type = (t) => {
    setText(t);
    if (!t.trim()) onChange("");
    else { const iso = isoFromDmy(t); if (iso) onChange(iso); }
  };
  const openPicker = () => {
    const el = picker.current;
    if (!el) return;
    try { el.showPicker(); } catch { el.focus(); }
  };
  return (
    <span style={{ position: "relative", display: "flex", width: width || "100%", flex: width ? "none" : undefined }}>
      <input id={id} aria-label={label} placeholder="dd/mm/yyyy" inputMode="numeric" value={text} aria-invalid={bad || undefined}
        onChange={e => type(e.target.value)}
        style={{ ...inputStyle, paddingRight: 34, ...(bad ? { border: `1px solid ${T.danger}` } : {}) }} />
      <button type="button" aria-label={`Choose ${label || "a date"}`} onClick={openPicker}
        style={{ position: "absolute", right: 2, top: 2, height: 28, width: 30, display: "inline-flex", alignItems: "center",
          justifyContent: "center", background: "none", border: "none", cursor: "pointer", color: T.fg3 }}>
        <Icon d={ICON.calendar} size={15} />
      </button>
      <input ref={picker} type="date" tabIndex={-1} aria-hidden="true" value={value || ""}
        onChange={e => { onChange(e.target.value); setText(formatDay(e.target.value)); }}
        style={{ position: "absolute", right: 0, bottom: 0, width: 1, height: 1, opacity: 0, border: 0, padding: 0, pointerEvents: "none" }} />
    </span>
  );
};

export const displayValue = (def = {}, value) => {
  if (value === undefined || value === null || value === "") return "";
  if (def.type === "flag") return value === true ? "Yes" : value === false ? "No" : "";
  if (def.type === "date") return formatDay(value) || String(value);
  if (def.type === "money") {
    const n = Number(String(value).replace(/[$,\s]/g, ""));
    return Number.isFinite(n) && String(value).trim() !== ""
      ? `$${n.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : String(value);
  }
  if (Array.isArray(value)) return value.filter(Boolean).join("; ");
  return String(value);
};

// The edit box a value starts in. Lists are a value per line.
const toInput = (def = {}, value) => {
  if (value === undefined || value === null) return "";
  if (def.type === "flag") return value === true ? "yes" : value === false ? "no" : "";
  if (Array.isArray(value)) return value.join("\n");
  return String(value);
};

// What the edit box sends back. **A flag has three states**: yes, no, and
// cleared — and cleared is `""`, never `false`.
export const fromInput = (def = {}, text) => {
  if (def.type === "flag") return text === "yes" ? true : text === "no" ? false : "";
  if (def.type === "list") return String(text).split("\n").map(s => s.trim()).filter(Boolean);
  return text;
};

// The changed values of one dialog, split by who keeps them: the contract-field
// registry or the practice record.
export const splitChanges = (draft, original, recordNames) => {
  const contract = {};
  const record = {};
  for (const [name, text] of Object.entries(draft)) {
    if (text === original[name]) continue;
    (recordNames.has(name) ? record : contract)[name] = text;
  }
  return { contract, record };
};

const FieldInput = ({ def, value, onChange, id }) => {
  if (def.type === "flag") {
    return (
      <select id={id} value={value} onChange={e => onChange(e.target.value)} style={inputStyle}>
        <option value="">—</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    );
  }
  const options = def.options || null;
  if (options) {
    return (
      <select id={id} value={value} onChange={e => onChange(e.target.value)} style={inputStyle}>
        <option value="">—</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
        {value && !options.includes(value) && <option value={value}>{value}</option>}
      </select>
    );
  }
  if (def.type === "date") return <DateInput id={id} value={value} onChange={onChange} label={def.label} />;
  if (def.type === "longtext" || def.type === "list") {
    return <textarea id={id} value={value} onChange={e => onChange(e.target.value)} rows={def.type === "list" ? 3 : 4}
      style={{ ...inputStyle, height: "auto", padding: "6px 8px", resize: "vertical", lineHeight: 1.5 }} />;
  }
  return <input id={id} {...fieldInputProps(def)} value={value} onChange={e => onChange(e.target.value)} style={inputStyle} />;
};

// ── Talking to the middleware ───────────────────────────────────────────────

// One `call` per `api`, shared by every component handed it, so the layout
// cache below is shared too.
const calls = new WeakMap();
const callFor = (api) => {
  if (!calls.has(api)) calls.set(api, makeCall(api));
  return calls.get(api);
};
const useCall = (api) => useMemo(() => callFor(api), [api]);
const makeCall = (api) => (async (path, { method = "GET", body } = {}) => {
  const res = await api(path, {
    method,
    ...(body !== undefined ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) } : {}),
  });
  let data = {};
  try { data = await res.json(); } catch { data = {}; }
  if (!res.ok || data.ok === false) {
    const err = new Error(data.error || `The server answered ${res.status}`);
    err.status = res.status;
    err.code = data.code;
    throw err;
  }
  return data;
});

// The layout is the same for every matter: read once per page load — once per
// connection, so a test handing in its own `api` is handed its own layout.
const layoutRequests = new WeakMap();
export const useLayout = (call) => {
  const [layout, setLayout] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    if (!layoutRequests.has(call)) {
      layoutRequests.set(call, call("/practice/layout").catch((e) => { layoutRequests.delete(call); throw e; }));
    }
    layoutRequests.get(call).then(d => { if (live) setLayout(d); }).catch(e => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [call]);
  return { layout, error };
};

// ── Contacts: the picker and the card ───────────────────────────────────────

const ContactTable = ({ rows, selected, onSelect, onOpen, sort, onSort }) => (
  <div style={{ border: `1px solid ${T.stroke}`, borderRadius: R, overflowX: "auto" }}>
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: FONT }}>
      <thead>
        <tr style={{ background: T.bg, textAlign: "left" }}>
          {[["name", "Name"], [null, "Contact person"], [null, "Phone"], [null, "Email"], ["updatedAt", "Last updated"]].map(([key, label]) => (
            <th key={label} style={{ padding: "8px 10px", fontWeight: 600, color: T.fg1, whiteSpace: "nowrap" }}>
              {key ? (
                <button type="button" onClick={() => onSort(key)}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit", color: "inherit" }}>
                  {label}{sort === key ? " ▾" : ""}
                </button>
              ) : label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.id} onClick={() => onSelect && onSelect(r)} onDoubleClick={() => onOpen && onOpen(r)}
            aria-selected={selected === r.id}
            style={{ cursor: "pointer", borderTop: `1px solid ${T.stroke}`, background: selected === r.id ? T.brandTint : T.card }}>
            <td style={{ padding: "7px 10px" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ color: T.fg4 }}><Icon d={r.kind === "organisation" ? ICON.building : ICON.person} size={14} /></span>
                {r.name}
              </span>
            </td>
            <td style={{ padding: "7px 10px", color: T.fg3 }}>{r.contactPerson}</td>
            <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>{r.phone}</td>
            <td style={{ padding: "7px 10px", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>{r.email}</td>
            <td style={{ padding: "7px 10px", color: T.fg3, whiteSpace: "nowrap" }}>{formatDay(r.updatedAt)}</td>
          </tr>
        ))}
        {rows.length === 0 && (
          <tr><td colSpan={5} style={{ padding: 16, color: T.fg3, textAlign: "center" }}>No contacts found.</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

const Pager = ({ page, pages, total, onPage }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 12.5, color: T.fg3 }}>
    <Button subtle disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</Button>
    <span>Page {page} of {pages} · {total} contact{total === 1 ? "" : "s"}</span>
    <Button subtle disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</Button>
  </div>
);

const useContactSearch = (call, { pageSize = 25 } = {}) => {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("name");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ rows: [], page: 1, pages: 1, total: 0 });
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      const qs = new URLSearchParams({ q: query, sort, page: String(page), pageSize: String(pageSize) });
      call(`/practice/contacts?${qs}`).then(d => { if (live) { setResult(d); setError(""); } })
        .catch(e => { if (live) setError(e.message); });
    }, 200);
    return () => { live = false; clearTimeout(t); };
  }, [call, query, sort, page, pageSize, tick]);
  return {
    query, setQuery: (q) => { setQuery(q); setPage(1); }, sort, setSort, page, setPage, result, error,
    refresh: () => setTick(n => n + 1),
  };
};

// "Select a Vendor's Agent": search first, create if not found.
export const ContactPicker = ({ call, layout, roleLabel, onPicked, onClose }) => {
  const search = useContactSearch(call);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  if (creating) {
    return <ContactForm call={call} layout={layout} heading={roleLabel}
      onSaved={(contact) => onPicked(contact)} onClose={() => setCreating(false)} />;
  }
  return (
    <Dialog title={`Select a ${roleLabel}`} onClose={onClose} width={860}
      footer={<>
        <Button onClick={onClose}>Cancel</Button>
        <Button primary disabled={!selected} onClick={() => onPicked(selected)}>Continue</Button>
      </>}>
      <label htmlFor="practice-contact-search" style={labelStyle}>Search existing contacts</label>
      <input id="practice-contact-search" autoFocus value={search.query} onChange={e => search.setQuery(e.target.value)}
        placeholder="Name, email, phone or suburb" style={{ ...inputStyle, margin: "4px 0 10px" }} />
      <p style={{ fontSize: 13, margin: "0 0 10px", color: T.fg1 }}>
        Can't find an existing contact? <LinkButton onClick={() => setCreating(true)}>Create a new contact</LinkButton>
      </p>
      {search.error && <p role="alert" style={{ color: T.danger, fontSize: 12.5 }}>{search.error}</p>}
      <ContactTable rows={search.result.rows} selected={selected?.id} sort={search.sort} onSort={search.setSort}
        onSelect={setSelected} onOpen={(r) => onPicked(r)} />
      <Pager page={search.result.page} pages={search.result.pages} total={search.result.total} onPage={search.setPage} />
    </Dialog>
  );
};

const blankPerson = () => ({ title: "", givenNames: "", middleNames: "", lastName: "", gender: "", dateOfBirth: "", homePhone: "",
  workPhone: "", workExt: "", mobile: "", email: "", otherEmails: "", dear: "informal", dearCustom: "",
  previousNames: "", maritalStatus: "", occupation: "", birthPlace: "", birthState: "", birthCountry: "",
  citizenship: "", nationality: "", interpreterLanguage: "", residency: "" });

// A list the card keeps (other emails, domains) is typed as one line; the
// middleware splits and checks it.
const asLine = (v) => (Array.isArray(v) ? v.join(", ") : v || "");
const blankStreet = () => ({ careOf: "", buildingLevel: "", unitType: "", unitNumber: "", streetNumber: "",
  streetName: "", streetType: "", suburb: "", state: "VIC", postcode: "", country: "Australia" });

export const draftOf = (contact) => ({
  kind: contact?.kind || "person",
  people: (contact?.people?.length ? contact.people : [{}]).map(p => ({ ...blankPerson(), ...p, otherEmails: asLine(p.otherEmails) })),
  organisation: {
    name: "", abn: "", acnType: "ACN", acn: "", phone: "", fax: "", email: "", ...(contact?.organisation || {}),
    otherEmails: asLine(contact?.organisation?.otherEmails), domains: asLine(contact?.organisation?.domains),
  },
  addresses: {
    street: { ...blankStreet(), ...(contact?.addresses?.street || {}) },
    postal: { careOf: "", boxType: "", boxNumber: "", suburb: "", state: "VIC", postcode: "", ...(contact?.addresses?.postal || {}) },
    forwarding: { ...blankStreet(), ...(contact?.addresses?.forwarding || {}) },
    dx: { dxNumber: "", exchange: "", ...(contact?.addresses?.dx || {}) },
  },
  notes: contact?.notes || "",
  voiStatus: contact?.voiStatus || "",
});

const Row = ({ label, htmlFor, tag, children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "minmax(110px, 140px) 1fr", alignItems: "center", gap: 8, marginBottom: 6 }}>
    <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2, minWidth: 0 }}>
      <label htmlFor={htmlFor} style={labelStyle}>{label}</label>{tag}
    </span>
    <div style={{ display: "flex", gap: 6, minWidth: 0 }}>{children}</div>
  </div>
);

// **Where a card's value came from**, while staff have left it as it was:
// the client's own form, or the office's record. The middleware keeps the
// marks (`contact.sources`) and drops one the moment the value is retyped, so
// a value with no tag is staff's.
const SOURCE_TAG = { client: "Client form", office: "Office record" };
const SourceTag = ({ source }) => (source ? (
  <span title={`From the ${SOURCE_TAG[source].toLowerCase()}, not yet changed by staff`}
    style={{ fontSize: 10.5, lineHeight: "14px", padding: "0 6px", borderRadius: 999, border: `1px solid ${T.stroke}`,
      color: T.fg3, background: T.card, whiteSpace: "nowrap", fontWeight: 400 }}>{SOURCE_TAG[source]}</span>
) : null);

const valueAt = (record, path) => String(path).split(".").reduce((at, key) => (at == null ? undefined : at[key]), record);

const TextIn = ({ id, value, onChange, placeholder, width, type = "text" }) => (
  <input id={id} type={type} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)}
    style={{ ...inputStyle, ...(width ? { width, flex: "none" } : {}) }} />
);

const Select = ({ id, value, onChange, options, width }) => (
  <select id={id} value={value} onChange={e => onChange(e.target.value)}
    style={{ ...inputStyle, ...(width ? { width, flex: "none" } : {}) }}>
    <option value="">—</option>
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);

const STREET_TYPES = ["Avenue", "Boulevard", "Circuit", "Close", "Court", "Crescent", "Drive", "Grove", "Highway",
  "Lane", "Parade", "Place", "Road", "Square", "Street", "Terrace", "Way"];
const UNIT_TYPES = ["Unit", "Apartment", "Flat", "Shop", "Suite", "Townhouse", "Villa"];

// Person or Firm/Business/Organisation — Smokeball's Create a contact, laid out
// the same way: the organisation's details, its address tabs, then the people.
// `onSubmit`, when given, takes the card instead of saving it to Contacts —
// the review of an intake's card hands it to the matter's own route. `title`
// and `intro` (shown above the card) belong to that review.
export const ContactForm = ({ call, layout, contact, heading, onSaved, onClose, onDeleted, onSubmit, title, intro, submitLabel }) => {
  const [draft, setDraft] = useState(() => draftOf(contact));
  const [cardTab, setCardTab] = useState("contact");
  const [addressTab, setAddressTab] = useState("street");
  const [forwardingOn, setForwardingOn] = useState(() => Boolean(contact?.addresses?.forwarding));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const opts = layout?.contactOptions || {};
  const isOrg = draft.kind === "organisation";

  const set = (path, value) => setDraft((d) => {
    const next = JSON.parse(JSON.stringify(d));
    let at = next;
    for (const key of path.slice(0, -1)) at = at[key];
    at[path[path.length - 1]] = value;
    return next;
  });
  // The tag a value wears while it is still what the source said. An address
  // is compared on the boxes the source filled, since the form shows its
  // defaults (VIC, Australia) in the others.
  const sourceAt = (path) => {
    const source = contact?.sources?.[path];
    if (!SOURCE_TAG[source]) return null;
    const was = valueAt(contact, path);
    const now = valueAt(draft, path);
    if (was && typeof was === "object") {
      return Object.entries(was).every(([k, v]) => String(v ?? "") === String(now?.[k] ?? "")) ? source : null;
    }
    return String(was ?? "") === String(now ?? "") ? source : null;
  };
  const tagAt = (path) => <SourceTag source={sourceAt(path)} />;
  const addPerson = () => setDraft(d => ({ ...d, people: [...d.people, blankPerson()] }));
  const removePerson = (i) => setDraft(d => ({ ...d, people: d.people.filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true); setError("");
    try {
      const body = { ...draft, organisation: isOrg ? draft.organisation : null,
        addresses: { ...draft.addresses, forwarding: !isOrg && forwardingOn ? draft.addresses.forwarding : {} } };
      if (onSubmit) { await onSubmit(body); return; }
      const data = contact?.id
        ? await call(`/practice/contacts/${encodeURIComponent(contact.id)}`, { method: "POST", body })
        : await call("/practice/contacts", { method: "POST", body });
      onSaved(data.contact, data);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!contact?.id || !window.confirm(`Delete ${contact.name || "this contact"}? This cannot be undone.`)) return;
    setSaving(true); setError("");
    try {
      await call(`/practice/contacts/${encodeURIComponent(contact.id)}`, { method: "DELETE" });
      onDeleted && onDeleted(contact);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const personBlock = (p, i) => (
    <div key={i} style={{ paddingTop: i ? 10 : 0, marginTop: i ? 10 : 0, borderTop: i ? `1px solid ${T.stroke}` : "none" }}>
      {draft.people.length > 1 && (
        <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, flex: 1 }}>{isOrg ? `Contact ${i + 1}` : `Person ${i + 1}`}</span>
          {draft.people.length > 1 && <LinkButton danger onClick={() => removePerson(i)}>Remove</LinkButton>}
        </div>
      )}
      <Row label="Title" htmlFor={`p${i}-title`} tag={tagAt(`people.${i}.title`)}>
        <Select id={`p${i}-title`} value={p.title} onChange={v => set(["people", i, "title"], v)} options={opts.titles || []} width={110} />
        {!isOrg && <Select id={`p${i}-gender`} value={p.gender} onChange={v => set(["people", i, "gender"], v)} options={opts.genders || []} width={140} />}
      </Row>
      {/* triConvey's three boxes: first, middle, last. */}
      <Row label="Name" htmlFor={`p${i}-given`} tag={<SourceTag source={sourceAt(`people.${i}.lastName`) || sourceAt(`people.${i}.givenNames`)} />}>
        <input id={`p${i}-given`} aria-label="First name" placeholder="First" value={p.givenNames} onChange={e => set(["people", i, "givenNames"], e.target.value)} style={inputStyle} />
        <input aria-label="Middle name" placeholder="Middle" value={p.middleNames} onChange={e => set(["people", i, "middleNames"], e.target.value)} style={inputStyle} />
        <input aria-label="Last name" placeholder="Last" value={p.lastName} onChange={e => set(["people", i, "lastName"], e.target.value)} style={inputStyle} />
      </Row>
      {isOrg && <Row label="Role" htmlFor={`p${i}-role`} tag={tagAt(`people.${i}.role`)}><TextIn id={`p${i}-role`} value={p.role || ""} onChange={v => set(["people", i, "role"], v)} placeholder="e.g. Conveyancer, Salesperson" /></Row>}
      {!isOrg && (
        <Row label="Dear">
          {["formal", "informal", "custom"].map(style => (
            <label key={style} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13, marginRight: 8 }}>
              <input type="radio" name={`p${i}-dear`} checked={p.dear === style} onChange={() => set(["people", i, "dear"], style)} />
              {style[0].toUpperCase() + style.slice(1)}
            </label>
          ))}
          {p.dear === "custom" && <TextIn value={p.dearCustom} onChange={v => set(["people", i, "dearCustom"], v)} />}
        </Row>
      )}
      {!isOrg && <Row label="Home phone" htmlFor={`p${i}-home`}><TextIn id={`p${i}-home`} value={p.homePhone} onChange={v => set(["people", i, "homePhone"], v)} /></Row>}
      <Row label="Work phone" htmlFor={`p${i}-work`}>
        <TextIn id={`p${i}-work`} value={p.workPhone} onChange={v => set(["people", i, "workPhone"], v)} />
        <TextIn value={p.workExt} onChange={v => set(["people", i, "workExt"], v)} placeholder="Ext." width={70} />
      </Row>
      <Row label="Mobile" htmlFor={`p${i}-mobile`} tag={tagAt(`people.${i}.mobile`)}><TextIn id={`p${i}-mobile`} value={p.mobile} onChange={v => set(["people", i, "mobile"], v)} /></Row>
      <Row label="Email" htmlFor={`p${i}-email`} tag={tagAt(`people.${i}.email`)}><TextIn id={`p${i}-email`} type="email" value={p.email} onChange={v => set(["people", i, "email"], v)} /></Row>
      <Row label="Other emails" htmlFor={`p${i}-other`}><TextIn id={`p${i}-other`} value={p.otherEmails} onChange={v => set(["people", i, "otherEmails"], v)} placeholder="Addresses they also write from" /></Row>
    </div>
  );

  // triConvey's Additional Details: who the person is beyond how to reach
  // them. Residency is ours — the question foreign-purchaser duty turns on.
  const additionalBlock = (p, i) => {
    const put = (key) => (v) => set(["people", i, key], v);
    return (
      <div key={i} style={{ paddingTop: i ? 10 : 0, marginTop: i ? 10 : 0, borderTop: i ? `1px solid ${T.stroke}` : "none" }}>
        {draft.people.length > 1 && <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>
          {[p.givenNames, p.lastName].filter(Boolean).join(" ") || `Person ${i + 1}`}</div>}
        <Fieldset legend="General details">
          <Row label="Previous names" htmlFor={`p${i}-prev`}><TextIn id={`p${i}-prev`} value={p.previousNames} onChange={put("previousNames")} /></Row>
          <Row label="Marital status" htmlFor={`p${i}-marital`}><Select id={`p${i}-marital`} value={p.maritalStatus} onChange={put("maritalStatus")} options={opts.maritalStatus || []} width={170} /></Row>
          <Row label="Occupation" htmlFor={`p${i}-occupation`}><TextIn id={`p${i}-occupation`} value={p.occupation} onChange={put("occupation")} /></Row>
        </Fieldset>
        <Fieldset legend="Birth details">
          <Row label="Date" htmlFor={`p${i}-dob`} tag={tagAt(`people.${i}.dateOfBirth`)}>
            <DateInput id={`p${i}-dob`} label="Date of birth" value={p.dateOfBirth} onChange={put("dateOfBirth")} width={170} />
          </Row>
          <Row label="Suburb / town, state" htmlFor={`p${i}-birthplace`}>
            <TextIn id={`p${i}-birthplace`} value={p.birthPlace} onChange={put("birthPlace")} placeholder="Suburb the person was born in" />
            <Select value={p.birthState} onChange={put("birthState")} options={opts.states || []} width={100} />
          </Row>
          <Row label="Country" htmlFor={`p${i}-birthcountry`}><TextIn id={`p${i}-birthcountry`} value={p.birthCountry} onChange={put("birthCountry")} /></Row>
        </Fieldset>
        <Fieldset legend="Citizenship and residency">
          <Row label="Country of citizenship" htmlFor={`p${i}-citizenship`} tag={tagAt(`people.${i}.citizenship`)}>
            <TextIn id={`p${i}-citizenship`} value={p.citizenship} onChange={put("citizenship")} />
          </Row>
          <Row label="Nationality" htmlFor={`p${i}-nationality`}><TextIn id={`p${i}-nationality`} value={p.nationality} onChange={put("nationality")} /></Row>
          <Row label="Language of interpreter" htmlFor={`p${i}-interpreter`}><TextIn id={`p${i}-interpreter`} value={p.interpreterLanguage} onChange={put("interpreterLanguage")} placeholder="If applicable" /></Row>
          <Row label="Residency" htmlFor={`p${i}-residency`} tag={tagAt(`people.${i}.residency`)}>
            <Select id={`p${i}-residency`} value={p.residency} onChange={put("residency")} options={opts.residency || []} />
          </Row>
        </Fieldset>
      </div>
    );
  };

  const postal = draft.addresses.postal;
  const dx = draft.addresses.dx;

  // One street address's rows — the residential (or an organisation's street)
  // address, and the forwarding address, share them.
  const streetRows = (key, id) => {
    const a = draft.addresses[key];
    const put = (field) => (v) => set(["addresses", key, field], v);
    return <>
      <Row label="Care of" htmlFor={`${id}-careof`}><TextIn id={`${id}-careof`} value={a.careOf} onChange={put("careOf")} placeholder="e.g. C/- XYZ Accounting" /></Row>
      <Row label="Building / level" htmlFor={`${id}-level`}><TextIn id={`${id}-level`} value={a.buildingLevel} onChange={put("buildingLevel")} /></Row>
      <Row label="Unit type, no.">
        <Select value={a.unitType} onChange={put("unitType")} options={UNIT_TYPES} width={140} />
        <TextIn value={a.unitNumber} onChange={put("unitNumber")} />
      </Row>
      <Row label="Street">
        <TextIn value={a.streetNumber} onChange={put("streetNumber")} placeholder="Number" width={80} />
        <TextIn value={a.streetName} onChange={put("streetName")} placeholder="Name" />
        <Select value={a.streetType} onChange={put("streetType")} options={STREET_TYPES} width={130} />
      </Row>
      <Row label="Suburb / town" htmlFor={`${id}-suburb`}><TextIn id={`${id}-suburb`} value={a.suburb} onChange={put("suburb")} /></Row>
      <Row label="State, postcode">
        <Select value={a.state} onChange={put("state")} options={opts.states || []} width={100} />
        <TextIn value={a.postcode} onChange={put("postcode")} width={90} />
      </Row>
      <Row label="Country" htmlFor={`${id}-country`}><TextIn id={`${id}-country`} value={a.country} onChange={put("country")} /></Row>
    </>;
  };
  const postalRows = <>
    <Row label="Care of" htmlFor="po-careof"><TextIn id="po-careof" value={postal.careOf} onChange={v => set(["addresses", "postal", "careOf"], v)} /></Row>
    <Row label="Box type, number">
      <Select value={postal.boxType} onChange={v => set(["addresses", "postal", "boxType"], v)} options={["PO Box", "GPO Box", "Locked Bag", "Private Bag"]} width={140} />
      <TextIn value={postal.boxNumber} onChange={v => set(["addresses", "postal", "boxNumber"], v)} placeholder="Number" />
    </Row>
    <Row label="Suburb / town" htmlFor="po-suburb"><TextIn id="po-suburb" value={postal.suburb} onChange={v => set(["addresses", "postal", "suburb"], v)} /></Row>
    <Row label="State, postcode">
      <Select value={postal.state} onChange={v => set(["addresses", "postal", "state"], v)} options={opts.states || []} width={100} />
      <TextIn value={postal.postcode} onChange={v => set(["addresses", "postal", "postcode"], v)} width={90} />
    </Row>
  </>;

  return (
    <Dialog title={title || (contact?.id ? `Edit ${contact.name || "contact"}` : "Create a contact")} onClose={onClose} width={640}
      footer={<>
        {contact?.id && onDeleted && <span style={{ flex: 1 }}><LinkButton danger onClick={remove}>Delete contact</LinkButton></span>}
        <Button onClick={onClose}>Cancel</Button>
        <Button primary disabled={saving} onClick={save}>{saving ? "Saving…" : submitLabel || "Save"}</Button>
      </>}>
      <div role="radiogroup" aria-label="Kind of contact" style={{ display: "flex", gap: 16, marginBottom: 12 }}>
        {[["person", "Person"], ["organisation", "Firm / Business / Organisation"]].map(([k, label]) => (
          <label key={k} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
            <input type="radio" name="contact-kind" checked={draft.kind === k} onChange={() => set(["kind"], k)} />{label}
          </label>
        ))}
      </div>
      {intro}
      {heading && <h3 style={{ fontSize: 16, fontWeight: 400, margin: "0 0 10px" }}>{heading}</h3>}

      {/* A person's card in triConvey's tabs: how to reach them, and who they are. */}
      {!isOrg && <div style={{ marginBottom: 10, borderBottom: `1px solid ${T.stroke}` }}>
        <Tabs size={13} active={cardTab} onChange={setCardTab}
          tabs={[["contact", "Name & contact details"], ["additional", "Additional details"]]} />
      </div>}
      {!isOrg && cardTab === "additional" && draft.people.map(additionalBlock)}

      {(isOrg || cardTab === "contact") && <>
        {isOrg && (
          <Fieldset>
            <Row label="Name" htmlFor="org-name" tag={tagAt("organisation.name")}><TextIn id="org-name" value={draft.organisation.name} onChange={v => set(["organisation", "name"], v)} /></Row>
            <Row label="ABN" htmlFor="org-abn" tag={<SourceTag source={sourceAt("organisation.acn") || sourceAt("organisation.abn")} />}>
              <TextIn id="org-abn" value={draft.organisation.abn} onChange={v => set(["organisation", "abn"], v)} />
              <select aria-label="Company number type" value={draft.organisation.acnType} onChange={e => set(["organisation", "acnType"], e.target.value)} style={{ ...inputStyle, width: 80, flex: "none" }}>
                {(opts.acnTypes || ["ACN", "ARBN"]).map(o => <option key={o}>{o}</option>)}
              </select>
              <TextIn value={draft.organisation.acn} onChange={v => set(["organisation", "acn"], v)} />
            </Row>
            <Row label="Phone" htmlFor="org-phone"><TextIn id="org-phone" value={draft.organisation.phone} onChange={v => set(["organisation", "phone"], v)} /></Row>
            <Row label="Fax" htmlFor="org-fax"><TextIn id="org-fax" value={draft.organisation.fax} onChange={v => set(["organisation", "fax"], v)} /></Row>
            <Row label="Email" htmlFor="org-email"><TextIn id="org-email" type="email" value={draft.organisation.email} onChange={v => set(["organisation", "email"], v)} /></Row>
            <Row label="Other emails" htmlFor="org-other"><TextIn id="org-other" value={draft.organisation.otherEmails} onChange={v => set(["organisation", "otherEmails"], v)} placeholder="e.g. settlements@…" /></Row>
            <Row label="Domains" htmlFor="org-domains"><TextIn id="org-domains" value={draft.organisation.domains} onChange={v => set(["organisation", "domains"], v)} placeholder="e.g. smithlawyers.com.au" /></Row>
            <p style={{ margin: "0 0 4px", fontSize: 12, color: T.fg3 }}>
              Email from anyone at these domains finds the matters this firm is on. The addresses above count already.
            </p>
          </Fieldset>
        )}

        {!isOrg && <Fieldset>{draft.people.map(personBlock)}
          <LinkButton onClick={addPerson}>Add another person</LinkButton></Fieldset>}

        {isOrg ? (
          <Fieldset legend={<>Address{tagAt("addresses.street")}</>}>
            <Tabs size={12.5} active={addressTab} onChange={setAddressTab}
              tabs={[["street", "Street address"], ["postal", "PO Box address"], ["dx", "DX address"]]} />
            <div style={{ paddingTop: 10 }}>
              {addressTab === "street" && streetRows("street", "a")}
              {addressTab === "postal" && postalRows}
              {addressTab === "dx" && <>
                <Row label="DX number" htmlFor="dx-number"><TextIn id="dx-number" value={dx.dxNumber} onChange={v => set(["addresses", "dx", "dxNumber"], v)} /></Row>
                <Row label="Exchange" htmlFor="dx-exchange"><TextIn id="dx-exchange" value={dx.exchange} onChange={v => set(["addresses", "dx", "exchange"], v)} /></Row>
              </>}
            </div>
          </Fieldset>
        ) : (
          // A person's addresses on one panel, as triConvey's card has them:
          // residential, PO box, and a forwarding address only when asked for.
          <>
            <Fieldset legend={<>Residential address{tagAt("addresses.street")}</>}>{streetRows("street", "a")}</Fieldset>
            <Fieldset legend="PO Box address">{postalRows}</Fieldset>
            <Fieldset legend="Forwarding address">
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, marginBottom: 8 }}>
                <input type="checkbox" checked={forwardingOn} onChange={e => setForwardingOn(e.target.checked)} style={{ accentColor: T.brand }} />
                Specify forwarding address
              </label>
              {forwardingOn && streetRows("forwarding", "fwd")}
            </Fieldset>
          </>
        )}

        {isOrg && <Fieldset legend="Contact">{draft.people.map(personBlock)}
          <LinkButton onClick={addPerson}>Add another contact</LinkButton></Fieldset>}

        <Fieldset legend="Identity check and notes">
          <Row label="AML & VOI" htmlFor="c-voi">
            <select id="c-voi" value={draft.voiStatus} onChange={e => set(["voiStatus"], e.target.value)} style={inputStyle}>
              <option value="">Not started</option>
              <option value="pending">Pending</option>
              <option value="verified">Verified</option>
              <option value="not_required">Not required</option>
            </select>
          </Row>
          <textarea aria-label="Notes" value={draft.notes} onChange={e => set(["notes"], e.target.value)} rows={3} placeholder="Notes"
            style={{ ...inputStyle, height: "auto", padding: "6px 8px", resize: "vertical" }} />
        </Fieldset>
      </>}
      {error && <p role="alert" style={{ color: T.danger, fontSize: 13, margin: 0 }}>{error}</p>}
    </Dialog>
  );
};

const VOI_LABEL = { pending: "Pending", verified: "Verified", not_required: "Not required" };

// ── The Contacts screen ─────────────────────────────────────────────────────

export const ContactsPage = ({ api, onOpenMatter, openId = "" }) => {
  const call = useCall(api);
  const { layout } = useLayout(call);
  const search = useContactSearch(call, { pageSize: 50 });
  const [editing, setEditing] = useState(null);
  const [linked, setLinked] = useState([]);

  const open = async (row) => {
    if (row === "new") { setEditing({}); setLinked([]); return; }
    try {
      const data = await call(`/practice/contacts/${encodeURIComponent(row.id)}`);
      setEditing(data.contact);
      setLinked(data.matters || []);
    } catch (e) {
      window.alert(e.message);
    }
  };

  // Arriving from the search box with a card in mind opens it.
  const opened = useRef("");
  useEffect(() => {
    if (!openId || opened.current === openId) return;
    opened.current = openId;
    open({ id: openId });
  });

  return (
    <div style={{ fontFamily: FONT }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <h1 style={{ margin: 0, flex: 1, fontSize: 28, fontWeight: 300, letterSpacing: "-0.02em", color: T.fg1 }}>Contacts</h1>
        <Button primary onClick={() => open("new")}><Icon d={ICON.plus} size={14} />New contact</Button>
      </div>
      <input aria-label="Search contacts" value={search.query} onChange={e => search.setQuery(e.target.value)}
        placeholder="Search by name, email, phone or suburb" style={{ ...inputStyle, maxWidth: 420, marginBottom: 12 }} />
      {search.error && <p role="alert" style={{ color: T.danger, fontSize: 13 }}>{search.error}</p>}
      <div style={{ background: T.card, borderRadius: 8 }}>
        <ContactTable rows={search.result.rows} sort={search.sort} onSort={search.setSort} onSelect={open} />
      </div>
      <Pager page={search.result.page} pages={search.result.pages} total={search.result.total} onPage={search.setPage} />

      {editing && (
        <ContactForm call={call} layout={layout} contact={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onDeleted={() => { setEditing(null); search.refresh(); }}
          onSaved={() => { setEditing(null); search.refresh(); }} />
      )}
      {editing?.id && linked.length > 0 && createPortal((
        <div style={{ position: "fixed", right: 16, bottom: 16, zIndex: 401, background: T.card, borderRadius: 8,
          boxShadow: SHADOW, padding: 12, maxWidth: 320, fontFamily: FONT, fontSize: 13 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>On {linked.length} matter{linked.length === 1 ? "" : "s"}</div>
          {linked.map(m => (
            <div key={m.ref} style={{ marginBottom: 4 }}>
              <LinkButton onClick={() => { setEditing(null); onOpenMatter(m.ref); }}>{m.ref}</LinkButton>
              {" "}<span style={{ color: T.fg3 }}>{m.roles.join(", ")} · {m.address}</span>
            </div>
          ))}
        </div>
      ), document.body)}
    </div>
  );
};

// ── The matter: stage, rows, section dialogs ────────────────────────────────

// A ribbon's buttons: an icon over a word, greyed where the feature is not
// built (`soon`) or the action cannot run now (`why`, `disabled`). `icon` is a
// name from ICON or a path. Shared with the Section 32 page, which draws its
// own buttons into the Section 32 tab's ribbon (`useRibbonSlot`).
export const RibbonItems = ({ items }) => items.filter(Boolean).map(b => {
  const off = Boolean(b.soon || b.why || b.disabled);
  const look = { display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 3, minWidth: 64, padding: "6px 6px 4px",
    background: "none", border: "none", borderRadius: R, fontFamily: FONT, fontSize: 12, textDecoration: "none", whiteSpace: "nowrap",
    color: off ? T.fg4 : T.fg2, cursor: off ? "not-allowed" : "pointer" };
  if (b.pressed) Object.assign(look, { background: T.brandTint, color: T.fg1, fontWeight: 600 });
  const inner = <><span style={{ color: off || b.muted ? T.fg4 : T.brand, display: "inline-flex" }}><Icon d={ICON[b.icon] || b.icon} size={20} /></span>{b.label}</>;
  if (b.href && !off) return <a key={b.label} href={b.href} style={look}>{inner}</a>;
  return <button key={b.label} type="button" disabled={off} title={b.soon ? SOON : b.why || b.title} onClick={off ? undefined : b.onClick}
    aria-pressed={b.pressed === undefined ? undefined : Boolean(b.pressed)} style={look}>{inner}</button>;
});

// **The ribbon's More**: what is used now and then, behind one button at the
// end of the row rather than a ⋮ floating over the page. Items are
// `{ label, onClick, disabled }` or "divider"; falsy ones are dropped.
export const RibbonMenu = ({ label = "More", items }) => {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [open]);
  const shown = items.filter(Boolean).filter((it, i, all) => it !== "divider" || (i > 0 && i < all.length - 1 && all[i - 1] !== "divider"));
  if (shown.length === 0) return null;
  return (
    <span ref={box} style={{ position: "relative", display: "inline-flex" }}>
      <button type="button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(o => !o)}
        style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 3, minWidth: 64, padding: "6px 6px 4px",
          background: open ? T.brandTint : "none", border: "none", borderRadius: R, fontFamily: FONT, fontSize: 12, color: T.fg2, cursor: "pointer", whiteSpace: "nowrap" }}>
        <span style={{ color: T.brand, display: "inline-flex" }}><Icon d={ICON.more} size={20} /></span>{label}
      </button>
      {open && (
        <div role="menu" style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, minWidth: 230, background: T.card, borderRadius: 8,
          boxShadow: SHADOW, padding: "4px 0", zIndex: 90, fontFamily: FONT }}>
          {shown.map((it, i) => it === "divider"
            ? <div key={i} role="separator" style={{ height: 1, background: T.strokeSubtle, margin: "4px 0" }} />
            : (
              <button key={it.label} type="button" role="menuitem" disabled={it.disabled}
                onClick={() => { setOpen(false); it.onClick(); }}
                style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: "8px 14px",
                  fontSize: 13, fontFamily: FONT, color: it.disabled ? T.fg4 : T.fg1, cursor: it.disabled ? "not-allowed" : "pointer" }}>
                {it.label}
              </button>
            ))}
        </div>
      )}
    </span>
  );
};

// A rule between groups of ribbon buttons — the views, then the actions.
export const RibbonDivider = () => <span aria-hidden="true" style={{ alignSelf: "stretch", width: 1, margin: "6px 6px", background: T.strokeSubtle }} />;

// The Duties form button's tooltip, by whether the extension is here (App.js
// asks it; this file imports nothing from App.js, so the words are its own).
const dutiesTitle = (status) => ({
  missing: "The duties form extension isn't installed in this browser. Press for how to install it.",
  unsupported: "The duties form fills on a computer, in Chrome or Edge.",
}[status?.state] || "Opens Duties Online and fills the SRO duties form from this matter, page by page. You check the answers and save.");

// What the Duties form button says after a press.
const DUTIES_WORDS = { busy: "Opening…", opened: "Opened", copied: "Code copied", failed: "Couldn't open" };

// What the contract tab is called, on the tab and wherever a screen points at it.
export const CONTRACT_TAB_LABEL = "Contract & S32";

// Where the Section 32 tab's ribbon is, for the page under it to draw into:
// its buttons follow that page's own rules (what may be released, what is
// running), so they are drawn there rather than copied here.
export const RibbonSlot = createContext(null);
export const useRibbonSlot = () => useContext(RibbonSlot);

// The stages as a strip of chevrons in the Matter tab's ribbon, under the
// buttons: done ones tinted, the current one filled, and each one a button, so
// moving the matter is one click. The set is the matter type's (the record's
// `stages`; a sale and a purchase differ), and the firm's from Settings.
const CHEVRON = 10;
const StageStrip = ({ stages, active, onChange, busy }) => {
  const at = Math.max(0, stages.findIndex(s => s.key === active));
  return (
    <div role="group" aria-label="Stage" style={{ display: "flex", gap: 2, overflowX: "auto", padding: "6px 0 10px", maxWidth: "100%" }}>
      {stages.map((s, i) => {
        const current = i === at;
        const done = i < at;
        const first = i === 0;
        const last = i === stages.length - 1;
        const clip = `polygon(0 0, ${last ? "100%" : `calc(100% - ${CHEVRON}px)`} 0, 100% 50%, ${last ? "100%" : `calc(100% - ${CHEVRON}px)`} 100%, 0 100%, ${first ? "0" : `${CHEVRON}px`} 50%)`;
        return (
          <button key={s.key} type="button" aria-pressed={current} disabled={busy} title={current ? `${s.label} (this stage)` : `Move to ${s.label}`}
            onClick={current ? undefined : () => onChange(s.key)}
            style={{ flex: "1 0 auto", minWidth: 96, height: 30, padding: `0 ${last ? 12 : CHEVRON + 10}px 0 ${first ? 12 : CHEVRON + 8}px`, marginLeft: first ? 0 : -CHEVRON + 2,
              clipPath: clip, border: "none", fontFamily: FONT, fontSize: 12, fontWeight: current ? 600 : 400, whiteSpace: "nowrap",
              background: current ? T.brand : done ? T.brandTint : T.strokeSubtle, color: current ? T.onBrand : done ? T.brandPressed : T.fg2,
              cursor: current ? "default" : busy ? "progress" : "pointer" }}>
            {s.label}
          </button>
        );
      })}
    </div>
  );
};

// One row of Matter details: a label, what it holds, and "+ Add" where nothing is.
const DetailRow = ({ label, indent, value, chip, onOpen, empty, soon }) => (soon ? (
  <div aria-disabled="true" title={SOON}
    style={{ display: "grid", gridTemplateColumns: "minmax(130px, 190px) minmax(0, 1fr) 20px", alignItems: "center", minHeight: 40,
      padding: "0 12px 0 16px", borderTop: `1px solid ${T.strokeSubtle}`, fontFamily: FONT, fontSize: 14, color: T.fg4, cursor: "not-allowed" }}>
    <span style={{ paddingLeft: indent ? 8 : 0 }}>{indent && <span aria-hidden="true">↳ </span>}{label}</span>
    <span style={{ fontSize: 13 }}>Coming soon</span>
    <span />
  </div>
) : (
  <button type="button" onClick={onOpen} aria-label={`${empty ? "Add" : "Open"} ${label}`}
    style={{ display: "grid", gridTemplateColumns: "minmax(130px, 190px) minmax(0, 1fr) 20px", alignItems: "center", width: "100%",
      minHeight: 40, padding: "0 12px 0 16px", background: "none", border: "none", borderTop: `1px solid ${T.strokeSubtle}`,
      cursor: "pointer", fontFamily: FONT, fontSize: 14, textAlign: "left", color: T.fg1 }}>
    {/* A sub-row of the row above it (the vendor's "On the contract", a
        solicitor under their party): marked, not only indented, so it reads
        as part of that party rather than a second one. */}
    <span style={{ color: T.fg3, paddingLeft: indent ? 8 : 0, display: "inline-flex", alignItems: "baseline", gap: 6 }}>
      {indent && <span aria-hidden="true" style={{ color: T.fg4 }}>↳</span>}{label}
    </span>
    {empty ? (
      <span style={{ color: T.brand, display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13 }}><Icon d={ICON.plus} size={14} />Add</span>
    ) : (
      <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flexWrap: "wrap" }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, maxWidth: "100%" }}>{value}</span>
        {chip}
      </span>
    )}
    <span style={{ color: T.fg4, display: "inline-flex" }}>{!empty && <Icon d={ICON.chevron} size={14} />}</span>
  </button>
));

// A section of fields, in its tabs and groups, edited as one and saved on OK.
// Fluent's form: the label above the box, three to a row. A field the contract
// also prints wears an "S32" tag — it is the same value as the Section 32 tab.
const SectionDialog = ({ section, defOf, valueOf, recordNames, onSave, onClose }) => {
  const tabs = useMemo(() => section.tabs || [], [section]);
  const [tab, setTab] = useState(0);
  const names = useMemo(() => tabs.flatMap(t => t.groups.flatMap(g => g.fields)), [tabs]);
  // The baseline is the values as the dialog opened, and stays so: a re-read
  // while it is open (the compile poll, a popped-out window) gives `valueOf` a
  // new identity, and a moving baseline would post every field the server
  // changed meanwhile back with its old value.
  const inputsNow = () => Object.fromEntries(names.map(n => [n, toInput(defOf(n), valueOf(n))]));
  const [original] = useState(inputsNow);
  const [draft, setDraft] = useState(original);
  // A field edited here that also changed elsewhere since: saving keeps this edit.
  const current = useMemo(inputsNow, [names, defOf, valueOf]); // eslint-disable-line react-hooks/exhaustive-deps
  const clashes = names.filter(n => draft[n] !== original[n] && current[n] !== original[n] && current[n] !== draft[n]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const printsOnContract = names.some(n => !recordNames.has(n));

  const ok = async () => {
    setSaving(true); setError("");
    try {
      const { contract, record } = splitChanges(draft, original, recordNames);
      const decode = (obj) => Object.fromEntries(Object.entries(obj).map(([n, v]) => [n, fromInput(defOf(n), v)]));
      await onSave(decode(contract), decode(record));
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog title={section.title || section.label} onClose={onClose} width={900}
      footer={<>
        {error && <span role="alert" style={{ color: T.danger, fontSize: 13, flex: 1 }}>{error}</span>}
        <Button onClick={onClose}>Cancel</Button>
        <Button primary disabled={saving} onClick={ok}>{saving ? "Saving…" : "Save"}</Button>
      </>}>
      {/* Property details carries the section 32 tabs too, so its tabs wrap
          rather than run off the dialog's edge. */}
      {tabs.length > 1 && <div style={{ borderBottom: `1px solid ${T.stroke}`, marginBottom: 8 }}>
        <Tabs tabs={tabs.map((t, i) => [i, t.label])} active={tab} onChange={setTab} wrap={tabs.length > 5} />
      </div>}
      {/* A tab whose label is a number (Sec. 32 (4)) says what it holds. */}
      {tabs[tab]?.title && <h3 style={{ fontSize: 16, fontWeight: 600, color: T.fg1, margin: "8px 0 4px" }}>{tabs[tab].title}</h3>}
      {printsOnContract && (
        <p style={{ display: "flex", gap: 8, alignItems: "center", background: T.bg, border: `1px solid ${T.stroke}`, borderRadius: R,
          padding: "8px 12px", fontSize: 13, color: T.fg2, margin: "8px 0" }}>
          Fields marked <S32Tag /> print on the contract — they are the same values the {CONTRACT_TAB_LABEL} tab reviews.
        </p>
      )}
      {/* Where the other half of this party is: the card, or the contract's wording. */}
      {section.note && <p style={{ fontSize: 13, color: T.fg3, margin: "4px 0 8px" }}>{section.note}</p>}
      {clashes.length > 0 && (
        <p role="status" style={{ background: T.bg, border: `1px solid ${T.stroke}`, borderRadius: R, padding: "8px 12px", fontSize: 13, color: T.fg2, margin: "8px 0" }}>
          Changed elsewhere since this opened: {clashes.map(n => defOf(n).label || n).join(", ")}. Saving keeps what you typed here.
        </p>
      )}
      {(tabs[tab]?.groups || []).map((g, gi) => (
        <Fieldset key={gi} legend={g.label}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "12px 16px" }}>
            {g.fields.map(name => {
              const def = defOf(name);
              const id = `field-${name.replace(/\W/g, "-")}`;
              return (
                <div key={name} style={{ minWidth: 0, gridColumn: def.type === "longtext" || def.type === "list" ? "1 / -1" : undefined }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    <label htmlFor={id} style={{ fontSize: 14, color: T.fg1 }}>{def.label || name}</label>
                    {!recordNames.has(name) && <S32Tag />}
                  </div>
                  <FieldInput id={id} def={def} value={draft[name] ?? ""} onChange={v => setDraft(d => ({ ...d, [name]: v }))} />
                  {def.note && <div style={{ fontSize: 12, color: T.fg3, marginTop: 4, lineHeight: 1.4 }}>{def.note}</div>}
                </div>
              );
            })}
          </div>
        </Fieldset>
      ))}
    </Dialog>
  );
};

// Form 265's four statuses, for a middleware that does not list them yet.
const PRACTICE_STATUSES = ["Open", "Closed", "Cancelled", "Deleted"];

const StatusDialog = ({ statuses, status, onSave, onClose }) => {
  const [chosen, setChosen] = useState(status);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const save = async () => {
    setSaving(true); setError("");
    try { await onSave(chosen); onClose(); } catch (e) { setError(e.message); } finally { setSaving(false); }
  };
  return (
    <Dialog title="Matter settings" onClose={onClose} width={460}
      footer={<>
        {error && <span role="alert" style={{ color: T.danger, fontSize: 13, flex: 1 }}>{error}</span>}
        <Button onClick={onClose}>Cancel</Button>
        <Button primary disabled={saving || chosen === status} onClick={save}>{saving ? "Saving…" : "Save"}</Button>
      </>}>
      <label htmlFor="matter-status" style={{ display: "block", fontSize: 14, color: T.fg1, marginBottom: 4 }}>Status</label>
      <select id="matter-status" value={chosen} onChange={e => setChosen(e.target.value)} style={{ ...inputStyle, maxWidth: 240 }}>
        {statuses.map(st => <option key={st} value={st}>{st}</option>)}
      </select>
      <p style={{ fontSize: 13, color: T.fg3, margin: "8px 0 0" }}>Closed, Cancelled and Deleted take the matter off the matters list. Nothing on it is lost.</p>
    </Dialog>
  );
};

const S32Tag = () => (
  <span aria-label="prints on the contract" style={{ fontSize: 10, fontWeight: 600, color: T.fg3, border: `1px solid ${T.stroke}`,
    borderRadius: 8, padding: "0 5px", lineHeight: "14px" }}>S32</span>
);

// The cards filling one role, with what to do about them.
const RoleDialog = ({ role, cards, note, onAdd, onEdit, onRemove, onClose }) => (
  <Dialog title={role.label} onClose={onClose} width={560}
    footer={<>
      <Button onClick={onClose}>Close</Button>
      <Button primary onClick={onAdd}><Icon d={ICON.plus} size={14} />{role.multiple || cards.length === 0 ? "Add" : "Replace"}</Button>
    </>}>
    {note && <p style={{ fontSize: 13, color: T.fg3, margin: "0 0 8px" }}>{note}</p>}
    {cards.map(c => (
      <div key={c.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 0", borderTop: `1px solid ${T.strokeSubtle}` }}>
        <Initials name={c.name} />
        <div style={{ flex: 1, fontSize: 13 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</div>
          <ContactLines contact={c} />
        </div>
        <LinkButton onClick={() => onEdit(c)}>Edit</LinkButton>
        <LinkButton danger onClick={() => onRemove(c)}>Remove</LinkButton>
      </div>
    ))}
  </Dialog>
);

// triConvey's gear, "Show and hide matter details": tick the less common
// cards this matter needs. A row with somebody on it stays — unticking it would
// hide who is on the matter — so its box is ticked and fixed.
const ShownDialog = ({ sections, shown, linked, onSave, onClose }) => {
  const [ticked, setTicked] = useState(() => new Set(shown));
  const [saving, setSaving] = useState(false);
  const toggle = (key) => setTicked(t => { const n = new Set(t); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  return (
    <Dialog title="Show and hide matter details" onClose={onClose} width={460}
      footer={<>
        <Button onClick={onClose}>Cancel</Button>
        <Button primary disabled={saving} onClick={async () => { setSaving(true); await onSave(sections.map(s => s.key).filter(k => ticked.has(k))); }}>{saving ? "Saving…" : "Save"}</Button>
      </>}>
      <p style={{ fontSize: 13, color: T.fg3, margin: "0 0 10px" }}>Tick the people this matter needs. The usual parties always show.</p>
      {sections.map(s => {
        const fixed = linked(s.key);
        return (
          <label key={s.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, padding: "5px 0", color: fixed ? T.fg3 : T.fg1 }}>
            <input type="checkbox" checked={fixed || ticked.has(s.key)} disabled={fixed} onChange={() => toggle(s.key)} style={{ accentColor: T.brand }} />
            {s.label}{fixed && <span style={{ fontSize: 12, color: T.fg4 }}>— has a contact</span>}
          </label>
        );
      })}
    </Dialog>
  );
};

const lead = (c) => (c.people || [])[0] || {};
const oneLineAddress = (a = {}) => {
  const number = [a.unitNumber, a.streetNumber].filter(Boolean).join("/");
  const street = [number, a.streetName, a.streetType].filter(Boolean).join(" ");
  return [a.buildingLevel, street, [a.suburb, a.state, a.postcode].filter(Boolean).join(" ")].filter(Boolean).join(", ");
};

export const initialsOf = (name = "") => String(name).split(/\s+/).filter(w => /^[A-Za-z]/.test(w))
  .filter((w, i, all) => i === 0 || i === all.length - 1).map(w => w[0].toUpperCase()).join("").slice(0, 2);

const Initials = ({ name, size = 36 }) => (
  <span aria-hidden="true" style={{ width: size, height: size, borderRadius: "50%", background: T.brandTint, color: T.brandPressed,
    fontWeight: 600, fontSize: Math.round(size * 0.36), display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
    {initialsOf(name)}
  </span>
);

const ContactLines = ({ contact }) => {
  const p = lead(contact);
  const lines = [
    ["Mobile", p.mobile], ["Phone", contact.organisation?.phone || p.workPhone || p.homePhone],
    ["Email", contact.organisation?.email || p.email], ["Address", oneLineAddress(contact.addresses?.street)],
    ["Date of birth", formatDay(p.dateOfBirth)],
  ].filter(([, v]) => v);
  return (
    <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 12px", margin: "8px 0 0", fontSize: 13, lineHeight: "22px" }}>
      {lines.map(([k, v]) => <div key={k} style={{ display: "contents" }}>
        <dt style={{ color: T.fg3 }}>{k}</dt>
        <dd style={{ margin: 0, color: T.fg1, overflowWrap: "anywhere" }}>
          {k === "Email" ? <a href={`mailto:${v}`} style={{ color: T.brand, textDecoration: "none" }}>{v}</a>
            : k === "Mobile" || k === "Phone" ? <a href={`tel:${String(v).replace(/\s/g, "")}`} style={{ color: T.brand, textDecoration: "none" }}>{v}</a>
              : v}
        </dd>
      </div>)}
    </dl>
  );
};

const Empty = ({ children }) => <p style={{ margin: 0, color: T.fg4, fontSize: 13 }}>{children}</p>;

// A card the matter already describes — the vendors off the client form, the
// other side's solicitor typed on the contract. The middleware makes the card
// (or names the one already in the file); this only offers the press.
//
// **Or what the client form could add to a card already linked** (`kind:
// "fill"`): only the card's blanks, worked out by the middleware, which never
// replaces a value on the card.
const SuggestedParty = ({ offer, onAdd, onReview }) => {
  const [busy, setBusy] = useState(false);
  const press = async () => { setBusy(true); try { await onAdd(); } finally { setBusy(false); } };
  const fill = offer.kind === "fill";
  const review = offer.kind === "review";
  const clashes = (offer.candidates || []).length;
  return (
    <div role="note" aria-label={fill ? `Client form details for ${offer.name}` : `Suggested ${offer.label.toLowerCase()}`}
      style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        margin: "0 16px 8px", padding: "8px 12px", background: review ? T.warnTint : T.brandTint, borderRadius: R, fontSize: 13 }}>
      {fill ? (
        <span style={{ flex: "1 1 240px", minWidth: 0 }}>
          <span style={{ color: T.fg3 }}>The {offer.from} has what {offer.name}’s card lacks: </span>
          <strong style={{ fontWeight: 600 }}>{(offer.fills || []).join(", ")}</strong>
          {/* Adding a person changes the card wherever it is linked. */}
          {offer.otherMatters > 0 && <span style={{ color: T.fg3 }}> — this card is also on {offer.otherMatters} other matter{offer.otherMatters === 1 ? "" : "s"}</span>}
        </span>
      ) : (
        <span style={{ flex: "1 1 240px", minWidth: 0 }}>
          <span style={{ color: T.fg3 }}>{offer.label} on the {offer.from}: </span>
          <strong style={{ fontWeight: 600 }}>{offer.name}</strong>
          {offer.email && <span style={{ color: T.fg3 }}> · {offer.email}</span>}
          {offer.existingId && <span style={{ color: T.fg3 }}> — already in Contacts as {offer.existingName}</span>}
          {review && <span style={{ color: T.fg2 }}> — clashes with {clashes} card{clashes === 1 ? "" : "s"} in Contacts</span>}
        </span>
      )}
      {/* The card itself, before anything is added: never blind. */}
      {!fill && offer.card && onReview && <Button onClick={onReview}>{review ? "Review" : "Preview"}</Button>}
      {!review && <Button disabled={busy} onClick={press}>{busy ? (fill ? "Filling…" : "Adding…")
        : fill ? "Fill in from client form"
          : offer.existingId ? `Link as ${offer.label.toLowerCase()}` : `Add as ${offer.label.toLowerCase()}`}</Button>}
    </div>
  );
};

// **The intake's card in front of staff before it is added** — the card form,
// filled with what the intake proposes and tagged with where each value came
// from, above the cards in Contacts it clashes with and why. Staff correct it
// and add it as a new card, or link one of those cards instead. The card is
// the middleware's proposal; what staff change is theirs.
const ReviewSuggested = ({ call, layout, offer, onAdd, onLink, onClose }) => {
  const role = offer.label.toLowerCase();
  const candidates = [...(offer.existingId ? [{ id: offer.existingId, name: offer.existingName, why: "Already in Contacts: the same people" }] : []),
    ...(offer.candidates || []).filter(c => c.id !== offer.existingId)];
  const intro = (
    <div style={{ marginBottom: 12, fontSize: 13 }}>
      <p style={{ margin: "0 0 8px", color: T.fg2 }}>
        From the {offer.from}. Check it, correct anything, then add it — or link a card already in Contacts.
      </p>
      {candidates.length > 0 && (
        <div role="list" aria-label="Cards in Contacts" style={{ border: `1px solid ${T.stroke}`, borderRadius: R }}>
          {candidates.map((c, i) => (
            <div role="listitem" key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
              borderTop: i ? `1px solid ${T.strokeSubtle}` : "none" }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontWeight: 600 }}>{c.name}</strong>
                <span style={{ display: "block", fontSize: 12, color: T.fg3 }}>{c.why}</span>
              </span>
              <Button onClick={() => onLink(c.id)}>Link this card instead</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
  return (
    <ContactForm call={call} layout={layout} contact={{ ...offer.card, sources: offer.sources }}
      title={`Review the ${role} from the ${offer.from}`} intro={intro} submitLabel={`Add as ${role}`}
      onSubmit={(card) => onAdd(card)} onSaved={() => {}} onClose={onClose} />
  );
};

// Every address this matter's email is matched on, and where it came from —
// so staff can see why a message did or did not find it.
const SOURCE_NOTE = { card: "Card", client: "Client form", correction: "Contract", directory: "Directory", form: "Agent form" };
const AddressBookCard = ({ book = [], limit = 5 }) => {
  const [all, setAll] = useState(false);
  const shown = all ? book : book.slice(0, limit);
  return (
    <Card label="Emails that find this matter">
      <CardHead title="Emails that find this matter" count={book.length || null} />
      <div style={{ padding: "2px 16px 12px", fontSize: 13 }}>
        {book.length === 0 ? <Empty>No email addresses yet. Add the parties' cards and their emails will find it.</Empty> : shown.map(a => (
          <div key={`${a.email}|${a.role}`} style={{ padding: "4px 0", borderTop: `1px solid ${T.strokeSubtle}` }}>
            <div style={{ overflowWrap: "anywhere" }}>{a.email}</div>
            <div style={{ fontSize: 12, color: T.fg3 }}>{[a.label, a.name].filter(Boolean).join(" · ")}<span style={{ color: T.fg4 }}> — {SOURCE_NOTE[a.source] || a.source}</span></div>
          </div>
        ))}
        {book.length > limit && <LinkButton onClick={() => setAll(v => !v)}>{all ? "Show fewer" : `Show all ${book.length}`}</LinkButton>}
      </div>
    </Card>
  );
};

// ── Documents ───────────────────────────────────────────────────────────────
//
// The matter's documents as the Section 32 module holds them — certificates,
// returns and uploads — read off the matter the portal already has. What each
// is and whether it merges is the middleware's (`displayName`, `isUpload`,
// `hasFile`), never decided here. Adding, replacing and arranging stay on the
// Section 32 tab, where the compile rules live.

const DOC_FILTERS = [
  ["all", "All", () => true],
  ["received", "Received", s => s.status === "Returned" || s.isUpload],
  ["ordered", "Ordered", s => s.status === "Ordered" && !s.isUpload],
  ["uploads", "Uploaded", s => s.isUpload],
  ["notRequired", "Not required", s => s.status === "Not Required"],
];

export const documentStatus = (s = {}) => {
  if (s.isUpload) return ["Uploaded", "neutral"];
  if (s.status === "Returned") return ["Received", "neutral"];
  if (s.status === "Not Required") return ["Not required", "neutral"];
  return ["Ordered", "warn"];
};

const DocumentsCard = ({ searches = [], documentHref, limit, onViewAll, onManage, onDraftAdjustments }) => {
  const [filter, setFilter] = useState("all");
  const [ticked, setTicked] = useState(() => new Set());
  const [menu, setMenu] = useState(null);
  const hasFile = (s) => Boolean(s.hasFile || s.storagePath || s.driveFileUrl);
  const asDoc = (s) => ({ kind: "search", id: s.id, name: s.displayName || s.name || "Certificate" });
  const [query, setQuery] = useState("");
  const test = (DOC_FILTERS.find(f => f[0] === filter) || DOC_FILTERS[0])[2];
  const q = query.trim().toLowerCase();
  const rows = searches.filter(test).filter(s => !q || String(s.displayName || s.name || "").toLowerCase().includes(q));
  const shown = limit ? rows.slice(0, limit) : rows;
  return (
    <Card label="Documents" style={{ overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 16px 6px", flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, flex: 1, fontSize: 14, fontWeight: 600 }}>Documents<span style={{ color: T.fg4, fontWeight: 400, marginLeft: 6 }}>{searches.length}</span></h2>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 28, border: `1px solid ${T.strokeInput}`, borderRadius: R, padding: "0 8px", color: T.fg4 }}>
          <Icon d={ICON.search} size={14} />
          <input aria-label="Search documents" placeholder="Search documents" value={query} onChange={e => setQuery(e.target.value)}
            style={{ border: "none", outline: "none", fontFamily: FONT, fontSize: 13, width: 160, color: T.fg1, background: "transparent" }} />
        </span>
        {onDraftAdjustments && ticked.size > 0 && (
          <Button onClick={() => onDraftAdjustments(searches.filter(s => ticked.has(s.id)).map(asDoc))}>
            <Icon d={ICON.dollar} size={14} />Draft Statement of Adjustments ({ticked.size})<Beta />
          </Button>
        )}
        {onManage && <Button subtle onClick={onManage}><Icon d={ICON.plus} size={14} />Add or arrange</Button>}
      </div>
      <div role="group" aria-label="Filter documents" style={{ display: "flex", gap: 6, padding: "6px 16px 10px", flexWrap: "wrap", borderBottom: `1px solid ${T.stroke}` }}>
        {DOC_FILTERS.map(([key, label, fn]) => {
          const on = filter === key;
          const n = searches.filter(fn).length;
          return (
            <button key={key} type="button" aria-pressed={on} onClick={() => setFilter(key)}
              style={{ height: 26, padding: "0 10px", borderRadius: 13, fontFamily: FONT, fontSize: 12.5, cursor: "pointer",
                border: `1px solid ${on ? T.brandStroke : T.stroke}`, background: on ? T.brandTint : T.card,
                color: on ? T.brandPressed : T.fg2, fontWeight: on ? 600 : 400 }}>
              {label}{n ? <span style={{ fontWeight: 400, marginLeft: 4 }}>{n}</span> : null}
            </button>
          );
        })}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: FONT, tableLayout: "fixed", minWidth: 480 }}>
          <thead>
            <tr style={{ background: T.bg, textAlign: "left" }}>
              {onDraftAdjustments && <th aria-label="Select" style={{ width: 36, padding: "8px 0 8px 12px" }} />}
              <th style={{ padding: "8px 16px", fontWeight: 600, color: T.fg3, fontSize: 12 }}>Name</th>
              <th style={{ padding: "8px 16px", fontWeight: 600, color: T.fg3, fontSize: 12, width: 130 }}>Status</th>
              <th style={{ padding: "8px 16px", fontWeight: 600, color: T.fg3, fontSize: 12, width: 110 }}>Date</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(s => {
              const href = documentHref ? documentHref(s) : "";
              const [label, tone] = documentStatus(s);
              const name = s.displayName || s.name || "Document";
              return (
                <tr key={s.id} style={{ borderTop: `1px solid ${T.strokeSubtle}`, background: ticked.has(s.id) ? T.brandTint : undefined }}
                  onContextMenu={onDraftAdjustments && hasFile(s) ? (e) => {
                    e.preventDefault();
                    const chosen = ticked.has(s.id) ? searches.filter(x => ticked.has(x.id)) : [s];
                    setMenu({ x: e.clientX, y: e.clientY, docs: chosen.map(asDoc), href });
                  } : undefined}>
                  {onDraftAdjustments && (
                    <td style={{ padding: "7px 0 7px 12px" }}>
                      {hasFile(s) && <input type="checkbox" aria-label={`Select ${name}`} checked={ticked.has(s.id)} style={{ accentColor: T.brand, margin: 0 }}
                        onChange={() => setTicked(x => { const n = new Set(x); if (n.has(s.id)) n.delete(s.id); else n.add(s.id); return n; })} />}
                    </td>
                  )}
                  <td style={{ padding: "7px 16px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, maxWidth: "100%", color: href ? T.fg1 : T.fg3 }}>
                      <span style={{ color: href ? T.danger : T.fg4, display: "inline-flex" }}><Icon d={ICON.file} size={15} /></span>
                      {href ? <a href={href} target="_blank" rel="noreferrer" style={{ color: T.fg1, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</a>
                        : <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{name}</span>}
                    </span>
                  </td>
                  <td style={{ padding: "7px 16px" }}>{tone === "neutral" ? <span style={{ color: T.fg3 }}>{label}</span> : <Pill tone={tone}>{label}</Pill>}</td>
                  <td style={{ padding: "7px 16px", color: T.fg3, whiteSpace: "nowrap" }}>{s.date ? formatOpenedDate(s.date) : ""}</td>
                </tr>
              );
            })}
            {shown.length === 0 && <tr><td colSpan={onDraftAdjustments ? 4 : 3} style={{ padding: 16, color: T.fg4 }}>No documents{filter !== "all" || q ? " match" : " yet"}.</td></tr>}
          </tbody>
        </table>
      </div>
      {limit && rows.length > limit && (
        <div style={{ padding: "10px 16px", borderTop: `1px solid ${T.stroke}` }}>
          <LinkButton onClick={onViewAll}>View all {rows.length} documents</LinkButton>
        </div>
      )}
      {menu && (
        <ContextMenu at={menu} onClose={() => setMenu(null)} items={[
          { label: menu.docs.length > 1 ? `Draft Statement of Adjustments (${menu.docs.length} documents)` : "Draft Statement of Adjustments", icon: ICON.dollar, beta: true, onClick: () => onDraftAdjustments(menu.docs) },
          ...(menu.href ? [{ label: "Open", icon: ICON.eye, onClick: () => window.open(menu.href, "_blank", "noopener") }] : []),
        ]} />
      )}
    </Card>
  );
};

// ── The matter's document tree ──────────────────────────────────────────────
//
// Everything the matter holds that is not a search: the firm's folders, any
// it made, the Section 32 searches as a folder of their own (read-only; added
// and arranged on the Section 32 tab), and the bin. The folder list, what may
// be stored and what opens in the browser are the middleware's
// (`GET /practice/matters/:ref/files`); nothing here decides them.
//
// `postFile(path, blob, onProgress)` and `fileHref(path)` are App's: the raw
// upload with the staff header, and a link carrying the read token, never the
// session.

const S32_FOLDER = "\u0000s32";
const BIN_FOLDER = "\u0000bin";

export const formatSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// dd/mm/yyyy in the office's own day, never the browser's locale format.
export const formatWhen = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const filePath = (ref, id = "") => `/practice/matters/${encodeURIComponent(ref)}/files${id ? `/${encodeURIComponent(id)}` : ""}`;

const RowAction = ({ label, onClick, children }) => (
  <button type="button" aria-label={label} title={label} onClick={onClick}
    style={{ background: "none", border: "none", cursor: "pointer", color: T.fg3, padding: "2px 4px", fontSize: 12.5, fontFamily: FONT }}>
    {children}
  </button>
);

const FolderButton = ({ label, depth = 0, count, on, onClick, icon = ICON.folder }) => (
  <button type="button" aria-pressed={on} onClick={onClick}
    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", border: "none", cursor: "pointer",
      padding: `6px 10px 6px ${10 + depth * 16}px`, borderRadius: R, fontFamily: FONT, fontSize: 13,
      background: on ? T.brandTint : "transparent", color: on ? T.brandPressed : T.fg1, fontWeight: on ? 600 : 400 }}>
    <span style={{ color: on ? T.brand : T.fg3, display: "inline-flex" }}><Icon d={icon} size={15} /></span>
    <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
    {count ? <span style={{ color: T.fg4, fontWeight: 400, fontSize: 12 }}>{count}</span> : null}
  </button>
);

export const DocumentsTab = ({ call, matterRef, searches = [], documentHref, postFile, fileHref, onManage, onChanged, importSignal = 0, onDraftAdjustments }) => {
  const [listing, setListing] = useState(null);
  // Ticked files, and the right-click menu: "Draft Statement of Adjustments"
  // sends the ticked files, or the one clicked when it is not ticked.
  const [ticked, setTicked] = useState(() => new Set());
  const [menu, setMenu] = useState(null);
  const [missing, setMissing] = useState(false);
  const [folder, setFolder] = useState("");
  const [query, setQuery] = useState("");
  const [uploads, setUploads] = useState([]);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);
  const [dragging, setDragging] = useState(false);
  const picker = useRef(null);

  // The ribbon's Import: open the file picker on the folder on screen, or the
  // top of the tree when the searches or the bin are showing.
  const [pendingImport, setPendingImport] = useState(false);
  useEffect(() => {
    if (!importSignal) return;
    setFolder(f => (f === S32_FOLDER || f === BIN_FOLDER ? "" : f));
    setPendingImport(true);
  }, [importSignal]);
  useEffect(() => {
    if (pendingImport && picker.current) { setPendingImport(false); picker.current.click(); }
  }, [pendingImport, folder, listing]);

  const load = useCallback(async () => {
    try {
      setListing(await call(filePath(matterRef)));
      setMissing(false);
    } catch (e) {
      // An older middleware has no document store: the searches still show.
      if (e.status === 404) { setMissing(true); setFolder(S32_FOLDER); } else setError(e.message);
    }
  }, [call, matterRef]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => { setTicked(new Set()); }, [folder]);
  const files = listing?.files || [];
  const bin = listing?.bin || [];
  const folders = listing?.folders || [];
  const countIn = (f) => files.filter(x => x.folder === f || x.folder.startsWith(`${f}/`)).length;

  const run = async (fn) => {
    setError("");
    try { await fn(); await load(); onChanged && onChanged(); } catch (e) { setError(e.message); }
  };

  const send = async (list) => {
    if (!postFile || folder === S32_FOLDER || folder === BIN_FOLDER) return;
    const picked = [...list];
    if (!picked.length) return;
    setError("");
    const into = folder;
    setUploads(picked.map(f => ({ name: f.name, progress: 0 })));
    const failed = [];
    for (let i = 0; i < picked.length; i++) {
      const f = picked[i];
      try {
        const blob = new Blob([f], { type: "application/octet-stream" });
        await postFile(`${filePath(matterRef)}?name=${encodeURIComponent(f.name)}&folder=${encodeURIComponent(into)}`, blob,
          (p) => setUploads(u => u.map((x, j) => (j === i ? { ...x, progress: p } : x))));
      } catch (e) {
        failed.push(`${f.name}: ${e.message}`);
      }
    }
    setUploads([]);
    if (failed.length) setError(failed.join(" · "));
    await load();
    onChanged && onChanged();
  };

  const q = query.trim().toLowerCase();
  const inFolder = folder === BIN_FOLDER ? bin : files.filter(f => f.folder === folder);
  const shown = (q ? (folder === BIN_FOLDER ? bin : files).filter(f => f.name.toLowerCase().includes(q)) : inFolder);
  const heading = folder === BIN_FOLDER ? "Bin" : folder === S32_FOLDER ? "Section 32 searches" : folder || "All folders";
  const canAdd = Boolean(postFile) && !missing && folder !== S32_FOLDER && folder !== BIN_FOLDER;

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
      <Card label="Folders" style={{ flex: "0 1 260px", minWidth: 220, padding: 8 }}>
        {!missing && (
          <>
            <FolderButton label="Matter documents" count={files.filter(f => !f.folder).length || null} on={folder === ""} onClick={() => setFolder("")} />
            {folders.map(f => (
              <FolderButton key={f} label={f.split("/").pop()} depth={f.split("/").length} count={countIn(f) || null}
                on={folder === f} onClick={() => setFolder(f)} />
            ))}
          </>
        )}
        <div style={{ borderTop: `1px solid ${T.stroke}`, margin: "6px 0" }} />
        <FolderButton label="Section 32 searches" icon={ICON.file} count={searches.length || null} on={folder === S32_FOLDER} onClick={() => setFolder(S32_FOLDER)} />
        {!missing && <FolderButton label="Bin" icon={ICON.trash} count={bin.length || null} on={folder === BIN_FOLDER} onClick={() => setFolder(BIN_FOLDER)} />}
        {!missing && (
          <div style={{ padding: "8px 6px 4px" }}>
            <LinkButton onClick={() => setDialog({ kind: "folder", path: folder && folder !== S32_FOLDER && folder !== BIN_FOLDER ? `${folder}/` : "" })}>
              <Icon d={ICON.plus} size={12} /> New folder
            </LinkButton>
          </div>
        )}
      </Card>

      <div style={{ flex: "1 1 480px", minWidth: 0 }}>
        {folder === S32_FOLDER ? (
          <DocumentsCard searches={searches} documentHref={documentHref} onManage={onManage} onDraftAdjustments={onDraftAdjustments} />
        ) : (
          <Card label={heading} style={{ overflow: "hidden", outline: dragging ? `2px dashed ${T.brand}` : "none" }}>
            <div
              onDragOver={canAdd ? (e) => { e.preventDefault(); setDragging(true); } : undefined}
              onDragLeave={canAdd ? () => setDragging(false) : undefined}
              onDrop={canAdd ? (e) => { e.preventDefault(); setDragging(false); send(e.dataTransfer.files); } : undefined}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 16px 10px", flexWrap: "wrap", borderBottom: `1px solid ${T.stroke}` }}>
                <h2 style={{ margin: 0, flex: 1, fontSize: 14, fontWeight: 600, minWidth: 0 }}>{heading}</h2>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 28, border: `1px solid ${T.strokeInput}`, borderRadius: R, padding: "0 8px", color: T.fg4 }}>
                  <Icon d={ICON.search} size={14} />
                  <input aria-label="Search files" placeholder="Search files" value={query} onChange={e => setQuery(e.target.value)}
                    style={{ border: "none", outline: "none", fontFamily: FONT, fontSize: 13, width: 140, color: T.fg1, background: "transparent" }} />
                </span>
                {canAdd && folder && !q && countIn(folder) === 0 && (
                  <Button subtle onClick={() => run(async () => {
                    await call(`/practice/matters/${encodeURIComponent(matterRef)}/folders/remove`, { method: "POST", body: { path: folder } });
                    setFolder("");
                  })}><Icon d={ICON.trash} size={14} />Remove folder</Button>
                )}
                {onDraftAdjustments && ticked.size > 0 && (
                  <Button onClick={() => onDraftAdjustments(files.filter(f => ticked.has(f.id)).map(f => ({ kind: "file", id: f.id, name: f.name })))}>
                    <Icon d={ICON.dollar} size={14} />Draft Statement of Adjustments ({ticked.size})<Beta />
                  </Button>
                )}
                {canAdd && (
                  <>
                    <input ref={picker} type="file" multiple hidden aria-label="Choose files"
                      onChange={e => { send(e.target.files); e.target.value = ""; }} />
                    <Button primary onClick={() => picker.current && picker.current.click()}><Icon d={ICON.plus} size={14} />Upload</Button>
                  </>
                )}
              </div>
              {missing && <div style={{ padding: 16 }}><Empty>The document store is not on this server yet.</Empty></div>}
              {error && <p role="alert" style={{ color: T.danger, fontSize: 13, margin: "10px 16px 0" }}>{error}</p>}
              {uploads.length > 0 && (
                <div aria-live="polite" style={{ padding: "8px 16px", fontSize: 13, color: T.fg2 }}>
                  {uploads.map(u => <div key={u.name}>Uploading {u.name}… {u.progress}%</div>)}
                </div>
              )}
              {!missing && (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: FONT, tableLayout: "fixed", minWidth: 520 }}>
                    <thead>
                      <tr style={{ background: T.bg, textAlign: "left" }}>
                        {onDraftAdjustments && folder !== BIN_FOLDER && <th aria-label="Select" style={{ width: 36, padding: "8px 0 8px 12px" }} />}
                        <th style={{ padding: "8px 16px", fontWeight: 600, color: T.fg3, fontSize: 12 }}>Name</th>
                        {q && <th style={{ padding: "8px 16px", fontWeight: 600, color: T.fg3, fontSize: 12, width: 170 }}>Folder</th>}
                        <th style={{ padding: "8px 16px", fontWeight: 600, color: T.fg3, fontSize: 12, width: 100 }}>Added</th>
                        <th style={{ padding: "8px 16px", fontWeight: 600, color: T.fg3, fontSize: 12, width: 80 }}>Size</th>
                        <th aria-label="Actions" style={{ padding: "8px 16px", width: 190 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map(f => {
                        const open = fileHref ? fileHref(`${filePath(matterRef, f.id)}/content`) : "";
                        const save = fileHref ? fileHref(`${filePath(matterRef, f.id)}/content?download=1`) : "";
                        return (
                          <tr key={f.id} style={{ borderTop: `1px solid ${T.strokeSubtle}`, background: ticked.has(f.id) ? T.brandTint : undefined }}
                            onContextMenu={onDraftAdjustments && !f.binnedAt ? (e) => {
                              e.preventDefault();
                              const chosen = ticked.has(f.id) ? files.filter(x => ticked.has(x.id)) : [f];
                              setMenu({ x: e.clientX, y: e.clientY, docs: chosen.map(x => ({ kind: "file", id: x.id, name: x.name })), file: f, open });
                            } : undefined}>
                            {onDraftAdjustments && !f.binnedAt && folder !== BIN_FOLDER && (
                              <td style={{ padding: "7px 0 7px 12px" }}>
                                <input type="checkbox" aria-label={`Select ${f.name}`} checked={ticked.has(f.id)} style={{ accentColor: T.brand, margin: 0 }}
                                  onChange={() => setTicked(s => { const n = new Set(s); if (n.has(f.id)) n.delete(f.id); else n.add(f.id); return n; })} />
                              </td>
                            )}
                            <td style={{ padding: "7px 16px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, maxWidth: "100%" }}>
                                <span style={{ color: T.fg3, display: "inline-flex" }}><Icon d={ICON.file} size={15} /></span>
                                {open && !f.binnedAt
                                  ? <a href={open} target="_blank" rel="noreferrer" title={f.addedBy ? `Added by ${f.addedBy}` : undefined} style={{ color: T.fg1, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis" }}>{f.name}</a>
                                  : <span style={{ overflow: "hidden", textOverflow: "ellipsis", color: f.binnedAt ? T.fg3 : T.fg1 }}>{f.name}</span>}
                              </span>
                            </td>
                            {q && <td style={{ padding: "7px 16px", color: T.fg3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.folder || "Matter documents"}</td>}
                            <td style={{ padding: "7px 16px", color: T.fg3, whiteSpace: "nowrap" }}>{formatWhen(f.createdAt)}</td>
                            <td style={{ padding: "7px 16px", color: T.fg3, whiteSpace: "nowrap" }}>{formatSize(f.size)}</td>
                            <td style={{ padding: "4px 12px", textAlign: "right", whiteSpace: "nowrap" }}>
                              {f.binnedAt ? (
                                <>
                                  <RowAction label={`Restore ${f.name}`} onClick={() => run(() => call(filePath(matterRef, f.id), { method: "POST", body: { binned: false } }))}>Restore</RowAction>
                                  <RowAction label={`Delete ${f.name} for good`} onClick={() => setDialog({ kind: "purge", file: f })}>Delete</RowAction>
                                </>
                              ) : (
                                <>
                                  {save && <a href={save} aria-label={`Download ${f.name}`} title="Download" style={{ color: T.fg3, fontSize: 12.5, padding: "2px 4px", textDecoration: "none" }}>Download</a>}
                                  <RowAction label={`Rename ${f.name}`} onClick={() => setDialog({ kind: "rename", file: f, name: f.name })}>Rename</RowAction>
                                  <RowAction label={`Move ${f.name}`} onClick={() => setDialog({ kind: "move", file: f, folder: f.folder })}>Move</RowAction>
                                  <RowAction label={`Move ${f.name} to the bin`} onClick={() => run(() => call(filePath(matterRef, f.id), { method: "POST", body: { binned: true } }))}><Icon d={ICON.trash} size={14} /></RowAction>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {listing && shown.length === 0 && (
                        <tr><td colSpan={(q ? 5 : 4) + (onDraftAdjustments && folder !== BIN_FOLDER ? 1 : 0)} style={{ padding: 16, color: T.fg4 }}>
                          {q ? "No files match." : folder === BIN_FOLDER ? "The bin is empty." : canAdd ? "No files here yet. Drop files here or press Upload." : "No files here yet."}
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>

      {dialog?.kind === "rename" && (
        <Dialog title="Rename file" width={460} onClose={() => setDialog(null)}
          footer={<><Button onClick={() => setDialog(null)}>Cancel</Button>
            <Button primary onClick={() => run(async () => { await call(filePath(matterRef, dialog.file.id), { method: "POST", body: { name: dialog.name } }); setDialog(null); })}>Save</Button></>}>
          <label htmlFor="file-name" style={labelStyle}>Name</label>
          <input id="file-name" value={dialog.name} onChange={e => setDialog({ ...dialog, name: e.target.value })} style={{ ...inputStyle, marginTop: 4 }} />
        </Dialog>
      )}
      {dialog?.kind === "move" && (
        <Dialog title={`Move ${dialog.file.name}`} width={460} onClose={() => setDialog(null)}
          footer={<><Button onClick={() => setDialog(null)}>Cancel</Button>
            <Button primary onClick={() => run(async () => { await call(filePath(matterRef, dialog.file.id), { method: "POST", body: { folder: dialog.folder } }); setDialog(null); })}>Move</Button></>}>
          <label htmlFor="file-folder" style={labelStyle}>Folder</label>
          <select id="file-folder" value={dialog.folder} onChange={e => setDialog({ ...dialog, folder: e.target.value })} style={{ ...inputStyle, marginTop: 4 }}>
            <option value="">Matter documents</option>
            {folders.map(f => <option key={f} value={f}>{f.replace(/\//g, " › ")}</option>)}
          </select>
        </Dialog>
      )}
      {dialog?.kind === "folder" && (
        <Dialog title="New folder" width={460} onClose={() => setDialog(null)}
          footer={<><Button onClick={() => setDialog(null)}>Cancel</Button>
            <Button primary onClick={() => run(async () => { await call(`/practice/matters/${encodeURIComponent(matterRef)}/folders`, { method: "POST", body: { path: dialog.path } }); setDialog(null); })}>Make folder</Button></>}>
          <label htmlFor="folder-path" style={labelStyle}>Folder (a / makes a folder inside another)</label>
          <input id="folder-path" value={dialog.path} onChange={e => setDialog({ ...dialog, path: e.target.value })} style={{ ...inputStyle, marginTop: 4 }} />
        </Dialog>
      )}
      {dialog?.kind === "purge" && (
        <Dialog title="Delete for good?" width={460} onClose={() => setDialog(null)}
          footer={<><Button onClick={() => setDialog(null)}>Cancel</Button>
            <Button primary onClick={() => run(async () => { await call(filePath(matterRef, dialog.file.id), { method: "DELETE" }); setDialog(null); })}>Delete</Button></>}>
          <p style={{ margin: 0, fontSize: 14 }}>{dialog.file.name} will be deleted and can't be got back.</p>
        </Dialog>
      )}
      {menu && (
        <ContextMenu at={menu} onClose={() => setMenu(null)} items={[
          { label: menu.docs.length > 1 ? `Draft Statement of Adjustments (${menu.docs.length} files)` : "Draft Statement of Adjustments", icon: ICON.dollar, beta: true, onClick: () => onDraftAdjustments(menu.docs) },
          ...(menu.open ? [{ label: "Open", icon: ICON.eye, onClick: () => window.open(menu.open, "_blank", "noopener") }] : []),
          { label: "Rename", icon: ICON.edit, onClick: () => setDialog({ kind: "rename", file: menu.file, name: menu.file.name }) },
          { label: "Move", icon: ICON.folder, onClick: () => setDialog({ kind: "move", file: menu.file, folder: menu.file.folder }) },
        ]} />
      )}
    </div>
  );
};

// ── Settlement financials: the statement of adjustments ─────────────────────
//
// Triconvey's Settlement Financials dialog — Adjustments and Payments — with a
// Review of what a person must look at, the certificates it was drafted from,
// a preview of the statement and Send to other side. **Every figure is the
// middleware's** (`GET /practice/matters/:ref/settlement`): this screen sends
// the items staff enter and draws the columns, totals and payments that come
// back. It never apportions (middleware settlement-adjustments.md).
//
// Drafting from certificates runs in the background on the middleware; the
// dialog and the matter both poll the job until it is done.

const settlementPath = (ref, rest = "") => `/practice/matters/${encodeURIComponent(ref)}/settlement${rest}`;
export const formatMoney = (n) => {
  if (n === null || n === undefined || n === "" || !Number.isFinite(Number(n))) return "";
  const v = Number(n);
  return `${v < 0 ? "-" : ""}$${Math.abs(v).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const SOA_TYPES = [
  ["timeRelated", "Time related"],
  ["waterUsage", "Water usage"],
  ["registrationFees", "Registration fees"],
  ["miscellaneous", "Miscellaneous"],
  ["penaltyInterest", "Penalty interest"],
  ["rent", "Rent or licence fees"],
];
const SOA_TITLES = ["Rates, Charges & Levies", "Total Service Charge", "Water Service Charge", "Sewerage Service Charge",
  "Drainage", "Parks & Gardens", "Owners Corporation Fees", "Land Tax"];
// Triconvey's quick picks for a miscellaneous item.
const SOA_MISC = [
  ["Vendor allows for PEXA fee", "vendor"],
  ["Default Notice Fee served by Vendor's solicitor", "purchaser"],
];
const REGISTRATIONS = ["Discharge of Mortgage", "Withdrawal of Caveat", "Withdrawal of Priority Notice"];
const LV_KEY = { "Discharge of Mortgage": "dischargeOfMortgage", "Withdrawal of Caveat": "withdrawalOfCaveat", "Withdrawal of Priority Notice": "withdrawalOfPriorityNotice" };
const PAYMENT_TYPES = ["EFT", "BPAY", "Cheque", "Trust", "Other"];
const LEVEL_LABEL = { block: ["Must fix", "danger"], check: ["To check", "warn"], info: ["Worth knowing", "neutral"] };

// A new item of a type, with what the matter already says filled in.
export const blankItem = (type, { settlementDate = "", amountDue = null, fees = null } = {}) => {
  switch (type) {
    case "timeRelated": return { type, title: "Rates, Charges & Levies", payee: "", frequency: "annually", periodTo: "", periodFrom: "", useFromDate: false, amount: "", paidAmount: "", arrears: "", basis: "paid", payOutstanding: true };
    case "waterUsage": return { type, title: "Water Usage Charge", payee: "", fromDate: "", toDate: "", dailyRate: "" };
    case "registrationFees": return { type, title: "Registration Fees", payee: "Land Victoria", allowedBy: "vendor",
      lines: [{ label: "Discharge of Mortgage", quantity: 1, fee: fees?.lv?.fees?.dischargeOfMortgage?.amount ?? "" }] };
    case "miscellaneous": return { type, title: "", payee: "", allowedBy: "purchaser", amount: "", description: "" };
    case "penaltyInterest": return { type, title: "Penalty Interest", payee: "", allowedBy: "purchaser", principalBasis: "balance", principal: "", ratePercent: "", days: "", fromDate: "", toDate: "" };
    case "rent": return { type, title: "Rent", payee: "", rentAmount: "", rentFrequency: "weekly", paidTo: "", recoverArrears: false, bond: "" };
    default: return { type };
  }
};

const Field = ({ label, htmlFor, children, wide }) => (
  <div style={{ display: "grid", gridTemplateColumns: wide ? "1fr" : "150px 1fr", gap: 4, alignItems: "center", marginBottom: 8 }}>
    <label htmlFor={htmlFor} style={labelStyle}>{label}</label>
    <div style={{ minWidth: 0 }}>{children}</div>
  </div>
);
const MoneyIn = ({ id, value, onChange, label }) => (
  <input id={id} aria-label={label} inputMode="decimal" value={typeof value === "number" ? value.toFixed(2) : value ?? ""} onChange={e => onChange(e.target.value)}
    placeholder="$0.00" style={{ ...inputStyle, textAlign: "right" }} />
);
const Check = ({ label, checked, onChange }) => (
  <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: T.fg2, cursor: "pointer" }}>
    <input type="checkbox" checked={Boolean(checked)} onChange={e => onChange(e.target.checked)} style={{ accentColor: T.brand, margin: 0 }} />{label}
  </label>
);

// One adjustment item, as Triconvey's Settlement Adjustment Item dialog lays
// it out: the type, the adjustment details, and where its money goes.
export const AdjustmentItemDialog = ({ item: initial, result, fees, figures, onSave, onClose, onDelete }) => {
  const [item, setItem] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setItem(i => ({ ...i, [k]: v }));
  const changeType = (type) => setItem(i => ({ ...blankItem(type, { fees }), id: i.id, origin: i.origin, source: i.source }));
  const save = async () => {
    setBusy(true); setError("");
    try { await onSave(item); } catch (e) { setError(e.message); setBusy(false); }
  };
  const id = (k) => `soa-${k}`;
  const allowed = (
    <Field label="Allowed by" htmlFor={id("allowed")}>
      <select id={id("allowed")} value={item.allowedBy || "purchaser"} onChange={e => set("allowedBy", e.target.value)} style={inputStyle}>
        <option value="vendor">Vendor allows the purchaser</option>
        <option value="purchaser">Purchaser allows the vendor</option>
      </select>
    </Field>
  );
  return (
    <Dialog title={initial.id ? "Settlement adjustment item" : "Add adjustment item"} width={600} onClose={onClose}
      footer={<>
        {onDelete && initial.id && <span style={{ marginRight: "auto" }}><LinkButton danger onClick={onDelete}>Remove item</LinkButton></span>}
        <Button onClick={onClose}>Cancel</Button>
        <Button primary disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</Button>
      </>}>
      <Field label="Type" htmlFor={id("type")}>
        <select id={id("type")} value={item.type} onChange={e => changeType(e.target.value)} style={inputStyle}>
          {SOA_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </Field>
      <Fieldset legend="Adjustment details">
        <Field label="Title" htmlFor={id("title")}>
          <input id={id("title")} list={item.type === "timeRelated" ? "soa-titles" : item.type === "miscellaneous" ? "soa-misc" : undefined}
            value={item.title || ""} onChange={e => {
              const v = e.target.value;
              const misc = SOA_MISC.find(m => m[0] === v);
              setItem(i => ({ ...i, title: v, ...(misc && i.type === "miscellaneous" ? { allowedBy: misc[1] } : {}) }));
            }} style={inputStyle} />
          <datalist id="soa-titles">{SOA_TITLES.map(t => <option key={t} value={t} />)}</datalist>
          <datalist id="soa-misc">{SOA_MISC.map(([t]) => <option key={t} value={t} />)}</datalist>
        </Field>
        {item.type !== "registrationFees" && (
          <Field label="Payee" htmlFor={id("payee")}>
            <input id={id("payee")} value={item.payee || ""} onChange={e => set("payee", e.target.value)} placeholder="e.g. Boroondara City Council" style={inputStyle} />
          </Field>
        )}

        {item.type === "timeRelated" && (<>
          <Field label="Period" htmlFor={id("freq")}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <select id={id("freq")} value={item.frequency} onChange={e => set("frequency", e.target.value)} style={{ ...inputStyle, width: 140 }}>
                <option value="annually">Annually</option><option value="half-yearly">Half-yearly</option>
                <option value="quarterly">Quarterly</option><option value="monthly">Monthly</option><option value="custom">Custom</option>
              </select>
              <span style={{ fontSize: 13, color: T.fg3 }}>to</span>
              <DateInput id={id("to")} label="Period to" value={item.periodTo} onChange={v => set("periodTo", v)} width={150} />
              <Check label="Use from date" checked={item.useFromDate || item.frequency === "custom"} onChange={v => set("useFromDate", v)} />
            </div>
          </Field>
          {(item.useFromDate || item.frequency === "custom") && (
            <Field label="From" htmlFor={id("from")}><DateInput id={id("from")} label="Period from" value={item.periodFrom} onChange={v => set("periodFrom", v)} width={150} /></Field>
          )}
          <Field label="Total amount" htmlFor={id("amount")}>
            <div style={{ display: "flex", gap: 8 }}>
              <MoneyIn id={id("amount")} label="Total amount" value={item.amount} onChange={v => set("amount", v)} />
              <select aria-label="Adjust as" value={item.basis || "paid"} onChange={e => set("basis", e.target.value)} style={{ ...inputStyle, width: 160 }}>
                <option value="paid">Adjust as Paid</option><option value="unpaid">Adjust as Unpaid</option>
              </select>
            </div>
          </Field>
          <Field label="Less paid amount" htmlFor={id("paid")}><MoneyIn id={id("paid")} label="Paid amount" value={item.paidAmount} onChange={v => set("paidAmount", v)} /></Field>
          <Field label="Arrears amount" htmlFor={id("arrears")}><MoneyIn id={id("arrears")} label="Arrears" value={item.arrears} onChange={v => set("arrears", v)} /></Field>
        </>)}

        {item.type === "waterUsage" && (<>
          <Field label="Last meter reading" htmlFor={id("from")}><DateInput id={id("from")} label="Last meter reading" value={item.fromDate} onChange={v => set("fromDate", v)} width={150} /></Field>
          <Field label="To (blank: settlement)" htmlFor={id("to")}><DateInput id={id("to")} label="Usage to" value={item.toDate} onChange={v => set("toDate", v)} width={150} /></Field>
          <Field label="Average per day" htmlFor={id("rate")}><MoneyIn id={id("rate")} label="Daily rate" value={item.dailyRate} onChange={v => set("dailyRate", v)} /></Field>
          <Check label="Adjust in the columns instead of paying the authority" checked={item.adjustInColumns} onChange={v => set("adjustInColumns", v)} />
        </>)}

        {item.type === "registrationFees" && (<>
          {(item.lines || []).map((l, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 64px 110px auto", gap: 8, alignItems: "center", marginBottom: 8 }}>
              <input aria-label="Registration" list="soa-registrations" value={l.label} onChange={e => {
                const label = e.target.value;
                const known = fees?.lv?.fees?.[LV_KEY[label]]?.amount;
                set("lines", item.lines.map((x, j) => (j === i ? { ...x, label, ...(known ? { fee: known } : {}) } : x)));
              }} style={inputStyle} />
              <input aria-label="Quantity" inputMode="numeric" value={l.quantity} onChange={e => set("lines", item.lines.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))} style={{ ...inputStyle, textAlign: "right" }} />
              <MoneyIn label="Fee" value={l.fee} onChange={v => set("lines", item.lines.map((x, j) => (j === i ? { ...x, fee: v } : x)))} />
              <button type="button" aria-label="Remove line" onClick={() => set("lines", item.lines.filter((_, j) => j !== i))} style={{ background: "none", border: "none", cursor: "pointer", color: T.fg3 }}><Icon d={ICON.close} size={14} /></button>
            </div>
          ))}
          <datalist id="soa-registrations">{REGISTRATIONS.map(r => <option key={r} value={r} />)}</datalist>
          <div style={{ marginBottom: 10 }}><LinkButton onClick={() => set("lines", [...(item.lines || []), { label: "Withdrawal of Caveat", quantity: 1, fee: fees?.lv?.fees?.withdrawalOfCaveat?.amount ?? "" }])}><Icon d={ICON.plus} size={12} /> Add a registration</LinkButton></div>
          {fees?.lv && <p style={{ fontSize: 12, color: T.fg4, margin: "0 0 8px" }}>Land Victoria {fees.lv.year} fees{fees.lv.source === "live" ? ", read from their fee page" : " from the schedule held here (their page could not be read)"}.</p>}
          {allowed}
        </>)}

        {item.type === "miscellaneous" && (<>
          <Field label="Description" htmlFor={id("desc")}>
            <textarea id={id("desc")} rows={3} value={item.description || ""} onChange={e => set("description", e.target.value)} style={{ ...inputStyle, height: "auto", padding: 8 }} />
          </Field>
          <Field label="Amount" htmlFor={id("amount")}><MoneyIn id={id("amount")} label="Amount" value={item.amount} onChange={v => set("amount", v)} /></Field>
          {allowed}
        </>)}

        {item.type === "penaltyInterest" && (<>
          <Field label="Interest runs on" htmlFor={id("basis")}>
            <select id={id("basis")} value={item.principalBasis || "custom"} onChange={e => set("principalBasis", e.target.value)} style={inputStyle}>
              <option value="balance">Balance owing{figures?.balance != null ? ` (${formatMoney(figures.balance)})` : ""}</option>
              <option value="price">Purchase price{figures?.price != null ? ` (${formatMoney(figures.price)})` : ""}</option>
              <option value="custom">Another amount</option>
            </select>
          </Field>
          {(item.principalBasis || "custom") === "custom" && (
            <Field label="Amount" htmlFor={id("principal")}><MoneyIn id={id("principal")} label="Amount" value={item.principal} onChange={v => set("principal", v)} /></Field>
          )}
          <Field label="Penalty rate (% a year)" htmlFor={id("rate")}><input id={id("rate")} inputMode="decimal" value={item.ratePercent ?? ""} onChange={e => set("ratePercent", e.target.value)} placeholder="the rate in the contract's particulars" style={inputStyle} /></Field>
          <Field label="Days" htmlFor={id("days")}>
            <input id={id("days")} inputMode="numeric" value={item.days ?? ""} onChange={e => set("days", e.target.value)} placeholder="days interest is payable for" style={{ ...inputStyle, width: 140 }} />
          </Field>
          {!String(item.days ?? "").trim() && (<>
            <p style={{ fontSize: 12, color: T.fg4, margin: "-2px 0 8px" }}>Or count the days between two dates:</p>
            <Field label="From" htmlFor={id("from")}><DateInput id={id("from")} label="Default from" value={item.fromDate} onChange={v => set("fromDate", v)} width={150} /></Field>
            <Field label="To (blank: settlement)" htmlFor={id("to")}><DateInput id={id("to")} label="Default to" value={item.toDate} onChange={v => set("toDate", v)} width={150} /></Field>
          </>)}
          <p style={{ fontSize: 12, color: T.fg4, margin: "0 0 8px" }}>Simple interest over a 365-day year, worked out when you save.</p>
          {allowed}
        </>)}

        {item.type === "rent" && (<>
          <Field label="Rent" htmlFor={id("rent")}>
            <div style={{ display: "flex", gap: 8 }}>
              <MoneyIn id={id("rent")} label="Rent" value={item.rentAmount} onChange={v => set("rentAmount", v)} />
              <select aria-label="Rent frequency" value={item.rentFrequency} onChange={e => set("rentFrequency", e.target.value)} style={{ ...inputStyle, width: 140 }}>
                <option value="weekly">Weekly</option><option value="fortnightly">Fortnightly</option><option value="monthly">Monthly</option>
              </select>
            </div>
          </Field>
          <Field label="Paid to" htmlFor={id("paidTo")}><DateInput id={id("paidTo")} label="Rent paid to" value={item.paidTo} onChange={v => set("paidTo", v)} width={150} /></Field>
          <Field label="Bond" htmlFor={id("bond")}><MoneyIn id={id("bond")} label="Bond" value={item.bond} onChange={v => set("bond", v)} /></Field>
          <Check label="Purchaser allows rent in arrears" checked={item.recoverArrears} onChange={v => set("recoverArrears", v)} />
          <p style={{ fontSize: 12, color: T.fg4, margin: "8px 0" }}>Rent and bond come from the property manager — confirm them by phone or email before settlement.</p>
        </>)}
        <div style={{ marginTop: 6 }}><Check label="Plus GST" checked={item.gst} onChange={v => set("gst", v)} /></div>
      </Fieldset>

      {result && (result.lines || []).length > 0 && (
        <Fieldset legend="Description">
          <div style={{ display: "flex", gap: 16, alignItems: "flex-start", fontSize: 13 }}>
            <div style={{ flex: 1, background: T.bg, borderRadius: R, padding: "8px 10px", whiteSpace: "pre-line", color: T.fg2 }}>{result.lines.join("\n")}</div>
            <div style={{ textAlign: "right", minWidth: 120 }}>
              <div style={{ color: T.fg3 }}>{result.vendor ? "Vendor allows" : result.purchaser ? "Purchaser allows" : result.payment ? "Paid to" : ""}</div>
              <div style={{ fontWeight: 600 }}>{formatMoney(result.vendor || result.purchaser || result.payment?.amount)}</div>
            </div>
          </div>
          <p style={{ fontSize: 12, color: T.fg4, margin: "6px 0 0" }}>As last saved; saving works it out again.</p>
        </Fieldset>
      )}
      {item.type === "timeRelated" && (
        <Fieldset legend="Payment details">
          <Check label="Pay what is owing to the payee at settlement" checked={item.payOutstanding !== false} onChange={v => set("payOutstanding", v)} />
          {result?.payment && <div style={{ fontSize: 13, color: T.fg2, marginTop: 6 }}>{result.payment.payee || "Payee"}: {formatMoney(result.payment.amount)}</div>}
        </Fieldset>
      )}
      {item.source?.document && (
        <p style={{ fontSize: 12, color: T.fg3, margin: "0 0 4px" }}>
          Drafted from {item.source.document}{item.source.issueDate ? ` dated ${formatDay(item.source.issueDate)}` : ""}{item.source.fileName ? ` (${item.source.fileName}${item.source.evidence ? `, ${item.source.evidence}` : ""})` : ""}.
        </p>
      )}
      {error && <p role="alert" style={{ color: T.danger, fontSize: 13 }}>{error}</p>}
    </Dialog>
  );
};

// The documents a draft can read, picked: files on the matter and returned
// searches, with an upload for one that is not there yet.
export const DraftAdjustmentsDialog = ({ call, matterRef, postFile, searches = [], preselected = [], onStarted, onClose }) => {
  const [files, setFiles] = useState(null);
  const [picked, setPicked] = useState(() => new Set(preselected.map(d => `${d.kind}:${d.id}`)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(null);
  const picker = useRef(null);
  useEffect(() => {
    let live = true;
    call(`/practice/matters/${encodeURIComponent(matterRef)}/files`)
      .then(d => { if (live) setFiles(d.files || []); })
      .catch(() => { if (live) setFiles([]); });
    return () => { live = false; };
  }, [call, matterRef]);
  const readable = (name = "", type = "") => /pdf|image/.test(type) || /\.(pdf|png|jpe?g|webp|heic)$/i.test(name);
  const docs = files && [
    ...files.filter(f => readable(f.name, f.contentType)).map(f => ({ kind: "file", id: f.id, name: f.name, where: f.folder || "Matter documents" })),
    ...searches.filter(s => s.hasFile || s.storagePath || s.driveFileUrl)
      .map(s => ({ kind: "search", id: s.id, name: s.displayName || s.name || "Certificate", where: "Section 32 searches" })),
  ];
  const toggle = (key) => setPicked(p => { const n = new Set(p); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const submit = async () => {
    setBusy(true); setError("");
    try {
      const documents = [...picked].map(k => { const [kind, ...rest] = k.split(":"); return { kind, id: rest.join(":") }; });
      const data = await call(settlementPath(matterRef, "/draft"), { method: "POST", body: { documents } });
      onStarted(data);
    } catch (e) { setError(e.message); setBusy(false); }
  };
  const uploadAndDraft = async (file) => {
    if (!file || !postFile) return;
    setError(""); setUploading({ name: file.name, progress: 0 });
    try {
      const blob = new Blob([file], { type: "application/octet-stream" });
      const res = await postFile(settlementPath(matterRef, `/draft/upload?name=${encodeURIComponent(file.name)}`), blob,
        (p) => setUploading({ name: file.name, progress: p }));
      onStarted(res && typeof res === "object" ? res : { message: `${file.name} is being read.` });
    } catch (e) { setError(e.message); setUploading(null); }
  };
  const list = docs || [];
  const named = preselected.filter(d => d.name).map(d => d.name);
  return (
    <Dialog title={<span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>Draft Statement of Adjustments<Beta /></span>} label="Draft Statement of Adjustments" width={620} onClose={onClose}
      footer={<>
        <Button onClick={onClose}>Cancel</Button>
        <Button primary disabled={busy || picked.size === 0} onClick={submit}><Icon d={ICON.send} size={14} />{busy ? "Starting…" : "Draft adjustments"}</Button>
      </>}>
      <p style={{ margin: "0 0 12px", fontSize: 14, color: T.fg2, lineHeight: 1.5 }}>
        Once submitted, the drafter reads {named.length === 1 ? <strong>{named[0]}</strong> : "the certificates"} for the rates, charges, usage and
        levies, and calculates the adjustments to the settlement date. It runs in the background — keep working; the adjustments
        appear in <strong>Settlement financials</strong> when ready, usually within about {draftSeconds(Math.max(1, picked.size))} seconds.
        Nothing is sent to anyone.
      </p>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: T.fg3, lineHeight: 1.5 }}>
        This is a beta: check every drafted figure against its certificate — each one waits in the Review for you to confirm it.
      </p>
      <div role="group" aria-label="Documents to read" style={{ border: `1px solid ${T.stroke}`, borderRadius: R, maxHeight: 280, overflowY: "auto" }}>
        {!docs && <div style={{ padding: 12 }}><Empty>Loading the matter's documents…</Empty></div>}
        {docs && list.length === 0 && <div style={{ padding: 12 }}><Empty>No PDFs on this matter yet. Upload the certificates below.</Empty></div>}
        {list.map(d => {
          const key = `${d.kind}:${d.id}`;
          return (
            <label key={key} style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 12px", borderTop: `1px solid ${T.strokeSubtle}`, cursor: "pointer", fontSize: 13 }}>
              <input type="checkbox" checked={picked.has(key)} onChange={() => toggle(key)} style={{ accentColor: T.brand, margin: 0 }} />
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</span>
              <span style={{ color: T.fg4, fontSize: 12, whiteSpace: "nowrap" }}>{d.where}</span>
            </label>
          );
        })}
      </div>
      {postFile && (
        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <input ref={picker} type="file" hidden accept=".pdf,image/*" aria-label="Upload a certificate" onChange={e => { uploadAndDraft(e.target.files[0]); e.target.value = ""; }} />
          <Button onClick={() => picker.current && picker.current.click()} disabled={Boolean(uploading)}><Icon d={ICON.import} size={14} />Upload a certificate and draft</Button>
          {uploading && <span aria-live="polite" style={{ fontSize: 13, color: T.fg3 }}>Uploading {uploading.name}… {uploading.progress}%</span>}
        </div>
      )}
      {error && <p role="alert" style={{ color: T.danger, fontSize: 13, marginTop: 10 }}>{error}</p>}
    </Dialog>
  );
};

// What the middleware will say a draft of `n` files takes (its
// `estimateSeconds`), so the dialog can say it before the press.
const draftSeconds = (n) => Math.min(180, 45 + 15 * Math.max(0, n - 1));

// Polls a matter's settlement state while a draft is running.
export const useSettlement = (call, matterRef, { enabled = true } = {}) => {
  const [state, setState] = useState(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      const data = await call(settlementPath(matterRef));
      // An answer without the financials is a server that has no such route.
      if (!data || !data.financials) { setMissing(true); return null; }
      setState(data); setMissing(false); setError("");
      return data;
    } catch (e) {
      if (e.status === 404 && !/Matter not found/.test(e.message)) setMissing(true);
      else setError(e.message);
      return null;
    }
  }, [call, matterRef]);
  useEffect(() => { if (enabled) load(); }, [enabled, load]);
  const running = state?.job?.status === "running";
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [running, load]);
  return { state, setState, missing, error, load, running };
};

const JobBanner = ({ job, onOpen }) => {
  if (!job) return null;
  const started = Date.parse(job.startedAt || "");
  if (job.status === "running") {
    return (
      <div role="status" style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 14px", borderRadius: R, background: T.brandTint, border: `1px solid ${T.brandStroke}`, fontSize: 13, color: T.fg1 }}>
        <span style={{ flex: 1 }}>Reading {job.files?.length === 1 ? job.files[0] : `${job.files?.length || ""} documents`} and calculating the adjustments — usually about {job.estimateSeconds || 60} seconds{Number.isFinite(started) ? ` (started ${new Date(started).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })})` : ""}. You can keep working.</span>
        <span aria-hidden="true" style={{ width: 80, height: 4, borderRadius: 2, background: T.brandStroke, overflow: "hidden", position: "relative" }}>
          <span style={{ position: "absolute", inset: 0, width: "40%", background: T.brand, animation: "soaSlide 1.2s ease-in-out infinite" }} />
        </span>
        <style>{"@keyframes soaSlide{0%{transform:translateX(-100%)}100%{transform:translateX(250%)}}"}</style>
      </div>
    );
  }
  if (job.status === "failed") {
    return <div role="alert" style={{ padding: "10px 14px", borderRadius: R, background: T.dangerTint, border: `1px solid ${T.dangerStroke}`, fontSize: 13, color: T.danger }}>The draft could not be made: {job.error}</div>;
  }
  if (job.status === "done" && onOpen) {
    return (
      <div role="status" style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 14px", borderRadius: R, background: T.brandTint, border: `1px solid ${T.brandStroke}`, fontSize: 13 }}>
        <span style={{ flex: 1 }}>Statement of adjustments drafted from {job.documents || "the"} certificate{job.documents === 1 ? "" : "s"}{job.errors?.length ? ` — ${job.errors.length} file${job.errors.length === 1 ? "" : "s"} could not be read` : ""}.</span>
        <Button primary onClick={onOpen}>View the adjustments</Button>
      </div>
    );
  }
  return null;
};

const TotalsLine = ({ label, value, strong, rule }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "3px 0", fontSize: 13, fontWeight: strong ? 700 : 400,
    borderTop: rule ? `1px solid ${T.fg1}` : "none" }}>
    <span>{label}</span><span style={{ whiteSpace: "nowrap" }}>{value}</span>
  </div>
);

// The statement, as the other side will get it: the middleware's HTML in a
// frame, with the PDF a link away (read token, never the session).
const StatementPreview = ({ call, matterRef, fileHref, onClose, onSend }) => {
  const [html, setHtml] = useState(null);
  const [open, setOpen] = useState(0);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    call(settlementPath(matterRef, "/statement")).then(d => { if (live) { setHtml(d.html); setOpen(d.open || 0); } }).catch(e => live && setError(e.message));
    return () => { live = false; };
  }, [call, matterRef]);
  const pdf = fileHref ? fileHref(settlementPath(matterRef, "/statement.pdf")) : "";
  return (
    <Dialog title="Statement of adjustments" width={900} onClose={onClose}
      footer={<>
        {open > 0 && <span style={{ marginRight: "auto", fontSize: 13, color: T.warn }}>Marked DRAFT: {open} thing{open === 1 ? "" : "s"} still to check.</span>}
        {pdf && <a href={pdf} target="_blank" rel="noreferrer" style={{ fontSize: 14, color: T.brand, textDecoration: "none", padding: "0 8px" }}>Open PDF</a>}
        <Button onClick={onClose}>Close</Button>
        {onSend && <Button primary onClick={onSend}><Icon d={ICON.send} size={14} />Send to other side</Button>}
      </>}>
      {error && <p role="alert" style={{ color: T.danger, fontSize: 13 }}>{error}</p>}
      {!html && !error && <Empty>Building the statement…</Empty>}
      {html && <iframe title="Statement of adjustments preview" srcDoc={html} sandbox="" style={{ width: "100%", height: "70vh", border: `1px solid ${T.stroke}`, borderRadius: R, background: T.card }} />}
    </Dialog>
  );
};

const SendStatementDialog = ({ call, matterRef, state, onSent, onClose }) => {
  const other = state.otherSide || {};
  const [to, setTo] = useState(other.email || "");
  const [cc, setCc] = useState("");
  const [message, setMessage] = useState("");
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const counts = state.review?.counts || {};
  const openChecks = (state.review?.findings || []).filter(f => f.level === "check" && !f.acknowledged);
  const send = async () => {
    setBusy(true); setError("");
    try {
      const data = await call(settlementPath(matterRef, "/send"), { method: "POST", body: { to, cc, message, override } });
      onSent(data);
    } catch (e) { setError(e.message); setBusy(false); }
  };
  return (
    <Dialog title="Send to other side" width={560} onClose={onClose}
      footer={<>
        <Button onClick={onClose}>Cancel</Button>
        <Button primary disabled={busy || counts.block > 0 || (openChecks.length > 0 && !override) || !to.trim()} onClick={send}>
          <Icon d={ICON.send} size={14} />{busy ? "Sending…" : "Send statement"}
        </Button>
      </>}>
      <p style={{ margin: "0 0 12px", fontSize: 14, color: T.fg2 }}>
        The statement is emailed as a PDF to {other.name || "the other side's conveyancer"} from the file handler, and a copy is filed in the matter's documents.
      </p>
      {counts.block > 0 && <p role="alert" style={{ color: T.danger, fontSize: 13 }}>Fix what is marked “Must fix” in the Review first.</p>}
      <Field label="To" htmlFor="soa-to"><input id="soa-to" value={to} onChange={e => setTo(e.target.value)} placeholder="conveyancer@example.com.au" style={inputStyle} /></Field>
      <Field label="Cc" htmlFor="soa-cc"><input id="soa-cc" value={cc} onChange={e => setCc(e.target.value)} style={inputStyle} /></Field>
      <Field label="Message (optional)" htmlFor="soa-msg" wide>
        <textarea id="soa-msg" rows={4} value={message} onChange={e => setMessage(e.target.value)} placeholder="Please find attached our Statement of Adjustments…" style={{ ...inputStyle, height: "auto", padding: 8 }} />
      </Field>
      {openChecks.length > 0 && counts.block === 0 && (
        <div style={{ background: T.warnTint, border: `1px solid ${T.warnStroke}`, borderRadius: R, padding: "8px 12px", fontSize: 13 }}>
          <div style={{ fontWeight: 600, color: T.warn, marginBottom: 4 }}>{openChecks.length} thing{openChecks.length === 1 ? "" : "s"} still to check</div>
          <ul style={{ margin: "0 0 6px", paddingLeft: 18, color: T.fg2 }}>{openChecks.slice(0, 6).map(f => <li key={f.id}>{f.title}</li>)}</ul>
          <Check label="I have checked these — send anyway" checked={override} onChange={setOverride} />
        </div>
      )}
      {error && <p role="alert" style={{ color: T.danger, fontSize: 13, marginTop: 10 }}>{error}</p>}
    </Dialog>
  );
};

// Triconvey's ADD ADJUSTMENT ITEM button, with its types under the arrow.
const AddItemMenu = ({ onPick }) => {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);
  return (
    <span ref={box} style={{ position: "relative", display: "inline-flex" }}>
      <Button onClick={() => setOpen(o => !o)}><Icon d={ICON.plus} size={14} />Add adjustment item<Icon d={ICON.down} size={14} /></Button>
      {open && (
        <div role="menu" style={{ position: "absolute", bottom: "100%", left: 0, marginBottom: 4, minWidth: 220, background: T.card, borderRadius: 8, boxShadow: SHADOW, padding: "4px 0", zIndex: 90 }}>
          {SOA_TYPES.map(([k, l]) => (
            <button key={k} type="button" role="menuitem" onClick={() => { setOpen(false); onPick(k); }}
              style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: "8px 14px", fontSize: 13, fontFamily: FONT, color: T.fg1, cursor: "pointer" }}>{l}</button>
          ))}
        </div>
      )}
    </span>
  );
};

// The Settlement financials dialog itself.
export const SettlementDialog = ({ call, matterRef, postFile, fileHref, settlement, searches = [], onEditConveyancing, onClose, onChanged, initialTab = "adjustments" }) => {
  const { state, setState, error: loadError, load, running } = settlement;
  const [tab, setTab] = useState(initialTab);
  const [editing, setEditing] = useState(null);
  const [sub, setSub] = useState(null);
  const [error, setError] = useState("");
  const [savingPayments, setSavingPayments] = useState(false);
  const fin = state?.financials || { items: [], payments: [] };
  const statement = state?.statement || { rows: [], totals: {}, payments: [] };
  const resultOf = (id) => statement.rows.find(r => r.id === id) || null;
  const review = state?.review || { findings: [], counts: {} };
  const t = statement.totals || {};

  const saveAll = async (items, payments = fin.payments, extra = {}) => {
    const data = await call(settlementPath(matterRef), { method: "POST", body: { items, payments, adjustmentDate: fin.adjustmentDate || "", ...extra } });
    setState(data);
    onChanged && onChanged(data);
    return data;
  };
  const saveItem = async (item) => {
    const exists = item.id && fin.items.some(i => i.id === item.id);
    const items = exists ? fin.items.map(i => (i.id === item.id ? item : i)) : [...fin.items, { ...item, origin: "manual" }];
    await saveAll(items);
    setEditing(null);
  };
  const removeItem = async (id) => {
    try { await saveAll(fin.items.filter(i => i.id !== id)); setEditing(null); } catch (e) { setError(e.message); }
  };
  const ack = async (f, acknowledged = true) => {
    setError("");
    try {
      const data = await call(settlementPath(matterRef, "/ack"), { method: "POST", body: { id: f.id, acknowledged } });
      setState(data); onChanged && onChanged(data);
    } catch (e) { setError(e.message); }
  };
  const [payDraft, setPayDraft] = useState(null);
  useEffect(() => { setPayDraft(null); }, [state]);
  const payments = payDraft || fin.payments;
  const setPayment = (id, k, v) => setPayDraft(payments.map(p => (p.id === id ? { ...p, [k]: v } : p)));
  const savePayments = async (list = payments) => {
    setSavingPayments(true); setError("");
    try { await saveAll(fin.items, list); } catch (e) { setError(e.message); } finally { setSavingPayments(false); }
  };

  const doAction = (f) => {
    if (f.action === "confirm") return ack(f);
    if (f.action === "addRent") return setEditing(blankItem("rent"));
    if (f.action === "addPenaltyInterest") return setEditing(blankItem("penaltyInterest"));
    if (f.action === "upload") return setSub({ kind: "draft" });
    return null;
  };
  const openItem = (id) => { const i = fin.items.find(x => x.id === id); if (i) setEditing(i); };
  const badge = (item) => (item.origin === "draft" && !item.confirmed && !item.edited ? <Pill tone="warn">Confirm</Pill>
    : item.origin === "draft" && item.confirmed ? <Pill tone="brand">Confirmed</Pill>
      : item.edited ? <Pill>Edited</Pill> : null);

  const counts = review.counts || {};
  const reviewCount = (counts.block || 0) + (counts.check || 0);
  const tabs = [
    ["adjustments", "Adjustments", fin.items.length || null],
    ["payments", "Payments", (statement.payments || []).length || null],
    ["review", "Review", reviewCount || null],
    ["certificates", "Certificates", (fin.sources || []).length || null],
  ];
  const lastSent = (fin.sent || []).slice(-1)[0];

  return (
    <>
      <Dialog title="Settlement financials" width={1000} onClose={onClose}
        footer={<>
          <span style={{ marginRight: "auto", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Button onClick={() => setSub({ kind: "draft" })}><Icon d={ICON.import} size={14} />Draft from certificates<Beta /></Button>
            {lastSent && <span style={{ fontSize: 12, color: T.fg3 }}>Sent to {lastSent.to} on {formatDay(lastSent.at)} (v{lastSent.version})</span>}
          </span>
          <Button onClick={() => setSub({ kind: "preview" })}><Icon d={ICON.eye} size={14} />View Statement of Adjustments</Button>
          <Button primary disabled={!state || counts.block > 0} title={counts.block > 0 ? "Fix what is marked Must fix first" : undefined} onClick={() => setSub({ kind: "send" })}>
            <Icon d={ICON.send} size={14} />Send to other side
          </Button>
        </>}>
        {!state && !loadError && <Empty>Loading…</Empty>}
        {loadError && <p role="alert" style={{ color: T.danger, fontSize: 13 }}>{loadError}</p>}
        {state && (<>
          <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", fontSize: 13, marginBottom: 8 }}>
            <span>Settlement date: <strong>{state.figures.settlementDate ? formatDay(state.figures.settlementDate) : "not set"}</strong></span>
            <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>Adjustment date:
              <DateInput id="soa-adjustment-date" label="Adjustment date" value={fin.adjustmentDate || state.figures.adjustmentDate || ""} width={140}
                onChange={v => { if (v !== (fin.adjustmentDate || state.figures.adjustmentDate || "")) saveAll(fin.items, fin.payments, { adjustmentDate: v }).catch(e => setError(e.message)); }} />
            </span>
            <span style={{ flex: 1 }} />
            {onEditConveyancing && <LinkButton onClick={onEditConveyancing}>Edit Conveyancing Details</LinkButton>}
          </div>
          {(running || state.job?.status === "failed") && <div style={{ marginBottom: 8 }}><JobBanner job={state.job} /></div>}
          <Tabs tabs={tabs} active={tab} onChange={setTab} />
          {error && <p role="alert" style={{ color: T.danger, fontSize: 13 }}>{error}</p>}

          {tab === "adjustments" && (
            <div style={{ marginTop: 8 }}>
              <div style={{ border: `1px solid ${T.stroke}`, borderRadius: R, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 640 }}>
                  <thead>
                    <tr style={{ background: T.bg, textAlign: "left" }}>
                      <th style={{ padding: "8px 12px", fontWeight: 600, color: T.fg3, fontSize: 12 }}>Title & description</th>
                      <th style={{ padding: "8px 12px", fontWeight: 600, color: T.fg3, fontSize: 12, width: 170 }}>Payee</th>
                      <th style={{ padding: "8px 12px", fontWeight: 600, color: T.fg3, fontSize: 12, width: 100, textAlign: "right" }}>Payment</th>
                      <th style={{ padding: "8px 12px", fontWeight: 600, color: T.fg3, fontSize: 12, width: 100, textAlign: "right" }}>Vendor</th>
                      <th style={{ padding: "8px 12px", fontWeight: 600, color: T.fg3, fontSize: 12, width: 100, textAlign: "right" }}>Purchaser</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fin.items.map(item => {
                      const r = resultOf(item.id) || { lines: [], problems: [] };
                      return (
                        <tr key={item.id} onClick={() => setEditing(item)} style={{ borderTop: `1px solid ${T.strokeSubtle}`, cursor: "pointer", verticalAlign: "top" }}
                          tabIndex={0} onKeyDown={e => { if (e.key === "Enter") setEditing(item); }} aria-label={`Edit ${item.title}`}>
                          <td style={{ padding: "8px 12px" }}>
                            <div style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 600 }}>{item.title}{badge(item)}</div>
                            <div style={{ whiteSpace: "pre-line", color: T.fg2 }}>{r.lines.join("\n")}</div>
                            {r.problems.length > 0 && <div style={{ color: T.danger, fontSize: 12, marginTop: 2 }}>{r.problems.join(" · ")}</div>}
                          </td>
                          <td style={{ padding: "8px 12px", color: T.fg2 }}>{item.type === "registrationFees" ? item.payee || "Land Victoria" : item.payee ? `[${item.payee}]` : ""}</td>
                          <td style={{ padding: "8px 12px", textAlign: "right" }}>{r.payment ? formatMoney(r.payment.amount) : ""}</td>
                          <td style={{ padding: "8px 12px", textAlign: "right" }}>{r.vendor ? formatMoney(r.vendor) : ""}</td>
                          <td style={{ padding: "8px 12px", textAlign: "right" }}>{r.purchaser ? formatMoney(r.purchaser) : ""}</td>
                        </tr>
                      );
                    })}
                    {fin.items.length === 0 && (
                      <tr><td colSpan={5} style={{ padding: 16, color: T.fg4 }}>
                        No adjustments yet. <LinkButton onClick={() => setSub({ kind: "draft" })}>Draft them from the certificates</LinkButton> or add an item.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div style={{ display: "flex", gap: 24, marginTop: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
                <div style={{ flex: "1 1 240px" }}>
                  <AddItemMenu onPick={k => setEditing(blankItem(k, { settlementDate: state.figures.settlementDate, amountDue: t.balance, fees: state.fees }))} />
                </div>
                <div style={{ flex: "0 1 340px", minWidth: 280 }}>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 24, fontSize: 13, fontWeight: 600, paddingBottom: 4, borderBottom: `2px solid ${T.fg1}` }}>
                    <span>{formatMoney(t.vendor)}</span><span>{formatMoney(t.purchaser)}</span>
                  </div>
                  <TotalsLine label="Purchase price" value={t.price === null ? "—" : formatMoney(t.price)} />
                  <TotalsLine label="Less deposit paid" value={formatMoney(t.deposit || 0)} />
                  <TotalsLine label="Balance" value={formatMoney(t.balance)} rule />
                  <TotalsLine label={t.adjustments < 0 ? "Less adjustments" : "Plus adjustments"} value={formatMoney(Math.abs(t.adjustments || 0))} />
                  {t.gst ? <TotalsLine label="Plus GST" value={formatMoney(t.gst)} /> : null}
                  <TotalsLine label="Amount due on settlement" value={formatMoney(t.amountDue)} strong rule />
                </div>
              </div>
            </div>
          )}

          {tab === "payments" && (
            <div style={{ marginTop: 8 }}>
              <div style={{ border: `1px solid ${T.stroke}`, borderRadius: R, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 560 }}>
                  <thead>
                    <tr style={{ background: T.bg, textAlign: "left" }}>
                      <th style={{ padding: "8px 12px", fontWeight: 600, color: T.fg3, fontSize: 12 }}>Payee</th>
                      <th style={{ padding: "8px 12px", fontWeight: 600, color: T.fg3, fontSize: 12, width: 120 }}>Payment type</th>
                      <th style={{ padding: "8px 12px", fontWeight: 600, color: T.fg3, fontSize: 12, width: 140, textAlign: "right" }}>Amount</th>
                      <th aria-label="Actions" style={{ width: 40 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {(statement.payments || []).filter(p => p.source === "item").map(p => (
                      <tr key={p.id} style={{ borderTop: `1px solid ${T.strokeSubtle}` }}>
                        <td style={{ padding: "8px 12px" }}>{p.label}<div style={{ fontSize: 12, color: T.fg4 }}>From the adjustments — change it there</div></td>
                        <td style={{ padding: "8px 12px", color: T.fg3 }}>{p.type}</td>
                        <td style={{ padding: "8px 12px", textAlign: "right" }}>{formatMoney(p.amount)}</td>
                        <td />
                      </tr>
                    ))}
                    {payments.map(p => (
                      <tr key={p.id} style={{ borderTop: `1px solid ${T.strokeSubtle}` }}>
                        <td style={{ padding: "6px 12px" }}>
                          <input aria-label="Payee" value={p.payee} onChange={e => setPayment(p.id, "payee", e.target.value)} onBlur={() => payDraft && savePayments()} style={inputStyle} />
                          {p.note && <div style={{ fontSize: 12, color: T.fg4, marginTop: 2 }}>{p.note}</div>}
                        </td>
                        <td style={{ padding: "6px 12px" }}>
                          <select aria-label={`${p.payee} payment type`} value={p.type || "EFT"} onChange={e => savePayments(payments.map(x => (x.id === p.id ? { ...x, type: e.target.value } : x)))} style={inputStyle}>
                            {PAYMENT_TYPES.map(x => <option key={x} value={x}>{x}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: "6px 12px" }}>
                          <MoneyIn label={`${p.payee} amount`} value={p.amount} onChange={v => setPayment(p.id, "amount", v)} />
                          {(p.amount === "" || p.amount === null) && <div style={{ fontSize: 12, color: T.warn, marginTop: 2, textAlign: "right" }}>Enter the amount</div>}
                        </td>
                        <td style={{ padding: "6px 4px" }}>
                          <button type="button" aria-label={`Remove ${p.payee}`} onClick={() => savePayments(payments.filter(x => x.id !== p.id))} style={{ background: "none", border: "none", cursor: "pointer", color: T.fg3 }}><Icon d={ICON.trash} size={14} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: "flex", gap: 24, marginTop: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
                <div style={{ flex: "1 1 240px", display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Button onClick={() => savePayments([...payments, { payee: "New payee", amount: "", type: "EFT", origin: "manual" }])}><Icon d={ICON.plus} size={14} />Add payment</Button>
                  {payDraft && <Button primary disabled={savingPayments} onClick={() => savePayments()}>{savingPayments ? "Saving…" : "Save payments"}</Button>}
                </div>
                <div style={{ flex: "0 1 340px", minWidth: 280 }}>
                  <TotalsLine label="Total payments to be made" value={formatMoney(t.payments)} strong />
                  <TotalsLine label="Amount due on settlement" value={formatMoney(t.amountDue)} />
                  <TotalsLine label="Less payments" value={formatMoney(t.payments)} />
                  <TotalsLine label="Balance to be advised" value={formatMoney(t.balanceToAdvise)} strong rule />
                </div>
              </div>
            </div>
          )}

          {tab === "review" && (
            <div style={{ marginTop: 8 }}>
              {review.findings.length === 0 && <Empty>Nothing to check.</Empty>}
              {["block", "check", "info"].map(level => {
                const list = review.findings.filter(f => f.level === level);
                if (!list.length) return null;
                const [label, tone] = LEVEL_LABEL[level];
                return (
                  <div key={level} style={{ marginBottom: 12 }}>
                    <h3 style={{ fontSize: 12, fontWeight: 600, color: T.fg3, margin: "8px 0 4px" }}>{label}</h3>
                    {list.map(f => (
                      <div key={f.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 10px", border: `1px solid ${T.strokeSubtle}`, borderRadius: R, marginBottom: 6, opacity: f.acknowledged ? 0.6 : 1 }}>
                        <Pill tone={f.acknowledged ? "neutral" : tone}>{f.area}</Pill>
                        <div style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
                          <div style={{ fontWeight: 600 }}>{f.title}</div>
                          <div style={{ color: T.fg2 }}>{f.detail}</div>
                          {f.acknowledged && <div style={{ fontSize: 12, color: T.fg4 }}>Checked by {f.acknowledged.by || "staff"}</div>}
                        </div>
                        <div style={{ display: "flex", gap: 10, alignItems: "center", whiteSpace: "nowrap" }}>
                          {f.itemId && <LinkButton onClick={() => openItem(f.itemId)}>Open</LinkButton>}
                          {f.action === "confirm" && <Button onClick={() => ack(f)}><Icon d={ICON.check} size={14} />Confirm</Button>}
                          {f.action === "addRent" && <Button onClick={() => doAction(f)}>Add rent</Button>}
                          {f.action === "addPenaltyInterest" && <Button onClick={() => doAction(f)}>Add penalty interest</Button>}
                          {f.action === "upload" && <Button onClick={() => doAction(f)}>Add certificates</Button>}
                          {f.paymentId && <LinkButton onClick={() => setTab("payments")}>Payments</LinkButton>}
                          {f.field && onEditConveyancing && <LinkButton onClick={onEditConveyancing}>Edit</LinkButton>}
                          {level === "check" && f.action !== "confirm" && (f.acknowledged
                            ? <LinkButton onClick={() => ack(f, false)}>Undo</LinkButton>
                            : <LinkButton onClick={() => ack(f)}>Looks right</LinkButton>)}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {tab === "certificates" && (
            <div style={{ marginTop: 8 }}>
              {(fin.sources || []).length === 0 && <Empty>No certificates read yet.</Empty>}
              {(fin.sources || []).map((s, i) => (
                <div key={`${s.fileId}-${s.kind}-${i}`} style={{ padding: "8px 10px", border: `1px solid ${T.strokeSubtle}`, borderRadius: R, marginBottom: 6, fontSize: 13 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <strong>{s.name}</strong>{s.issuer && <span style={{ color: T.fg3 }}>· {s.issuer}</span>}
                    {s.issueDate && <span style={{ color: T.fg3 }}>· issued {formatDay(s.issueDate)}</span>}
                    {s.reference && <span style={{ color: T.fg4 }}>· {s.reference}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: T.fg4 }}>{s.fileName}{s.propertyAddress ? ` · ${s.propertyAddress}` : ""}</div>
                </div>
              ))}
              {state.fees && (
                <p style={{ fontSize: 12, color: T.fg4 }}>
                  Fees: Land Victoria {state.fees.lv.year} ({state.fees.lv.source === "live" ? "read live" : "schedule held here"}) ·
                  PEXA ({state.fees.pexa.source === "live" ? "read live" : "FY27 schedule"}), checked {formatWhen(state.fees.pexa.checkedAt)}.
                </p>
              )}
            </div>
          )}
        </>)}
      </Dialog>

      {editing && (
        <AdjustmentItemDialog item={editing} result={editing.id ? resultOf(editing.id) : null} fees={state?.fees} figures={t}
          onSave={saveItem} onClose={() => setEditing(null)} onDelete={editing.id ? () => removeItem(editing.id) : null} />
      )}
      {sub?.kind === "draft" && (
        <DraftAdjustmentsDialog call={call} matterRef={matterRef} postFile={postFile} searches={searches} onClose={() => setSub(null)}
          onStarted={() => { setSub(null); load(); }} />
      )}
      {sub?.kind === "preview" && (
        <StatementPreview call={call} matterRef={matterRef} fileHref={fileHref} onClose={() => setSub(null)} onSend={() => setSub({ kind: "send" })} />
      )}
      {sub?.kind === "send" && state && (
        <SendStatementDialog call={call} matterRef={matterRef} state={state} onClose={() => setSub(null)}
          onSent={(data) => { setState(data); setSub(null); onChanged && onChanged(data); }} />
      )}
    </>
  );
};

// Drafting from certificates is beta: said wherever it is started.
const Beta = () => <Pill tone="brand">Beta</Pill>;

// The right-click menu on a document row.
const ContextMenu = ({ at, items, onClose }) => {
  useEffect(() => {
    const close = () => onClose();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("mousedown", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("mousedown", close); window.removeEventListener("scroll", close, true); window.removeEventListener("keydown", onKey); };
  }, [onClose]);
  return createPortal((
    <div role="menu" onMouseDown={e => e.stopPropagation()}
      style={{ position: "fixed", left: Math.min(at.x, window.innerWidth - 270), top: Math.min(at.y, window.innerHeight - 40 * items.length - 16), zIndex: 450, minWidth: 250,
        background: T.card, borderRadius: R, boxShadow: SHADOW, padding: 4, fontFamily: FONT }}>
      {items.map(it => (
        <button key={it.label} type="button" role="menuitem" onClick={() => { onClose(); it.onClick(); }}
          style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", background: "none", border: "none", padding: "8px 10px", fontSize: 13, fontFamily: FONT, color: T.fg1, cursor: "pointer", borderRadius: R }}
          onMouseEnter={e => { e.currentTarget.style.background = T.bg; }} onMouseLeave={e => { e.currentTarget.style.background = "none"; }}>
          {it.icon && <Icon d={it.icon} size={14} />}{it.label}{it.beta && <span style={{ marginLeft: "auto" }}><Beta /></span>}
        </button>
      ))}
    </div>
  ), document.body);
};

// ── The Matter check ────────────────────────────────────────────────────────
//
// What is missing, disagreeing or inconsistent across the matter, with the
// evidence beside each, so staff verify rather than type
// (`GET /practice/matters/:ref/check`; middleware practice-core.md, "The
// Matter check"). Every rule is the middleware's: this draws findings and
// sends the four actions (use a value, edit, "Looks right", ask the client).

const CHECK_LEVELS = [
  ["block", "Must fix", "danger"],
  ["check", "To check", "warn"],
  ["info", "Worth knowing", "neutral"],
];

const showValue = (v) => (v === true ? "Yes" : v === false ? "No" : v === null || v === undefined || v === "" ? "(blank)" : String(v));

const CheckRow = ({ f, onUse, onEdit, onAck, picked, onPick, busy }) => (
  <div style={{ display: "flex", gap: 10, padding: "10px 14px", borderTop: `1px solid ${T.strokeSubtle}`, opacity: f.acknowledged ? 0.6 : 1 }}>
    {f.ask && !f.acknowledged
      ? <input type="checkbox" aria-label={`Ask the client: ${f.title}`} checked={picked} onChange={e => onPick(e.target.checked)}
          style={{ marginTop: 3, accentColor: T.brand }} />
      : <span style={{ width: 13, flex: "none" }} />}
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: T.fg1 }}>{f.title}</div>
      {(f.asked || f.acknowledged) && (
        <div style={{ fontSize: 12, color: T.fg3 }}>
          {f.asked && "Asked the client"}{f.asked && f.acknowledged && " · "}
          {f.acknowledged && `Looks right${f.acknowledged.by ? ` (${f.acknowledged.by})` : ""}`}
        </div>
      )}
      {f.detail && <div style={{ fontSize: 12.5, color: T.fg3, marginTop: 2 }}>{f.detail}</div>}
      {f.sources && f.sources.length > 0 && (
        <div role="list" aria-label="What each source says" style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
          {f.sources.map((s, i) => (
            <div role="listitem" key={i} style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "2px 8px", fontSize: 12.5 }}>
              <span style={{ color: T.fg3, minWidth: 110 }}>{s.label}</span>
              <span style={{ color: T.fg1, flex: 1, minWidth: 120, overflowWrap: "anywhere" }}>{showValue(s.value)}</span>
              {f.field && onUse && !f.acknowledged && <LinkButton onClick={() => onUse(f, s)}>Use this</LinkButton>}
            </div>
          ))}
        </div>
      )}
      {(onEdit || f.level !== "block") && (
        <div style={{ display: "flex", gap: 12, marginTop: 6 }}>
          {onEdit && <LinkButton onClick={() => onEdit(f)}>Edit</LinkButton>}
          {f.level !== "block" && <LinkButton onClick={() => !busy && onAck(f, !f.acknowledged)}>{f.acknowledged ? "Undo" : "Looks right"}</LinkButton>}
        </div>
      )}
    </div>
  </div>
);

export const MatterCheckPanel = ({ call, matterRef, check, onReload, onUse, editFor }) => {
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState(null);
  const [copied, setCopied] = useState(false);
  const base = `/practice/matters/${encodeURIComponent(matterRef)}/check`;
  const findings = check?.findings || [];

  const run = async (fn) => {
    setBusy(true); setError("");
    try { await fn(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const ack = (f, on) => run(async () => { await call(`${base}/ack`, { method: "POST", body: { id: f.id, acknowledged: on } }); await onReload(); });
  const use = (f, s) => run(async () => { await onUse(f.field, s.value); await onReload(); });
  const ask = () => run(async () => {
    const d = await call(`${base}/ask`, { method: "POST", body: { ids: [...picked] } });
    setDraft(d); setCopied(false); setPicked(new Set());
    await onReload();
  });
  const pick = (id, on) => setPicked(prev => { const next = new Set(prev); if (on) next.add(id); else next.delete(id); return next; });

  if (!check) return <p style={{ color: T.fg3, fontSize: 13 }}>Checking the matter…</p>;
  const open = findings.filter(f => !f.acknowledged);
  const done = findings.filter(f => f.acknowledged);

  return (
    <div>
      <div aria-label="Matter check">
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", flexWrap: "wrap" }}>
          <div style={{ flex: 1, fontSize: 13, color: T.fg3 }}>{open.length ? `${open.length} to look at` : "Nothing to look at"}</div>
          <Button primary disabled={busy || picked.size === 0} onClick={ask}><Icon d={ICON.mail} size={14} />Ask the client{picked.size ? ` (${picked.size})` : ""}</Button>
        </div>
        {error && <p role="alert" style={{ color: T.danger, fontSize: 13, margin: "0 14px 10px" }}>{error}</p>}
        {open.length === 0 && <div style={{ padding: "4px 14px 16px" }}><Empty>Everything the check looks at is in order.</Empty></div>}
        {CHECK_LEVELS.map(([level, label, tone]) => {
          const rows = open.filter(f => f.level === level);
          if (!rows.length) return null;
          return (
            <section key={level} aria-label={label}>
              <div style={{ padding: "6px 14px", background: T.bg, borderTop: `1px solid ${T.stroke}`, display: "flex", alignItems: "center", gap: 8 }}>
                <Pill tone={tone}>{label}</Pill><span style={{ fontSize: 12, color: T.fg3 }}>{rows.length}</span>
              </div>
              {rows.map(f => (
                <CheckRow key={f.id} f={f} busy={busy} picked={picked.has(f.id)} onPick={on => pick(f.id, on)}
                  onUse={onUse ? use : null} onEdit={editFor(f)} onAck={ack} />
              ))}
            </section>
          );
        })}
        {done.length > 0 && (
          <section aria-label="Looks right">
            <div style={{ padding: "6px 14px", background: T.bg, borderTop: `1px solid ${T.stroke}`, fontSize: 12, color: T.fg3 }}>Looks right: {done.length}</div>
            {done.map(f => <CheckRow key={f.id} f={f} busy={busy} picked={false} onPick={() => {}} onEdit={null} onAck={ack} />)}
          </section>
        )}
      </div>

      {draft && (
        <Dialog title="Ask the client" width={620} onClose={() => setDraft(null)}
          footer={<>
            <Button onClick={() => setDraft(null)}>Close</Button>
            <Button onClick={() => {
              const text = `${draft.subject}\n\n${draft.body}`;
              if (navigator.clipboard) navigator.clipboard.writeText(text).then(() => setCopied(true), () => {});
            }}>{copied ? "Copied" : "Copy"}</Button>
            <a href={`mailto:${encodeURIComponent((draft.to || []).join(","))}?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.body)}`}
              style={{ display: "inline-flex", alignItems: "center", height: 32, padding: "0 12px", borderRadius: R, background: T.brand,
                color: T.onBrand, fontWeight: 600, fontSize: 14, textDecoration: "none", border: `1px solid ${T.brand}` }}>Open in email</a>
          </>}>
          <p style={{ margin: "0 0 10px", fontSize: 13, color: T.fg3 }}>
            A draft to send from your email. Their answers come back to you; check them in here.
          </p>
          <div style={{ fontSize: 13, marginBottom: 6 }}><span style={{ color: T.fg3 }}>To: </span>{(draft.to || []).join(", ") || "(no email on the client's card)"}</div>
          <div style={{ fontSize: 13, marginBottom: 10 }}><span style={{ color: T.fg3 }}>Subject: </span>{draft.subject}</div>
          <textarea aria-label="Message" readOnly value={draft.body} rows={12}
            style={{ ...inputStyle, height: "auto", padding: "8px 10px", lineHeight: "20px", resize: "vertical" }} />
        </Dialog>
      )}
    </div>
  );
};


// ── Open matters, as tabs across the window ─────────────────────────────────
//
// Smokeball opens each matter in its own window; a web app opens them as tabs
// inside one, the way the new Outlook does, with "Pop out" for a second
// screen. `matters` is `[{ref, label}]`; App.js owns the list.
// `inline`: in the page's centred column under the classic menu row (the
// steps before the practice look, and the WordPress frame), rather than
// across the window under the brand bar.
export const MatterTabs = ({ matters = [], activeRef, onOpen, onClose, onHome, homeActive, inline = false }) => (
  <div role="tablist" aria-label="Open matters"
    style={{ display: "flex", alignItems: "flex-end", gap: 0, height: 38, boxSizing: "border-box", padding: inline ? 0 : "0 12px", background: inline ? "transparent" : T.card,
      borderBottom: `1px solid ${T.stroke}`, overflowX: "auto", fontFamily: FONT }}>
    {[{ ref: null, label: "All matters" }, ...matters].map((m, i) => {
      const on = m.ref === null ? homeActive : m.ref === activeRef;
      return (
        <span key={m.ref || "home"} style={{ display: "inline-flex", alignItems: "center", height: 34, flexShrink: 0,
          borderLeft: i ? `1px solid ${T.strokeSubtle}` : "none", boxShadow: on ? `inset 0 -2px 0 ${T.brand}` : "none" }}>
          <button type="button" role="tab" aria-selected={on} onClick={() => (m.ref === null ? onHome() : onOpen(m.ref))}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 34, padding: m.ref ? "0 4px 0 12px" : "0 12px",
              background: "none", border: "none", cursor: "pointer", fontFamily: FONT, fontSize: 13,
              fontWeight: on ? 600 : 400, color: on ? T.fg1 : T.fg3, whiteSpace: "nowrap" }}>
            {m.ref === null && <Icon d={ICON.folder} size={14} />}{m.label}
          </button>
          {m.ref && (
            <button type="button" aria-label={`Close ${m.label}`} onClick={() => onClose(m.ref)}
              style={{ background: "none", border: "none", cursor: "pointer", color: T.fg4, padding: "0 8px", display: "inline-flex", height: 34, alignItems: "center" }}>
              <Icon d={ICON.close} size={12} />
            </button>
          )}
        </span>
      );
    })}
  </div>
);

// ── Tasks, events and memos ─────────────────────────────────────────────────

const useRows = (call, ref, kind) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => call(`/practice/matters/${encodeURIComponent(ref)}/${kind}`)
    .then(d => { setRows(d.rows); setError(""); }).catch(e => setError(e.message)), [call, ref, kind]);
  useEffect(() => { load(); }, [load]);
  const add = async (body) => { await call(`/practice/matters/${encodeURIComponent(ref)}/${kind}`, { method: "POST", body }); await load(); };
  const edit = async (id, body) => { await call(`/practice/matters/${encodeURIComponent(ref)}/${kind}/${id}`, { method: "POST", body }); await load(); };
  const remove = async (id) => { await call(`/practice/matters/${encodeURIComponent(ref)}/${kind}/${id}`, { method: "DELETE" }); await load(); };
  return { rows, error, add, edit, remove, reload: load };
};

const AddBar = ({ children, onSubmit, busy }) => (
  <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
    style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", padding: 12, background: T.card,
      border: `1px solid ${T.stroke}`, borderRadius: 8, marginBottom: 12 }}>
    {children}
    <Button primary type="submit" disabled={busy}><Icon d={ICON.plus} size={14} />Add</Button>
  </form>
);

// The ribbon's New on a tab: focus that tab's first box, each time it is pressed.
const useFocusOn = (signal) => {
  const ref = useRef(null);
  useEffect(() => { if (signal && ref.current) ref.current.focus(); }, [signal]);
  return ref;
};

const TasksTab = ({ call, matterRef, onChanged, newSignal = 0 }) => {
  const tasks = useRows(call, matterRef, "tasks");
  const first = useFocusOn(newSignal);
  const [draft, setDraft] = useState({ subject: "", dueDate: "", assignee: "", priority: "normal" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (fn) => {
    setBusy(true); setError("");
    try { await fn(); onChanged && onChanged(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return (
    <div>
      <AddBar busy={busy} onSubmit={() => run(async () => {
        await tasks.add(draft);
        setDraft({ subject: "", dueDate: "", assignee: "", priority: "normal" });
      })}>
        <input ref={first} aria-label="Task" placeholder="New task" value={draft.subject} onChange={e => setDraft({ ...draft, subject: e.target.value })} style={{ ...inputStyle, flex: "2 1 220px", width: "auto" }} />
        <DateInput label="Due date" value={draft.dueDate} onChange={v => setDraft(d => ({ ...d, dueDate: v }))} width={160} />
        <input aria-label="Assigned to" placeholder="Assigned to" value={draft.assignee} onChange={e => setDraft({ ...draft, assignee: e.target.value })} style={{ ...inputStyle, width: 160 }} />
        <select aria-label="Priority" value={draft.priority} onChange={e => setDraft({ ...draft, priority: e.target.value })} style={{ ...inputStyle, width: 110 }}>
          <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
        </select>
      </AddBar>
      {(error || tasks.error) && <p role="alert" style={{ color: T.danger, fontSize: 13 }}>{error || tasks.error}</p>}
      <div style={{ background: T.card, border: `1px solid ${T.stroke}`, borderRadius: 8 }}>
        {(tasks.rows || []).map((t, i) => (
          <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderTop: i ? `1px solid ${T.stroke}` : "none" }}>
            <input type="checkbox" aria-label={`Done: ${t.subject}`} checked={Boolean(t.completed)}
              onChange={e => run(() => tasks.edit(t.id, { completed: e.target.checked }))} />
            <span style={{ flex: 1, fontSize: 13, textDecoration: t.completed ? "line-through" : "none", color: t.completed ? T.fg4 : T.fg1 }}>
              {t.subject}{t.priority === "high" && <strong style={{ color: T.danger, marginLeft: 6, fontSize: 11 }}>HIGH</strong>}
            </span>
            <span style={{ fontSize: 12.5, color: T.fg3, width: 160 }}>{t.assignee}</span>
            <span style={{ fontSize: 12.5, color: T.fg3, width: 90 }}>{formatDay(t.dueDate)}</span>
            <button type="button" aria-label={`Delete ${t.subject}`} onClick={() => run(() => tasks.remove(t.id))}
              style={{ background: "none", border: "none", cursor: "pointer", color: T.fg4 }}><Icon d={ICON.trash} size={14} /></button>
          </div>
        ))}
        {tasks.rows && tasks.rows.length === 0 && <div style={{ padding: 16 }}><Empty>No tasks on this matter.</Empty></div>}
      </div>
    </div>
  );
};

const EventsTab = ({ call, matterRef, onChanged, newSignal = 0 }) => {
  const diary = useRows(call, matterRef, "diary");
  const first = useFocusOn(newSignal);
  const blank = { title: "", date: "", startTime: "", endTime: "", location: "", kind: "appointment" };
  const [draft, setDraft] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (fn) => {
    setBusy(true); setError("");
    try { await fn(); onChanged && onChanged(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return (
    <div>
      <AddBar busy={busy} onSubmit={() => run(async () => { await diary.add(draft); setDraft(blank); })}>
        <input ref={first} aria-label="Event" placeholder="New event" value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} style={{ ...inputStyle, flex: "2 1 200px", width: "auto" }} />
        <DateInput label="Date" value={draft.date} onChange={v => setDraft(d => ({ ...d, date: v }))} width={160} />
        <input aria-label="Start" type="time" value={draft.startTime} onChange={e => setDraft({ ...draft, startTime: e.target.value })} style={{ ...inputStyle, width: 110 }} />
        <input aria-label="End" type="time" value={draft.endTime} onChange={e => setDraft({ ...draft, endTime: e.target.value })} style={{ ...inputStyle, width: 110 }} />
        <input aria-label="Location" placeholder="Location" value={draft.location} onChange={e => setDraft({ ...draft, location: e.target.value })} style={{ ...inputStyle, flex: "1 1 140px", width: "auto" }} />
        <select aria-label="Kind" value={draft.kind} onChange={e => setDraft({ ...draft, kind: e.target.value })} style={{ ...inputStyle, width: 140 }}>
          <option value="appointment">Appointment</option><option value="deadline">Deadline</option><option value="reminder">Reminder</option>
        </select>
      </AddBar>
      {(error || diary.error) && <p role="alert" style={{ color: T.danger, fontSize: 13 }}>{error || diary.error}</p>}
      <div style={{ background: T.card, border: `1px solid ${T.stroke}`, borderRadius: 8 }}>
        {(diary.rows || []).map((ev, i) => (
          <div key={ev.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 12px", borderTop: i ? `1px solid ${T.stroke}` : "none", fontSize: 13 }}>
            <span style={{ width: 90, fontWeight: 600 }}>{formatDay(ev.date)}</span>
            <span style={{ width: 100, color: T.fg3 }}>{[ev.startTime, ev.endTime].filter(Boolean).join("–")}</span>
            <span style={{ flex: 1 }}>{ev.title}{ev.location && <span style={{ color: T.fg3 }}> · {ev.location}</span>}</span>
            <span style={{ fontSize: 11.5, color: T.fg3, textTransform: "capitalize" }}>{ev.kind}</span>
            <button type="button" aria-label={`Delete ${ev.title}`} onClick={() => run(() => diary.remove(ev.id))}
              style={{ background: "none", border: "none", cursor: "pointer", color: T.fg4 }}><Icon d={ICON.trash} size={14} /></button>
          </div>
        ))}
        {diary.rows && diary.rows.length === 0 && <div style={{ padding: 16 }}><Empty>No events on this matter.</Empty></div>}
      </div>
    </div>
  );
};

const MemoEditor = ({ memo, onSave, onClose }) => {
  const [draft, setDraft] = useState({ title: memo?.title || "", body: memo?.body || "", pinned: Boolean(memo?.pinned) });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  return (
    <Dialog title={memo?.id ? "Edit memo" : "New memo"} onClose={onClose} width={620}
      footer={<>
        {error && <span role="alert" style={{ color: T.danger, fontSize: 12.5, flex: 1 }}>{error}</span>}
        <Button onClick={onClose}>Cancel</Button>
        <Button primary disabled={saving} onClick={async () => {
          setSaving(true); setError("");
          try { await onSave(draft); onClose(); } catch (e) { setError(e.message); } finally { setSaving(false); }
        }}>Save</Button>
      </>}>
      <input aria-label="Title" placeholder="Title" value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} style={{ ...inputStyle, marginBottom: 8 }} />
      <textarea aria-label="Memo" value={draft.body} onChange={e => setDraft({ ...draft, body: e.target.value })} rows={10}
        style={{ ...inputStyle, height: "auto", padding: "8px", resize: "vertical", lineHeight: 1.5 }} />
      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, marginTop: 8 }}>
        <input type="checkbox" checked={draft.pinned} onChange={e => setDraft({ ...draft, pinned: e.target.checked })} />
        Pin to the matter's notes
      </label>
    </Dialog>
  );
};

const MemosTab = ({ call, matterRef, onChanged, newSignal = 0 }) => {
  const memos = useRows(call, matterRef, "memos");
  const [editing, setEditing] = useState(null);
  useEffect(() => { if (newSignal) setEditing({}); }, [newSignal]);
  const save = async (draft) => {
    if (editing?.id) await memos.edit(editing.id, draft); else await memos.add(draft);
    onChanged && onChanged();
  };
  return (
    <div>
      <div style={{ marginBottom: 12 }}><Button primary onClick={() => setEditing({})}><Icon d={ICON.plus} size={14} />New memo</Button></div>
      {memos.error && <p role="alert" style={{ color: T.danger, fontSize: 13 }}>{memos.error}</p>}
      {(memos.rows || []).map(m => (
        <article key={m.id} style={{ background: T.card, border: `1px solid ${T.stroke}`, borderRadius: 8, padding: 12, marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {m.pinned && <span title="Pinned" style={{ color: T.danger }}><Icon d={ICON.pin} size={14} /></span>}
            <strong style={{ flex: 1, fontSize: 14 }}>{m.title || "Memo"}</strong>
            <span style={{ fontSize: 12, color: T.fg4 }}>{formatDay(m.createdAt)}</span>
            <LinkButton onClick={() => setEditing(m)}>Edit</LinkButton>
            <LinkButton danger onClick={async () => { if (window.confirm("Delete this memo?")) { await memos.remove(m.id); onChanged && onChanged(); } }}>Delete</LinkButton>
          </div>
          {m.body && <p style={{ margin: "6px 0 0", fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{m.body}</p>}
        </article>
      ))}
      {memos.rows && memos.rows.length === 0 && <Empty>No memos on this matter.</Empty>}
      {editing && <MemoEditor memo={editing} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
};

// ── The workspace ───────────────────────────────────────────────────────────

// `children` is the Section 32 module's own page (MatterDetailV2). It stays
// mounted on every tab — its panes hold state somebody may be part-way through
// — and is only hidden, the way App.js's own panes are.
//
// `refreshSignal` changes when another window saved something on this matter
// (App.js listens on a BroadcastChannel); the record is read again.
export const MatterWorkspace = ({ api, matter, registry = [], defaultTab = "matter", onSaveContractFields,
  onMatterChanged, onOpenSection32Agent, documentHref, postFile, fileHref, onPopOut, refreshSignal = 0, preview = PREVIEW_ON,
  emails = null, onStatusChanged, contractBadge = null, headerFacts = [], headerLinks = null, onOpenDutiesForm, dutiesStatus = null, onlyTabs = null, children }) => {
  const ready = (feature) => featureReady(feature, preview);
  const call = useCall(api);
  const { layout, error: layoutError } = useLayout(call);
  // Each matter keeps the tab it was left on, for this browser tab, so moving
  // between open matters comes back where it was (sessionStorage, by ref).
  const tabKey = `practice-tab:${matter.ref}`;
  const [tab, setTabState] = useState(() => {
    try { return window.sessionStorage.getItem(tabKey) || defaultTab; } catch { return defaultTab; }
  });
  const setTab = (key) => {
    setTabState(key);
    try { window.sessionStorage.setItem(tabKey, key); } catch { /* a private window forgets it */ }
  };
  const [newSignal, setNewSignal] = useState({ memos: 0, events: 0, tasks: 0 });
  const [s32Slot, setS32Slot] = useState(null);
  const startNew = (key) => { setTab(key); setNewSignal(n => ({ ...n, [key]: n[key] + 1 })); };
  const [record, setRecord] = useState(null);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);
  const [lists, setLists] = useState({ diary: [], tasks: [], memos: [] });
  const [check, setCheck] = useState(null);
  const [side, setSide] = useState("check");
  const [importSignal, setImportSignal] = useState(0);
  // Settlement financials: the statement of adjustments, and a draft the
  // office sent to the drafter from this tab (its banner follows it here).
  const settlement = useSettlement(call, matter.ref);
  const [watchingDraft, setWatchingDraft] = useState(false);
  const [detailsHeight, setDetailsHeight] = useState(() => {
    try { return Number(window.localStorage.getItem(DETAILS_HEIGHT_KEY)) || DETAILS_HEIGHT; } catch { return DETAILS_HEIGHT; }
  });
  const ref = matter.ref;
  const live = useRef(true);
  // Set on every mount, not only at creation: an effect run twice (React's
  // strict mode, or a remount that keeps the ref) cleared it for good, and
  // every load after that was dropped — the matter sat on "Loading the matter…".
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);

  const loadRecord = useCallback(async () => {
    try {
      const path = (kind) => `/practice/matters/${encodeURIComponent(ref)}/${kind}`;
      const [rec, diary, tasks, memos] = await Promise.all([
        call(`/practice/matters/${encodeURIComponent(ref)}`),
        call(path("diary")).catch(() => ({ rows: [] })),
        call(path("tasks")).catch(() => ({ rows: [] })),
        call(path("memos")).catch(() => ({ rows: [] })),
      ]);
      if (!live.current) return;
      setRecord(rec.record);
      setLists({ diary: diary.rows || [], tasks: tasks.rows || [], memos: memos.rows || [] });
      setError("");
    } catch (e) {
      // An older middleware has no `/practice` at all: say so, and leave the
      // Section 32 tab working rather than breaking the matter.
      if (live.current) setError(e.status === 404 ? "The main module is not on this server yet." : e.message);
    }
  }, [call, ref]);
  useEffect(() => { loadRecord(); }, [loadRecord, refreshSignal]);

  // The Matter check reads the contract fields and the record, so it is asked
  // again whenever either changes. An older middleware has no check: the tab
  // then says so rather than breaking the matter.
  const loadCheck = useCallback(async () => {
    try {
      const data = await call(`/practice/matters/${encodeURIComponent(ref)}/check`);
      if (live.current) setCheck(data);
    } catch (e) {
      if (live.current) setCheck(e.status === 404 ? { unavailable: true, findings: [] } : { error: e.message, findings: [] });
    }
  }, [call, ref]);
  useEffect(() => { loadCheck(); }, [loadCheck, refreshSignal, record, matter.contractFields]);

  const registryByName = useMemo(() => new Map(registry.map(f => [f.name, f])), [registry]);
  const recordByName = useMemo(() => new Map((layout?.recordFields || []).map(f => [f.name, f])), [layout]);
  const recordNames = useMemo(() => new Set(recordByName.keys()), [recordByName]);
  const defOf = useCallback((name) => recordByName.get(name) || registryByName.get(name) || { name, label: name, type: "text" }, [recordByName, registryByName]);
  // The note a role's row carries (`section.note`, the middleware's): where
  // the contract's wording for this party is.
  const roleNote = (roleKey) => (layout?.sections || []).find(sec => sec.kind === "role" && sec.role === roleKey)?.note || "";
  const contractValues = matter.contractFields?.values;
  const details = record?.details;
  const valueOf = useCallback((name) => (recordNames.has(name) ? (details || {})[name] : (contractValues || {})[name]),
    [recordNames, details, contractValues]);
  // One value of a row's summary, read out as the section says (`summaryAs`):
  // the price as money, a date with what it is the date of.
  const summaryPart = (section, n) => {
    const as = (section.summaryAs || {})[n] || {};
    const text = displayValue(as.type ? { ...defOf(n), type: as.type } : defOf(n), valueOf(n));
    return text && as.label ? `${as.label} ${text}` : text;
  };

  const changed = () => { onMatterChanged && onMatterChanged(); };

  const saveSection = async (contract, recordChanges) => {
    if (Object.keys(contract).length) {
      const ok = await onSaveContractFields(contract);
      if (!ok) throw new Error("The contract fields were not saved");
    }
    if (Object.keys(recordChanges).length) {
      const data = await call(`/practice/matters/${encodeURIComponent(ref)}/details`, { method: "POST", body: { fields: recordChanges } });
      setRecord(data.record);
    }
    // Critical dates read the contract's dates too.
    await loadRecord();
    changed();
  };

  // `extra`: `{ card }` (reviewed and edited) or `{ linkId }` (a card the
  // review named). Thrown on failure when `rethrow`, so the review stays open.
  const addSuggested = async (offer, extra = {}, { rethrow = false } = {}) => {
    try {
      const body = offer.kind === "fill" ? { role: offer.role, contactId: offer.contactId } : { role: offer.role, ...extra };
      const data = await call(`/practice/matters/${encodeURIComponent(ref)}/parties/suggested`, { method: "POST", body });
      setRecord(data.record);
      changed();
    } catch (e) { if (rethrow) throw e; setError(e.message); }
  };

  const linkRole = async (roleKey, ids) => {
    const data = await call(`/practice/matters/${encodeURIComponent(ref)}/parties`, { method: "POST", body: { role: roleKey, contactIds: ids } });
    setRecord(data.record);
    changed();
  };

  const [movingStage, setMovingStage] = useState(false);
  const setStage = async (stage) => {
    setMovingStage(true);
    try {
      const data = await call(`/practice/matters/${encodeURIComponent(ref)}/stage`, { method: "POST", body: { stage } });
      setRecord(data.record);
      changed();
    } catch (e) { setError(e.message); } finally { setMovingStage(false); }
  };
  // The matter type's own set; a middleware from before stage sets sends one
  // list for every type, on the layout.
  const stages = record?.stages || layout?.stages || [];
  const setType = async (matterType) => {
    const data = await call(`/practice/matters/${encodeURIComponent(ref)}/type`, { method: "POST", body: { matterType } });
    setRecord(data.record);
    changed();
  };
  const toggleTask = async (task) => {
    try {
      await call(`/practice/matters/${encodeURIComponent(ref)}/tasks/${task.id}`, { method: "POST", body: { completed: !task.completed } });
      await loadRecord();
      changed();
    } catch (e) { setError(e.message); }
  };

  const parties = record?.parties || {};
  const vendors = parties.vendor || [];
  const title = vendors.map(c => c.name).join(" & ") || matter.client || `Matter ${ref}`;
  const matterType = record?.matterType || matter.practiceMatterType || "";
  // The contract page's facts about the matter (its agent, when it opened)
  // read here, on the matter's own line, rather than in a bar on that tab.
  const meta = [ref, matterType, matter.address, matter.fileHandlerName, ...headerFacts].filter(Boolean);
  const roles = useMemo(() => new Map((layout?.roles || []).map(r => [r.key, r])), [layout]);
  const clientEmail = vendors.map(c => lead(c).email || c.organisation?.email).find(Boolean) || matter.clientEmail || "";
  const searches = matter.searches || [];
  const openTasks = lists.tasks.filter(t => !t.completed);

  const rowFor = (section) => {
    if (section.onlyFor && !section.onlyFor.includes(matterType)) return null;
    if ((section.hideFor || []).includes(matterType)) return null;
    if (section.kind === "info") return null; // read in the header
    if (section.kind === "type") {
      return <DetailRow key={section.key} label={section.label} value={matterType} empty={!matterType}
        onOpen={() => setDialog({ kind: "type" })} />;
    }
    if (section.kind === "role") {
      // A less common row shows only where the gear ticked it or a card is on it.
      if (section.optional && !(record.shownSections || []).includes(section.key)) return null;
      const role = roles.get(section.role) || { key: section.role, label: section.label };
      const cards = parties[section.role] || [];
      const vOf = (c) => (c.voiStatus && c.voiStatus !== "verified" && c.voiStatus !== "not_required" ? c.voiStatus : "");
      const chip = (voi) => (voi ? <Pill tone="warn">VOI {VOI_LABEL[voi]?.toLowerCase() || voi}</Pill> : null);
      if (cards.length <= 1) {
        return <DetailRow key={section.key} label={section.label} indent={section.indent} empty={cards.length === 0}
          value={cards.map(c => c.name).join(", ")} chip={chip(cards.map(vOf).find(Boolean))}
          onOpen={() => (cards.length ? setDialog({ kind: "role", role }) : setDialog({ kind: "pick", role }))} />;
      }
      // **One row per card**, as triConvey draws two purchasers buying
      // separately: each its own line, each opening the card it names.
      return cards.map(c => (
        <DetailRow key={`${section.key}-${c.id}`} label={section.label} indent={section.indent} value={c.name} chip={chip(vOf(c))}
          onOpen={() => setDialog({ kind: "edit", contact: c, back: { kind: "role", role } })} />
      ));
    }
    const summaryOf = (sep) => (section.summary || []).map(n => summaryPart(section, n)).filter(Boolean).join(sep);
    if (section.kind === "agent") {
      const summary = summaryOf(" · ");
      return <DetailRow key={section.key} label={section.label} value={summary} empty={!summary}
        onOpen={() => setDialog({ kind: "agent", section })} />;
    }
    // **Settlement financials opens its own dialog** — the statement of
    // adjustments — unless the middleware is from before it (404), when the
    // row is the plain fields it always was.
    if (section.key === "settlement" && !settlement.missing) {
      const st = settlement.state;
      const open = st ? (st.review?.counts?.block || 0) + (st.review?.counts?.check || 0) : 0;
      const value = !st ? "" : settlement.running ? "Drafting from the certificates…"
        : (st.financials.items.length || st.financials.payments.length)
          ? <>{`Due on settlement ${formatMoney(st.statement.totals.amountDue)}`}{st.financials.sent?.length ? <span style={{ color: T.fg3 }}> · sent</span> : null}{open ? <span style={{ color: T.warn }}> · {open} to check</span> : null}</>
          : "";
      return <DetailRow key={section.key} label={section.label} indent={section.indent} empty={!value} value={value}
        onOpen={() => setDialog({ kind: "settlement" })} />;
    }
    const parts = (section.summary || []).map(n => summaryPart(section, n)).filter(Boolean);
    // An "On the contract" row says where its names came from, and flags a
    // card naming different people (the Matter check's finding).
    const lead = (section.summary || [])[0];
    const from = parts.length && lead ? (record.contractSource || {})[lead] : "";
    const differs = lead && /\.legalNames$/.test(lead) ? (record.contractNames || {})[lead.split(".")[0]] : null;
    return <DetailRow key={section.key} label={section.label} indent={section.indent} empty={parts.length === 0}
      value={parts.length ? <>{parts[0]}{parts.slice(1).map((p, i) => <span key={i} style={{ color: T.fg3 }}> · {p}</span>)}
        {from && <span style={{ color: T.fg3 }}> · {from}</span>}</> : ""}
      chip={differs ? <span title={differs.title}><Pill tone="warn">Differs from the card</Pill></span> : null}
      onOpen={() => setDialog({ kind: "section", section })} />;
  };

  // In the order the middleware gives for this matter type (`layout.orders`) —
  // a purchase leads with the purchaser — else the sections' own order. A
  // section a heading names (`group`, from an older middleware) still gets one.
  const orderedSections = () => {
    const order = layout.orders?.[matterType];
    if (!order) return layout.sections;
    const at = (key) => { const i = order.indexOf(key); return i === -1 ? order.length : i; };
    return [...layout.sections].sort((a, b) => at(a.key) - at(b.key));
  };
  const groupedRows = () => {
    const out = [];
    let group = null;
    for (const section of orderedSections()) {
      const row = rowFor(section);
      if (!row || (Array.isArray(row) && row.length === 0)) continue;
      if (section.group && section.group !== group) {
        group = section.group;
        out.push(<h3 key={`g-${group}`} style={{ margin: 0, padding: "14px 16px 6px", fontSize: 12, fontWeight: 600, color: T.fg3 }}>{group}</h3>);
      }
      out.push(...[].concat(row));
    }
    return out;
  };

  // The gear: which less common rows this matter shows.
  const saveShown = async (sections) => {
    const data = await call(`/practice/matters/${encodeURIComponent(ref)}/shown`, { method: "POST", body: { sections } });
    setRecord(data.record);
  };

  // triConvey's tab order, so staff find each thing where they always have —
  // Matter, Intake, Emails, Memos, Events, Tasks, Messages, Activity, Time &
  // disbursements, Matter insights — except the contract, which is second:
  // it is the matter's main work, where triConvey put its searches tab. Its files
  // are a pane of the Matter tab, under Matter Details, and so are ours.
  const soonTab = (key, label, feature) => [key, label, null, !ready(feature)];
  const tabs = [
    ["matter", "Matter"],
    // **Second, after Matter**: the contract is the matter's main work.
    // The contract's status rides on its tab, seen from every tab.
    ["s32", contractBadge ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>{CONTRACT_TAB_LABEL}{contractBadge}</span> : CONTRACT_TAB_LABEL],
    soonTab("intake", "Intake", "intake"),
    emails ? ["emails", "Emails"] : soonTab("emails", "Emails", "emails"),
    ["memos", "Memos", lists.memos.length || null],
    ["events", "Events", ready("events") ? lists.diary.length || null : null, !ready("events")],
    ["tasks", "Tasks", ready("tasks") ? openTasks.length || null : null, !ready("tasks")],
    soonTab("messages", "Messages", "messages"),
    soonTab("activity", "Activity", "activity"),
    soonTab("time", "Time & disbursements", "time"),
    soonTab("insights", "Matter insights", "insights"),
  ];
  // **`onlyTabs`: the steps of moving across** (App.js, `lookStepFor`) show a
  // matter with only the tabs it names, and none of the buttons that lead to
  // the rest or to what is not built yet — hidden, not greyed, until the
  // firm's step reaches them.
  const shownTabs = onlyTabs ? tabs.filter(t => onlyTabs.includes(t[0])) : tabs;
  const go = (key) => setTab(key);

  // The Matter tab's ribbon, in triConvey's order. What is not built yet is
  // shown greyed, so the row reads as it does there.
  const [duties, setDuties] = useState("");
  const openDuties = async () => {
    setDuties("busy");
    const outcome = await onOpenDutiesForm().catch(() => "failed");
    if (!live.current) return;
    setDuties(outcome);
    setTimeout(() => { if (live.current) setDuties(""); }, 6000);
  };
  const emailHref = clientEmail ? `mailto:${clientEmail}?subject=${encodeURIComponent(`${ref} · ${matter.address || ""}`)}` : "";
  const ribbon = [
    { label: "New document", icon: ICON.file, feature: "newDocument" },
    { label: "Precedent", icon: ICON.memo, feature: "precedent" },
    { label: "Memo", icon: ICON.memo, tab: "memos", onClick: () => setDialog({ kind: "memo" }) },
    { label: "Message", icon: ICON.message, feature: "messages" },
    { label: "Email", icon: ICON.mail, feature: "email", href: emailHref, why: emailHref ? undefined : "No email for the client yet" },
    { label: "Import", icon: ICON.import, onClick: () => { setTab("matter"); setImportSignal(n => n + 1); }, why: postFile ? undefined : "Uploading is not available here" },
    { label: "Scan", icon: ICON.scan, feature: "scan" },
    // Ordering certificates from inside the matter, as triConvey's triSearch
    // tab's New Search did. Not ready, so greyed with the other "Soon"s.
    { label: "Order searches", icon: ICON.search, feature: "orderSearches" },
    // The SRO's duties form, filled by the extension (ProveyAustralia/s32-middleware issue 383).
    // The ribbon's word says how the last press went, for a few seconds.
    onOpenDutiesForm && { label: DUTIES_WORDS[duties] || "Duties form", icon: ICON.duties, onClick: openDuties, disabled: duties === "busy",
      title: dutiesTitle(dutiesStatus), muted: dutiesStatus?.state === "missing" || dutiesStatus?.state === "unsupported" },
    { label: "Event", icon: ICON.calendar, feature: "events", onClick: () => startNew("events") },
    { label: "Task", icon: ICON.check, feature: "tasks", onClick: () => startNew("tasks") },
    { label: "Phone message", icon: ICON.phone, feature: "phone" },
    { label: "Activity", icon: ICON.activity, feature: "activity" },
    { label: "Billing", icon: ICON.dollar, feature: "billing" },
    { label: "Time finder", icon: ICON.clock, feature: "time" },
  ];
  // The other tabs' ribbons, as triConvey's: each tab shows its own buttons
  // and nothing else. Print prints what the tab shows.
  const print = { label: "Print", icon: ICON.print, onClick: () => window.print() };
  const ribbons = {
    matter: ribbon,
    memos: [
      { label: "Letter", icon: ICON.file, feature: "newDocument" },
      { label: "Precedent", icon: ICON.memo, feature: "precedent" },
      { label: "Memo", icon: ICON.memo, onClick: () => startNew("memos") },
      { label: "Email", icon: ICON.mail, feature: "email", href: emailHref, why: emailHref ? undefined : "No email for the client yet" },
      print,
    ],
    events: [
      { label: "Event", icon: ICON.calendar, onClick: () => startNew("events") },
      { label: "Task", icon: ICON.check, feature: "tasks", onClick: () => startNew("tasks") },
      print,
    ],
    tasks: [
      { label: "Task", icon: ICON.check, onClick: () => startNew("tasks") },
      { label: "Workflow", icon: ICON.activity, feature: "workflows" },
      { label: "Event", icon: ICON.calendar, feature: "events", onClick: () => startNew("events") },
      print,
    ],
  };
  const tabLabel = (tabs.find(t => t[0] === tab) || [])[1] || "Matter";
  // A tab remembered from a later step, or one this step hides, opens the
  // default instead.
  useEffect(() => {
    if (onlyTabs && !onlyTabs.includes(tab)) setTabState(onlyTabs.includes(defaultTab) ? defaultTab : onlyTabs[0]);
  }, [onlyTabs, tab, defaultTab]);
  const showRibbon = Boolean(ribbons[tab]) || tab === "s32";
  const saveDetailsHeight = (el) => {
    if (!el) return;
    const h = Math.round(el.getBoundingClientRect().height);
    if (Math.abs(h - detailsHeight) < 4) return;
    setDetailsHeight(h);
    try { window.localStorage.setItem(DETAILS_HEIGHT_KEY, String(h)); } catch { /* a private window keeps the default */ }
  };

  // Where a finding's Edit goes: the section that shows its field, else the
  // Section 32 tab (where every contract field is), else the role's cards.
  const sectionWith = (name) => (layout?.sections || []).find(sec => (sec.tabs || []).some(t => (t.groups || []).some(g => (g.fields || []).includes(name))));
  const editFor = (f) => {
    if (f.field) {
      const section = sectionWith(f.field);
      return section ? () => setDialog({ kind: "section", section }) : () => go("s32");
    }
    if (f.role) {
      const role = roles.get(f.role) || { key: f.role, label: f.role, multiple: true };
      return () => setDialog((parties[f.role] || []).length ? { kind: "role", role } : { kind: "pick", role });
    }
    return null;
  };
  const pickValue = async (field, value) => {
    const ok = await onSaveContractFields({ [field]: value });
    if (!ok) throw new Error("The value was not saved");
    changed();
  };
  const openCount = check ? (check.findings || []).filter(f => !f.acknowledged).length : 0;

  return (
    <div style={{ fontFamily: FONT, color: T.fg1 }}>
      <Card label="Matter" style={{ padding: "16px 20px 0", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: "1 1 360px" }}>
            <h1 style={{ margin: 0, fontSize: 22, lineHeight: "30px", fontWeight: 600, letterSpacing: "-0.01em", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              {title}
              {(record?.practiceStatus || matter.practiceStatus) && <Pill>{record?.practiceStatus || matter.practiceStatus}</Pill>}
            </h1>
            <div style={{ color: T.fg3, marginTop: 2, fontSize: 14 }}>
              {meta.map((m, i) => <span key={i}>{i > 0 && <span aria-hidden="true" style={{ color: T.strokeInput, margin: "0 8px" }}>|</span>}{m}</span>)}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {headerLinks}
            {onPopOut && <Button subtle onClick={() => onPopOut(ref)}><Icon d={ICON.popout} size={15} />Pop out</Button>}
            {onStatusChanged && <Button subtle label="Matter settings" title="Matter settings" onClick={() => setDialog({ kind: "settings" })}><Icon d={ICON.gear} size={16} /></Button>}
          </div>
        </div>
        <div style={{ marginTop: 10 }}><Tabs tabs={shownTabs} active={tab} onChange={go} /></div>
        {showRibbon && (
          <div style={{ borderTop: `1px solid ${T.strokeSubtle}`, marginTop: 6 }}>
          {tab === "s32"
            ? <div role="toolbar" aria-label={`${CONTRACT_TAB_LABEL} actions`} ref={setS32Slot} style={{ display: "flex", gap: 2, flexWrap: "wrap", padding: "8px 0 10px", minHeight: 52 }} />
            : (
              <div role="toolbar" aria-label={`${tabLabel} actions`} style={{ display: "flex", gap: 2, flexWrap: "wrap", padding: tab === "matter" ? "8px 0 2px" : "8px 0 10px" }}>
                <RibbonItems items={ribbons[tab].filter(Boolean).map(b => ({ ...b, soon: b.feature && !ready(b.feature) }))
                  .filter(b => !onlyTabs || (!b.soon && (!b.tab || onlyTabs.includes(b.tab))))} />
              </div>
            )}
          {tab === "matter" && stages.length > 0 && <StageStrip stages={stages} active={record?.stage} onChange={setStage} busy={movingStage} />}
          </div>
        )}
        {!showRibbon && <div style={{ height: 12 }} />}
      </Card>

      {(error || layoutError) && tab !== "s32" && (
        <p role="alert" style={{ color: T.danger, fontSize: 13, background: T.card, borderRadius: 8, boxShadow: T.shadowCard, padding: 12 }}>
          {error || layoutError}
        </p>
      )}

      {tab === "matter" && (settlement.running || (watchingDraft && settlement.state?.job && settlement.state.job.status !== "running")) && (
        <div style={{ marginBottom: 12 }}>
          <JobBanner job={settlement.state?.job} onOpen={() => { setWatchingDraft(false); setDialog({ kind: "settlement" }); }} />
        </div>
      )}
      {tab === "matter" && layout && record && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
          <div style={{ flex: "999 1 520px", minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
            <Card label="Matter details" style={{ overflow: "hidden" }}>
              <CardHead title="Matter details" action={layout.sections.some(s => s.optional) && (
                <button type="button" onClick={() => setDialog({ kind: "shown" })} aria-label="Show and hide matter details" title="Show and hide matter details"
                  style={{ background: "none", border: "none", cursor: "pointer", color: T.fg2, padding: 4, display: "inline-flex", borderRadius: R }}>
                  <Icon d={ICON.gear} size={16} />
                </button>
              )} />
              {/* Its own height and its own scroll, dragged from the corner, so
                  the files stay in view beneath it (as in triConvey). */}
              <div data-testid="matter-details-pane" onMouseUp={e => saveDetailsHeight(e.currentTarget)} onTouchEnd={e => saveDetailsHeight(e.currentTarget)}
                style={{ height: detailsHeight, minHeight: 160, maxHeight: "85vh", overflowY: "auto", resize: "vertical", paddingBottom: 4, borderTop: `1px solid ${T.strokeSubtle}` }}>
                {(record.suggestedParties || []).map(o => <SuggestedParty key={`${o.role}:${o.contactId || ""}`} offer={o}
                  onAdd={() => addSuggested(o)} onReview={() => setDialog({ kind: "review", offer: o })} />)}
                {groupedRows()}
              </div>
            </Card>
            <DocumentsTab call={call} matterRef={ref} searches={searches} documentHref={documentHref}
              postFile={postFile} fileHref={fileHref} onManage={() => go("s32")} importSignal={importSignal}
              onDraftAdjustments={settlement.missing ? undefined : (docs) => setDialog({ kind: "draftSoa", docs })} />
          </div>
          {/* triConvey's right pane: Archie · Widgets · Timeline. Ours puts the
              Matter check where Archie is: the assistant with the record behind it. */}
          <aside aria-label="Matter summary" style={{ flex: "1 1 320px", minWidth: 0, position: "sticky", top: 16 }}>
            <Card label="Matter side pane" style={{ overflow: "hidden" }}>
              <div style={{ padding: "10px 14px 0" }}>
                <Tabs size={13} active={side} onChange={setSide} tabs={[
                  ["check", "Check", openCount || null],
                  ["widgets", "Widgets"],
                  ["timeline", "Timeline", null, !ready("timeline")],
                ]} />
              </div>
              {side === "check" && (check?.unavailable
                ? <div style={{ padding: 14 }}><Empty>The Matter check needs the latest middleware.</Empty></div>
                : <MatterCheckPanel call={call} matterRef={ref} check={check} onReload={loadCheck}
                    onUse={onSaveContractFields ? pickValue : null} editFor={editFor} />)}
              {side === "widgets" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 12, background: T.bg }}>
            <Card label="Client">
              <CardHead title="Client" action={vendors[0] && <LinkButton onClick={() => setDialog({ kind: "edit", contact: vendors[0] })}>View</LinkButton>} />
              <div style={{ padding: "4px 16px 14px" }}>
                {vendors.length === 0 ? (
                  <><Empty>No client linked yet.</Empty>
                    <div style={{ marginTop: 6 }}><LinkButton onClick={() => setDialog({ kind: "pick", role: roles.get("vendor") || { key: "vendor", label: "Vendor", multiple: true } })}>Add the vendor</LinkButton></div></>
                ) : vendors.map(c => (
                  <div key={c.id} style={{ marginBottom: 6 }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <Initials name={c.name} />
                      <div><div style={{ fontWeight: 600 }}>{c.name}</div><div style={{ fontSize: 12, color: T.fg3 }}>Vendor</div></div>
                    </div>
                    <ContactLines contact={c} />
                    {c.voiStatus && c.voiStatus !== "verified" && c.voiStatus !== "not_required" && (
                      <div style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}><span style={{ color: T.fg3, width: 64 }}>VOI</span><Pill tone="warn">{VOI_LABEL[c.voiStatus] || c.voiStatus}</Pill></div>
                    )}
                  </div>
                ))}
              </div>
            </Card>

            <AddressBookCard book={record.addressBook || []} />

            <Card label="Key dates">
              <CardHead title="Key dates" />
              <div style={{ padding: "2px 16px 12px" }}>
                {(record.criticalDates || []).length === 0 ? <Empty>No key dates yet.</Empty> : record.criticalDates.map(d => (
                  <div key={d.name} style={{ display: "grid", gridTemplateColumns: "84px 1fr auto", gap: 8, alignItems: "center", padding: "5px 0", fontSize: 13 }}>
                    <span style={{ fontWeight: 600 }}>{formatDay(d.date)}</span>
                    <span>{d.label}</span>
                    {typeof d.daysAway === "number" && (d.daysAway >= 0 && d.daysAway <= 3
                      ? <Pill tone="danger">{d.daysAway === 0 ? "Today" : `${d.daysAway} day${d.daysAway === 1 ? "" : "s"}`}</Pill>
                      : <span style={{ fontSize: 12, color: T.fg4 }}>{d.daysAway < 0 ? `${-d.daysAway} days ago` : `${d.daysAway} days`}</span>)}
                  </div>
                ))}
              </div>
            </Card>

            <Card label="Tasks">
              <CardHead title="Tasks" count={ready("tasks") ? openTasks.length || null : null} action={ready("tasks") ? <LinkButton onClick={() => go("tasks")}>Add</LinkButton> : <span title={SOON} style={{ fontSize: 12, color: T.fg4 }}>Soon</span>} />
              <div style={{ padding: "2px 16px 12px", fontSize: 13 }}>
                {!ready("tasks") ? <Empty>Coming soon.</Empty> : openTasks.length === 0 ? <Empty>Nothing open.</Empty> : openTasks.slice(0, SIDE_LIMIT).map(t => (
                  <label key={t.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 0", cursor: "pointer" }}>
                    <input type="checkbox" checked={false} onChange={() => toggleTask(t)} style={{ accentColor: T.brand, margin: 0 }} />
                    <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.subject}</span>
                    <span style={{ fontSize: 12, color: dueSoon(t.dueDate, record) ? T.danger : T.fg4 }}>{t.dueDate ? formatDay(t.dueDate) : "No date"}</span>
                  </label>
                ))}
                {openTasks.length > SIDE_LIMIT && <div style={{ marginTop: 4 }}><LinkButton onClick={() => go("tasks")}>View all {openTasks.length} tasks</LinkButton></div>}
              </div>
            </Card>

            <Card label="Notes">
              <CardHead title="Notes" count={lists.memos.length || null} action={<LinkButton onClick={() => setDialog({ kind: "memo" })}>Add</LinkButton>} />
              <div style={{ padding: "2px 16px 14px" }}>
                {lists.memos.length === 0 ? <Empty>No notes yet.</Empty> : lists.memos.slice(0, NOTES_LIMIT).map(m => (
                  <div key={m.id} style={{ background: T.noteBg, border: `1px solid ${T.noteStroke}`, borderRadius: R, padding: "8px 10px", marginBottom: 8, fontSize: 13 }}>
                    {m.title && <div style={{ fontWeight: 600 }}>{m.title}</div>}
                    {m.body && <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", color: T.fg2, display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{m.body}</div>}
                    <div style={{ fontSize: 11.5, color: T.fg4, marginTop: 4, display: "flex", gap: 6, alignItems: "center" }}>
                      {m.pinned && <span title="Pinned" style={{ display: "inline-flex" }}><Icon d={ICON.pin} size={11} /></span>}{formatDay(m.createdAt)}
                    </div>
                  </div>
                ))}
                {lists.memos.length > NOTES_LIMIT && <LinkButton onClick={() => go("memos")}>View all {lists.memos.length} notes</LinkButton>}
              </div>
            </Card>
                </div>
              )}
            </Card>
          </aside>
        </div>
      )}
      {tab === "matter" && !error && !layoutError && (!layout || !record) && <p style={{ color: T.fg3, fontSize: 13 }}>Loading the matter…</p>}
      {tab === "tasks" && ready("tasks") && <TasksTab call={call} matterRef={ref} newSignal={newSignal.tasks} onChanged={() => { loadRecord(); changed(); }} />}
      {tab === "events" && ready("events") && <EventsTab call={call} matterRef={ref} newSignal={newSignal.events} onChanged={() => { loadRecord(); changed(); }} />}
      {tab === "emails" && emails}
      {tab === "memos" && <MemosTab call={call} matterRef={ref} newSignal={newSignal.memos} onChanged={() => { loadRecord(); changed(); }} />}
      <RibbonSlot.Provider value={tab === "s32" ? s32Slot : null}>
        <div style={{ display: tab === "s32" ? "block" : "none" }}>{children}</div>
      </RibbonSlot.Provider>

      {dialog?.kind === "settlement" && (
        <SettlementDialog call={call} matterRef={ref} postFile={postFile} fileHref={fileHref} settlement={settlement} searches={searches}
          onEditConveyancing={layout && layout.sections.find(s => s.key === "conveyancing") ? () => setDialog({ kind: "section", section: layout.sections.find(s => s.key === "conveyancing") }) : null}
          onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === "draftSoa" && (
        <DraftAdjustmentsDialog call={call} matterRef={ref} postFile={postFile} searches={searches} preselected={dialog.docs || []}
          onClose={() => setDialog(null)}
          onStarted={() => { setDialog(null); setWatchingDraft(true); settlement.load(); }} />
      )}
      {dialog?.kind === "section" && (
        <SectionDialog section={dialog.section} defOf={defOf} valueOf={valueOf} recordNames={recordNames}
          onSave={saveSection} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === "agent" && (
        <Dialog title={dialog.section.label} onClose={() => setDialog(null)} width={560}
          footer={<>
            <Button onClick={() => setDialog(null)}>Close</Button>
            <Button primary onClick={() => { setDialog(null); setTab("s32"); onOpenSection32Agent && onOpenSection32Agent(); }}>Choose from the agent directory</Button>
          </>}>
          <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 16px", margin: 0, fontSize: 14 }}>
            {dialog.section.fields.map(n => (
              <div key={n} style={{ display: "contents" }}>
                <dt style={{ color: T.fg3 }}>{defOf(n).label}</dt>
                <dd style={{ margin: 0 }}>{displayValue(defOf(n), valueOf(n)) || "—"}</dd>
              </div>
            ))}
          </dl>
          <p style={{ fontSize: 13, color: T.fg3, marginBottom: 0 }}>
            Agents and agencies come from the shared agent directory, chosen on the {CONTRACT_TAB_LABEL} tab.
          </p>
        </Dialog>
      )}
      {/* **Archiving is a setting of the matter, not a link under a list.**
          Delete stays behind archiving and the typed reference, as it was. */}
      {/* **The matter's status, where archiving was**: form 265's four words,
          set here or in form 265, whichever changed it last. Anything but Open
          takes the matter off the matters list. No permanent delete. */}
      {dialog?.kind === "settings" && (
        <StatusDialog statuses={layout?.practiceStatuses || PRACTICE_STATUSES}
          status={record?.practiceStatus || matter.practiceStatus || (matter.archived ? "Deleted" : "Open")}
          onSave={async (status) => {
            const data = await call(`/practice/matters/${encodeURIComponent(ref)}/status`, { method: "POST", body: { status } });
            setRecord(data.record);
            onStatusChanged && onStatusChanged(status);
          }}
          onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === "shown" && layout && record && (
        <ShownDialog sections={layout.sections.filter(s => s.optional)} shown={record.shownSections || []}
          linked={(key) => (parties[key] || []).length > 0}
          onSave={async (keys) => { try { await saveShown(keys); setDialog(null); } catch (e) { setError(e.message); setDialog(null); } }}
          onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === "type" && layout && (
        <Dialog title="Matter type" onClose={() => setDialog(null)} width={420}
          footer={<Button onClick={() => setDialog(null)}>Close</Button>}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {layout.matterTypes.map(t => (
              <label key={t} style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 14 }}>
                <input type="radio" name="matter-type" checked={matterType === t} style={{ accentColor: T.brand }}
                  onChange={async () => { try { await setType(t); setDialog(null); } catch (e) { setError(e.message); } }} />{t}
              </label>
            ))}
          </div>
        </Dialog>
      )}
      {dialog?.kind === "role" && (
        <RoleDialog role={dialog.role} cards={parties[dialog.role.key] || []} note={roleNote(dialog.role.key)}
          onClose={() => setDialog(null)}
          onAdd={() => setDialog({ kind: "pick", role: dialog.role })}
          onEdit={(c) => setDialog({ kind: "edit", contact: c, back: dialog })}
          onRemove={async (c) => {
            try { await linkRole(dialog.role.key, (parties[dialog.role.key] || []).map(x => x.id).filter(id => id !== c.id)); } catch (e) { setError(e.message); }
          }} />
      )}
      {dialog?.kind === "pick" && layout && (
        <ContactPicker call={call} layout={layout} roleLabel={dialog.role.label} onClose={() => setDialog(null)}
          onPicked={async (picked) => {
            const current = (parties[dialog.role.key] || []).map(c => c.id);
            const ids = dialog.role.multiple ? [...new Set([...current, picked.id])] : [picked.id];
            try { await linkRole(dialog.role.key, ids); setDialog(null); } catch (e) { setError(e.message); setDialog(null); }
          }} />
      )}
      {dialog?.kind === "edit" && layout && (
        <ContactForm call={call} layout={layout} contact={dialog.contact}
          intro={dialog.back?.role && roleNote(dialog.back.role.key)
            ? <p style={{ fontSize: 13, color: T.fg3, margin: "0 0 10px" }}>{roleNote(dialog.back.role.key)}</p> : null}
          onClose={() => setDialog(dialog.back || null)}
          onSaved={async () => { setDialog(dialog.back || null); await loadRecord(); changed(); }} />
      )}
      {dialog?.kind === "review" && layout && (
        <ReviewSuggested call={call} layout={layout} offer={dialog.offer} onClose={() => setDialog(null)}
          onAdd={async (card) => { await addSuggested(dialog.offer, { card }, { rethrow: true }); setDialog(null); }}
          onLink={async (linkId) => {
            try { await addSuggested(dialog.offer, { linkId }, { rethrow: true }); setDialog(null); } catch (e) { setError(e.message); setDialog(null); }
          }} />
      )}
      {dialog?.kind === "memo" && (
        <MemoEditor memo={{ pinned: true }} onClose={() => setDialog(null)}
          onSave={async (draft) => { await call(`/practice/matters/${encodeURIComponent(ref)}/memos`, { method: "POST", body: draft }); await loadRecord(); changed(); }} />
      )}
    </div>
  );
};

// How many rows a side card shows before "View all".
const SIDE_LIMIT = 4;
// Matter Details is a pane of its own height, as in triConvey, so the files
// sit beneath it in view; staff drag it taller or shorter and it stays so.
const DETAILS_HEIGHT = 340;
const DETAILS_HEIGHT_KEY = "practice-details-height";
const NOTES_LIMIT = 3;

// Due within three days of the firm's today, where the record carries one.
const dueSoon = (iso, record) => {
  const today = (record?.criticalDates || []).find(d => typeof d.daysAway === "number");
  if (!iso || !today) return false;
  const todayMs = Date.parse(today.date) - today.daysAway * 86400000;
  const days = Math.round((Date.parse(iso) - todayMs) / 86400000);
  return days <= 3;
};

// ── The firm's brand colour, on the Settings screen ─────────────────────────
//
// One colour, Outlook blue unless the firm sets its own. The middleware refuses
// a colour white text cannot be read on (it fills the title bar and the
// primary button) and works out the shades; this only picks and previews.
// Two logos. A dark one for white grounds (the header, the signing page, the
// section 137B form) and a white one for the coloured bar. One file cannot
// do both, and a plate behind a logo reads as a sticker. Sent as a data URL;
// the middleware checks the bytes.
const MAX_LOGO_KB = 300;
const LOGO_FIELDS = {
  dark: {
    path: "/practice/appearance/logo",
    title: "Dark logo",
    hint: "For white backgrounds: the header, the signing page, the section 137B form, and the contract cover when you use a standard template.",
    choose: "Choose a dark logo",
    alt: "Dark logo",
    remove: "Remove dark logo",
    saved: "Dark logo saved",
    removed: "Dark logo removed",
    plate: T.card,
  },
  white: {
    path: "/practice/appearance/logo/white",
    title: "White logo",
    hint: "For the coloured bar.",
    choose: "Choose a white logo",
    alt: "White logo",
    remove: "Remove white logo",
    saved: "White logo saved",
    removed: "White logo removed",
    plate: T.fg1,
  },
};
export const LogoSetting = ({ call, onSaved, onError, variant = "dark" }) => {
  const field = LOGO_FIELDS[variant] || LOGO_FIELDS.dark;
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    call("/practice/appearance/logo").then(d => { if (alive) setLogo((variant === "white" ? d.white : d.logo) || null); }).catch(() => {});
    return () => { alive = false; };
  }, [call, variant]);
  const save = async (body, method = "POST") => {
    setBusy(true); setError("");
    try {
      const d = await call(field.path, { method, ...(body ? { body } : {}) });
      const next = (variant === "white" ? d.white : d.logo) || null;
      setLogo(next);
      onSaved && onSaved(next ? field.saved : field.removed);
    } catch (e) {
      setError(e.message);
      onError && onError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const choose = (file) => {
    if (!file) return;
    if (file.size > MAX_LOGO_KB * 1024) { setError(`The logo must be under ${MAX_LOGO_KB}KB`); return; }
    const reader = new FileReader();
    reader.onload = () => save({ dataUrl: reader.result });
    reader.readAsDataURL(file);
  };
  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${T.stroke}` }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: T.fg1, marginBottom: 3 }}>{field.title}</div>
      <p style={{ fontSize: 12, color: T.fg4, margin: "0 0 8px" }}>
        {field.hint} PNG, JPEG or WebP, under {MAX_LOGO_KB}KB.
      </p>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        {logo && <img src={logo} alt={field.alt} style={{ maxHeight: 40, maxWidth: 200, border: `1px solid ${T.stroke}`, borderRadius: R, padding: 4, background: field.plate }} />}
        <input type="file" accept="image/png,image/jpeg,image/webp" aria-label={field.choose} disabled={busy}
          onChange={e => { choose(e.target.files?.[0]); e.target.value = ""; }} style={{ fontSize: 12 }} />
        {logo && <LinkButton disabled={busy} onClick={() => save(null, "DELETE")}>{field.remove}</LinkButton>}
      </div>
      {error && <p role="alert" style={{ fontSize: 12, color: T.danger, margin: "6px 0 0" }}>{error}</p>}
    </div>
  );
};

// ── Which contract template the firm prints ────────────────────────────────
//
// Three choices: the standard REIV/LIV form (the matter's own special
// conditions only, from 1); the same form with the platform's special
// conditions; or the firm's own template. The first two need the firm's own
// REIV/LIV licence number. The firm's own is not uploaded as markup: the firm
// sends its precedent, which is emailed to the super admins to code, and the
// firm keeps printing what it prints now until the coded template is uploaded.
// Which template prints, and the cover's logo and colours, are the
// middleware's (`settings/contract_template`); this only asks.
const TEMPLATE_PROBLEMS = {
  no_template: "Nothing to print yet — choose a standard template, or send your own. Until then a compile merges the searches only.",
  shared_missing: "The standard template is not available just now, so contracts cannot be generated. Contact support.",
};
const TEMPLATE_OPTIONS = [
  { value: "standard", label: "Standard REIV/LIV template",
    hint: "The form as REIV/LIV publish it. Its special conditions are only the ones you add on each matter, numbered from 1." },
  { value: "platform", label: "Standard REIV/LIV template with the platform's special conditions",
    hint: "The same form with our special conditions already in it (auction, entire agreement, finance and the rest). The ones you add on a matter follow on from them." },
  { value: "own", label: "Our own template",
    hint: "Send us your contract precedent and we will set it up for you." },
];
const MAX_PRECEDENT_MB = 15;

export const ContractTemplateSettings = ({ api, onSaved, onError, onChange, bare = false }) => {
  const call = useCall(api);
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [missing, setMissing] = useState(false);
  const [note, setNote] = useState("");
  const [sent, setSent] = useState(false);

  // Through a ref, so the read on mount does not depend on the caller's
  // callback (a new function each render) and run again.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const adopt = (t) => {
    setState(t);
    onChangeRef.current && onChangeRef.current(t);
    // The middleware answers `standard` for a firm that has chosen nothing,
    // so the standard template arrives pre-selected.
    setDraft({ source: t.source, edition: t.edition, licenceNumber: t.licenceNumber || "", coverColours: t.coverColours });
  };
  useEffect(() => {
    let alive = true;
    call("/settings/contract-template").then(d => { if (alive) adopt(d.contractTemplate); })
      .catch(e => { if (!alive) return; if (e.status === 404) setMissing(true); else setError(e.message); });
    return () => { alive = false; };
  }, [call]);

  // A middleware without the route: say nothing rather than a broken card.
  if (missing) return null;

  const save = async () => {
    setBusy(true); setError("");
    try {
      const d = await call("/settings/contract-template", { method: "POST", body: draft });
      adopt(d.contractTemplate);
      onSaved && onSaved("Contract template saved");
    } catch (e) {
      setError(e.message);
      onError && onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // The precedent goes as the request body, its name and the note in the
  // query, so a Word file is not inflated as base64 (the middleware's
  // `POST /settings/contract-template/request`).
  const send = async (file) => {
    if (!file) return;
    if (file.size > MAX_PRECEDENT_MB * 1024 * 1024) { setError(`The file must be under ${MAX_PRECEDENT_MB}MB`); return; }
    setBusy(true); setError("");
    try {
      const qs = new URLSearchParams({ name: file.name, ...(note.trim() ? { note: note.trim() } : {}) });
      const res = await api(`/settings/contract-template/request?${qs}`, {
        method: "POST", headers: { "Content-Type": "application/octet-stream" }, body: file,
      });
      let data = {};
      try { data = await res.json(); } catch { data = {}; }
      if (!res.ok || data.ok === false) throw new Error(data.error || `The server answered ${res.status}`);
      adopt(data.contractTemplate);
      // The firm's saved choice has not changed, but the confirmation belongs
      // under "Our own template", so that stays the one open.
      setDraft(d => ({ ...d, source: "own" }));
      setSent(true);
      setNote("");
      onSaved && onSaved("Template sent");
    } catch (e) {
      setError(e.message);
      onError && onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const set = (patch) => setDraft(d => ({ ...d, ...patch }));
  const choosingOwn = draft && draft.source === "own";
  const own = state && state.ownTemplate;
  const request = state && state.ownRequest;
  const waiting = request && request.status === "requested";
  // Saving `own` only switches back to a template already coded; sending one
  // is the upload below, which needs no Save.
  // During setup the pre-selected template is itself the answer, so it saves
  // untouched.
  const dirty = state && draft && (state.needsSetup || ["source", "edition", "licenceNumber", "coverColours"].some(k => (draft[k] || "") !== (state[k] || "")));
  const canSave = !(choosingOwn && !own);
  const needsLicence = draft && ["standard", "platform"].includes(draft.source) && !draft.licenceNumber.trim();
  const describeTemplate = (t) => t && `${t.name || "Contract template"}${t.updatedAt ? `, updated ${formatDay(t.updatedAt.slice(0, 10))}` : ""}`;
  const current = state && TEMPLATE_OPTIONS.find(o => o.value === state.effectiveSource);

  return (
    <div style={bare ? { fontFamily: FONT } : { background: T.card, border: `1px solid ${T.stroke}`, borderRadius: 10, padding: 16, maxWidth: 620, marginBottom: 20, fontFamily: FONT }}>
      {!bare && <div style={{ fontSize: 13, fontWeight: 500, color: T.fg1, marginBottom: 3 }}>Contract template</div>}
      <p style={{ fontSize: 12, color: T.fg4, margin: "0 0 12px" }}>
        The contract of sale and section 32 every compile generates.
      </p>
      {!state && !error && <div style={{ fontSize: 12, color: T.fg4 }}>Loading…</div>}
      {state && draft && (
        <>
          {state.isProvider ? (
            <p style={{ fontSize: 12, color: T.fg3, margin: "0 0 12px" }}>
              {own ? `Your firm prints its own template (${describeTemplate(own)}). It is the platform template other firms use under their own licence.`
                : "Your firm has no template uploaded."}
            </p>
          ) : (
            <div role="radiogroup" aria-label="Contract template" style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 12 }}>
              {TEMPLATE_OPTIONS.map(o => (
                <label key={o.value} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13 }}>
                  <input type="radio" name="contract-template-source" aria-label={o.label} checked={draft.source === o.value} disabled={busy}
                    onChange={() => set({ source: o.value })} style={{ accentColor: T.brand, marginTop: 2 }} />
                  <span><span style={{ fontWeight: 500, color: T.fg1 }}>{o.label}</span>
                    <span style={{ display: "block", color: T.fg3, fontSize: 12 }}>{o.hint}</span></span>
                </label>
              ))}
            </div>
          )}

          {!state.isProvider && draft.source && !choosingOwn && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
              <label style={labelStyle}>Edition
                <select aria-label="Edition" value={draft.edition} disabled={busy} onChange={e => set({ edition: e.target.value })}
                  style={{ ...inputStyle, marginTop: 4 }}>
                  {state.editions.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
                </select>
              </label>
              <p style={{ fontSize: 12, color: T.fg4, margin: 0 }}>
                Your logo replaces ours on the cover, and your firm's name and licence number print in the footer of every page of the contract.
              </p>
            </div>
          )}

          {!state.isProvider && choosingOwn && (
            <div style={{ marginBottom: 12, padding: 12, background: T.bg, border: `1px solid ${T.stroke}`, borderRadius: R }}>
              {own && <p style={{ fontSize: 12, color: T.fg3, margin: "0 0 8px" }}>{`Your template: ${describeTemplate(own)}.`}</p>}
              {(sent || waiting) && (
                <p role="status" style={{ fontSize: 13, color: T.fg1, margin: "0 0 10px", lineHeight: 1.5 }}>
                  {`Thanks — we have your template${request ? ` (${request.fileName})` : ""}. The coding and uploading of your template will be completed as soon as possible.`}
                  {current && current.value !== "own" ? ` Until then your contracts keep using the ${current.label.toLowerCase()}.`
                    : ""}
                </p>
              )}
              <p style={{ fontSize: 12, color: T.fg4, margin: "0 0 8px" }}>
                {waiting ? "Send a different version if it has changed." : "Your contract precedent as a Word document (.docx or .doc) or a PDF, under 15MB. It is emailed to us to set up."}
              </p>
              <label style={{ ...labelStyle, display: "block", marginBottom: 8 }}>Anything we should know (optional)
                <textarea aria-label="Anything we should know" value={note} disabled={busy} onChange={e => setNote(e.target.value)} rows={2}
                  style={{ ...inputStyle, height: "auto", padding: "6px 8px", marginTop: 4, resize: "vertical" }} />
              </label>
              <input type="file" accept=".docx,.doc,.pdf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                aria-label="Upload your contract template" disabled={busy}
                onChange={e => { send(e.target.files?.[0]); e.target.value = ""; }} style={{ fontSize: 12 }} />
            </div>
          )}

          <label style={{ ...labelStyle, display: "block", marginBottom: 12 }}>
            REIV/LIV licence number{!state.isProvider && !choosingOwn && <span style={{ color: T.danger, marginLeft: 3 }}>*</span>}
            <input aria-label="REIV/LIV licence number" value={draft.licenceNumber} disabled={busy}
              onChange={e => set({ licenceNumber: e.target.value.toUpperCase() })} style={{ ...inputStyle, marginTop: 4, maxWidth: 260, display: "block" }} />
          </label>
          {needsLicence && (
            <p role="status" style={{ fontSize: 12, color: T.warn, margin: "-6px 0 12px" }}>
              Enter your REIV/LIV licence number. Contracts are generated on this template meanwhile, but your licence number is not printed on them until it is entered.
            </p>
          )}

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: T.fg1, marginBottom: 12 }}>
            <input type="checkbox" aria-label="Colour the cover with your brand colour" checked={draft.coverColours === "brand"} disabled={busy}
              onChange={e => set({ coverColours: e.target.checked ? "brand" : "standard" })} style={{ accentColor: T.brand }} />
            <span>Colour the cover with your brand colour
              <span style={{ display: "block", color: T.fg3, fontSize: 12 }}>The band, the labels and the page tint on the firm's cover follow the brand colour above.</span></span>
          </label>

          {canSave && (
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <Button primary disabled={busy || !dirty || !draft.source} onClick={save}>{busy ? "Saving…" : "Save"}</Button>
            </div>
          )}
          {state.problem && TEMPLATE_PROBLEMS[state.problem] && (
            <p role="status" style={{ fontSize: 12, color: T.danger, margin: "10px 0 0" }}>{TEMPLATE_PROBLEMS[state.problem]}</p>
          )}
        </>
      )}
      {error && <p role="alert" style={{ fontSize: 12, color: T.danger, margin: "8px 0 0" }}>{error}</p>}
    </div>
  );
};

// ── Setting a new firm up ──────────────────────────────────────────────────
//
// A step of signing up, after the two-step sign-in: the owner or admin of a
// firm that has never saved a contract template choice (`needsSetup`, the
// middleware's) is asked for it, with the standard template pre-selected.
// *Skip for now* leaves the firm on the standard template, which prints; the
// same card stays in Settings to change later. A firm that is set up, or the
// one that supplies the template, goes straight through (`onDone`).
export const ContractTemplateSetup = ({ api, onDone, onSaved }) => {
  const call = useCall(api);
  const [state, setState] = useState(null);
  const finished = useRef(false);
  const done = useCallback(() => {
    if (finished.current) return;
    finished.current = true;
    onDone && onDone();
  }, [onDone]);
  useEffect(() => {
    let alive = true;
    call("/settings/contract-template")
      .then(d => {
        if (!alive) return;
        const t = d && d.contractTemplate;
        if (!t || t.isProvider || !t.needsSetup) done();
        else setState(t);
      })
      // A middleware without the route, or a refusal, is not a reason to keep
      // somebody out of the portal.
      .catch(() => { if (alive) done(); });
    return () => { alive = false; };
  }, [call, done]);
  if (!state) return null;
  return (
    <div style={{ fontFamily: FONT }}>
      <h1 style={{ fontSize: 22, fontWeight: 300, letterSpacing: "-0.02em", color: T.fg1, margin: "0 0 8px" }}>Choose your contract template</h1>
      <p style={{ fontSize: 13, color: T.fg2, margin: "0 0 16px", lineHeight: 1.5 }}>
        The contract of sale and section 32 your matters will use. The standard REIV/LIV template is selected —
        enter your REIV/LIV licence number and save, or choose another. You can change this later in Settings.
      </p>
      <ContractTemplateSettings api={api} bare onSaved={onSaved} onChange={setState} />
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
        <Button primary={!state.needsSetup} onClick={done}>{state.needsSetup ? "Skip for now" : "Continue"}</Button>
      </div>
    </div>
  );
};

// ── Moving across in steps ──────────────────────────────────────────────────
//
// What each step of the firm's look brings, in the words the card and
// Settings use. The rules of which step a tab draws are App.js's
// (`lookStepFor`); these are only its words.
export const LOOK_STEPS = [
  { key: "classic", label: "Classic", news: "" },
  { key: "step-1", label: "Step 1 — open matters stay open",
    news: "Matters you open now stay open as tabs across the top, and the search box finds any matter or person. Nothing else has moved." },
  { key: "step-2", label: "Step 2 — the matter's tabs",
    news: "A matter now opens in its own tabs — Matter, Contract & S32 and Emails — on the Contract & S32 page you know. Its buttons are in the ribbon above it." },
  { key: "step-3", label: "Step 3 — the new colours",
    news: "The new colours, and the menu down the left. Everything else is where it was." },
  { key: "practice", label: "Practice — Matter details first",
    news: "A matter now opens on Matter details: its stages, parties, documents and the Matter check, with Memos and the other tabs beside it. Contract & S32 is one tab along." },
];

// The card the first time somebody sees a step, and the line that says they
// have kept an earlier one. `onKeepPrevious` / `onFollowFirm` change this
// browser's own choice; the firm's step is Settings'.
export const LookNews = ({ step, firmStep, seen, onSeen, onKeepPrevious, onFollowFirm }) => {
  if (step < firmStep) {
    return (
      <div role="status" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", background: T.card, border: `1px solid ${T.stroke}`,
        borderRadius: 8, padding: "8px 14px", marginBottom: 14, fontFamily: FONT, fontSize: 13, color: T.fg2 }}>
        <span style={{ flex: 1 }}>You are using an earlier look than the rest of the firm.</span>
        <LinkButton onClick={onFollowFirm}>Move up with the firm</LinkButton>
      </div>
    );
  }
  const info = LOOK_STEPS[step];
  if (!info || !info.news || seen >= step) return null;
  return (
    <div role="region" aria-label="What's new" style={{ background: T.card, border: `1px solid ${T.brandStroke}`, borderLeft: `4px solid ${T.brand}`,
      borderRadius: 8, padding: "12px 16px", marginBottom: 14, fontFamily: FONT }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: T.fg1 }}>What's new</div>
      <p style={{ fontSize: 13, color: T.fg2, margin: "4px 0 10px", lineHeight: 1.5 }}>{info.news}</p>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <Button primary onClick={onSeen}>Got it</Button>
        <LinkButton onClick={onKeepPrevious}>Not yet — keep the previous look for me</LinkButton>
      </div>
    </div>
  );
};

export const BrandSettings = ({ api, onSaved, onError, practice = false }) => {
  const call = useCall(api);
  const [appearance, setAppearance] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    call("/practice/appearance").then(d => { if (alive) { setAppearance(d.appearance); setDraft(d.appearance.brandColor); } })
      .catch(e => { if (alive) setError(e.status === 404 ? "Brand colours need the latest middleware." : e.message); });
    return () => { alive = false; };
  }, [call]);

  const save = async (body) => {
    setSaving(true); setError("");
    try {
      const d = await call("/practice/appearance", { method: "POST", body });
      setAppearance(d.appearance);
      setDraft(d.appearance.brandColor);
      applyBrand(brandTokensFor(d.appearance, practice) || {});
      onSaved && onSaved(body.experience ? (body.experience === "classic" ? "Back to the classic look for everyone"
        : `${(LOOK_STEPS.find(l => l.key === body.experience) || {}).label || "The new look"} is on for everyone`) : "Brand colour saved");
    } catch (e) {
      setError(e.message);
      onError && onError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: T.card, border: `1px solid ${T.stroke}`, borderRadius: 10, padding: 16, maxWidth: 620, marginBottom: 20, fontFamily: FONT }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: T.fg1, marginBottom: 3 }}>Brand colour</div>
      <p style={{ fontSize: 12, color: T.fg4, margin: "0 0 12px" }}>
        The title bar, the main button on each screen, links and selection. Outlook blue unless you choose your own.
      </p>
      {appearance && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input type="color" aria-label="Brand colour" value={draft} onChange={e => setDraft(e.target.value.toUpperCase())}
            style={{ width: 40, height: 32, padding: 0, border: `1px solid ${T.strokeInput}`, borderRadius: R, background: T.card }} />
          <input aria-label="Brand colour hex" value={draft} onChange={e => setDraft(e.target.value)} style={{ ...inputStyle, width: 110 }} />
          <span style={{ display: "inline-flex", alignItems: "center", height: 32, padding: "0 12px", borderRadius: R, background: draft, color: T.card, fontWeight: 600, fontSize: 14 }}>Preview</span>
          <Button primary disabled={saving || draft === appearance.brandColor} onClick={() => save({ brandColor: draft })}>{saving ? "Saving…" : "Save"}</Button>
          {/* Back to the firm's starting colour: Outlook blue, or the firm's own
              default where it has one (Provey's red). */}
          {!appearance.isDefault && (
            <LinkButton onClick={() => save({ brandColor: "" })}>
              {appearance.defaultBrandColor ? `Use the default (${appearance.defaultBrandColor})` : "Use the default colour"}
            </LinkButton>
          )}
        </div>
      )}
      {/* **Accepted, and said in plain words.** A colour between 3:1 and 4.5:1
          against white reads plainly as a bold white label but is under the
          standard for small text; the firm may choose it, knowing. No ratio is
          shown: it means nothing to the person choosing. */}
      {/* The two looks start from different colours until the firm saves one. */}
      {appearance && appearance.isDefault && appearance.practiceBrandColor && appearance.practiceBrandColor !== appearance.brandColor && (
        <p style={{ fontSize: 12, color: T.fg3, margin: "8px 0 0" }}>
          {`The classic portal and client pages use ${appearance.brandColor}; the practice look uses Outlook blue until you save a colour.`}
        </p>
      )}
      {appearance && appearance.meetsAA === false && (
        <p style={{ fontSize: 12, color: T.fg3, margin: "8px 0 0" }}>
          This colour is clear on buttons and the title bar, but a little light for small text,
          so links use a slightly darker shade of it.
        </p>
      )}
      {appearance && <LogoSetting call={call} variant="dark" onSaved={onSaved} onError={onError} />}
      {appearance && <LogoSetting call={call} variant="white" onSaved={onSaved} onError={onError} />}
      {appearance && (
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${T.stroke}` }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: T.fg1, marginBottom: 3 }}>Look</div>
          <p style={{ fontSize: 12, color: T.fg4, margin: "0 0 8px" }}>
            Move the firm from the classic portal to the practice look one step at a time. Classic stays exactly as it is;
            each step adds one thing, and everyone sees a short note the first time. Anyone can keep the previous step for
            themselves while they settle in. Try a step in one tab first by adding <code>?ui=step-2</code> (or <code>?ui=practice</code>) to the address.
            The WordPress site moves with it.
          </p>
          <div role="radiogroup" aria-label="Look" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {LOOK_STEPS.map(({ key, label, news }) => (
              <label key={key} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13 }}>
                <input type="radio" name="portal-look" aria-label={label} checked={appearance.experience === key} disabled={saving}
                  onChange={() => {
                    if (key !== "classic" && !window.confirm(`Turn on "${label}" for everyone at the firm?`)) return;
                    save({ experience: key });
                  }} style={{ accentColor: T.brand, marginTop: 2 }} />
                <span><span style={{ fontWeight: 500, color: T.fg1 }}>{label}</span>
                  {news && <span style={{ display: "block", color: T.fg3, fontSize: 12 }}>{news}</span>}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      {error && <p role="alert" style={{ color: T.danger, fontSize: 12.5, margin: "8px 0 0" }}>{error}</p>}
    </div>
  );
};

// ── Opening a matter here ───────────────────────────────────────────────────
//
// New matter, from the title bar. The number is the middleware's — the firm's
// own counter, in a transaction — and is shown here only as what the next one
// will look like. VIC only, like the rest of the main module.
const CLIENT_ROLE_LABEL = { Purchase: "purchaser" };

export const NewMatterDialog = ({ api, onOpened, onClose }) => {
  const call = useCall(api);
  const { layout } = useLayout(call);
  const [matterType, setMatterType] = useState("Sale");
  const [address, setAddress] = useState("");
  const [client, setClient] = useState(null);
  const [picking, setPicking] = useState(false);
  const [numbering, setNumbering] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    call("/practice/numbering").then(d => { if (alive) setNumbering(d.numbering); }).catch(() => {});
    return () => { alive = false; };
  }, [call]);

  const open = async () => {
    setBusy(true); setError("");
    try {
      const d = await call("/practice/matters", { method: "POST", body: { matterType, address, ...(client ? { contactId: client.id } : {}) } });
      onOpened(d.matterRef, { address, client: client?.name || "", matterType });
    } catch (e) {
      setError(e.status === 404 ? "Opening a matter here needs the latest middleware." : e.message);
    } finally {
      setBusy(false);
    }
  };

  if (picking && layout) {
    return <ContactPicker call={call} layout={layout} roleLabel={`client (${CLIENT_ROLE_LABEL[matterType] || "vendor"})`}
      onClose={() => setPicking(false)} onPicked={(c) => { setClient(c); setPicking(false); }} />;
  }
  const types = layout?.matterTypes || ["Sale", "Purchase", "Transfer", "Other"];
  return (
    <Dialog title="New matter" onClose={onClose} width={560}
      footer={<>
        <Button onClick={onClose}>Cancel</Button>
        <Button primary disabled={busy} onClick={open}>{busy ? "Opening…" : "Open matter"}</Button>
      </>}>
      <Row label="State"><span style={{ fontSize: 14, alignSelf: "center" }}>Victoria</span></Row>
      <Row label="Matter type" htmlFor="new-matter-type">
        <select id="new-matter-type" value={matterType} onChange={e => setMatterType(e.target.value)} style={inputStyle}>
          {types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </Row>
      <Row label="Property address" htmlFor="new-matter-address">
        <TextIn id="new-matter-address" value={address} onChange={setAddress} placeholder="e.g. 9 Bedggood Avenue, Point Lonsdale VIC 3225" />
      </Row>
      <Row label="Client">
        {client ? <span style={{ fontSize: 14, alignSelf: "center", flex: 1 }}>{client.name}</span>
          : <span style={{ fontSize: 13, alignSelf: "center", flex: 1, color: T.fg4 }}>None yet — you can add them later</span>}
        <LinkButton onClick={() => setPicking(true)}>{client ? "Change" : "Choose"}</LinkButton>
      </Row>
      {numbering && (
        <p style={{ fontSize: 12.5, color: T.fg3, margin: "8px 0 0" }}>
          The next number will look like <strong style={{ color: T.fg1 }}>{numbering.nextExample}</strong> (shown for a sale). Set the format in Settings.
        </p>
      )}
      {error && <p role="alert" style={{ color: T.danger, fontSize: 13, margin: "8px 0 0" }}>{error}</p>}
    </Dialog>
  );
};

// The firm's matter-number shape, in Settings. What may and may not be chosen
// is the middleware's; this draws the switches and shows the next number.
// The firm's preset document folders, per matter type (`/practice/folders`).
// Which folders a sale or a purchase starts with is the firm's own practice:
// nothing in the portal names one. Until the firm saves its own the middleware
// serves its starter set (`starter: true`), and the first save replaces it.
export const FolderSettings = ({ api, onSaved, onError }) => {
  const call = useCall(api);
  const [sets, setSets] = useState(null);
  const [draft, setDraft] = useState({});
  const [type, setType] = useState("Sale");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const asText = (byType = {}, types = []) => Object.fromEntries(types.map(t => [t, (byType[t] || []).join("\n")]));
  useEffect(() => {
    let alive = true;
    call("/practice/folders").then(d => {
      if (!alive) return;
      setSets(d); setDraft(asText(d.byType, d.types));
      if (d.types && d.types.length && !d.types.includes("Sale")) setType(d.types[0]);
    }).catch(e => { if (alive) setError(e.status === 404 ? "Document folders need the latest middleware." : e.message); });
    return () => { alive = false; };
  }, [call]);

  const lines = (text) => String(text || "").split("\n").map(l => l.trim()).filter(Boolean);
  const changed = sets && sets.types.some(t => lines(draft[t]).join("\n") !== (sets.byType[t] || []).join("\n"));
  const save = async () => {
    setSaving(true); setError("");
    try {
      const byType = Object.fromEntries(sets.types.map(t => [t, lines(draft[t])]));
      const d = await call("/practice/folders", { method: "POST", body: { byType } });
      setSets(d); setDraft(asText(d.byType, d.types));
      onSaved && onSaved("Document folders saved");
    } catch (e) {
      setError(e.message);
      onError && onError(e.message);
    } finally {
      setSaving(false);
    }
  };
  const copyToAll = () => setDraft(d => Object.fromEntries(sets.types.map(t => [t, d[type] || ""])));

  return (
    <div role="group" aria-label="Document folders" style={{ background: T.card, border: `1px solid ${T.stroke}`, borderRadius: 10, padding: 16, maxWidth: 620, marginBottom: 20, fontFamily: FONT }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: T.fg1, marginBottom: 3 }}>Document folders</div>
      <p style={{ fontSize: 12, color: T.fg4, margin: "0 0 12px" }}>
        The folders each matter type starts with, in this order. One per line; a / makes a folder inside another.
        They are copied onto a matter when its documents are first opened, and are the matter's own from then on:
        a change here reaches matters set up afterwards, not ones already set up.
      </p>
      {sets && <>
        {sets.starter && <p style={{ fontSize: 12.5, color: T.fg2, margin: "0 0 10px" }}>These are the starter folders. Saving makes them your own.</p>}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
          <label htmlFor="folder-set-type" style={labelStyle}>Matter type</label>
          <select id="folder-set-type" value={type} onChange={e => setType(e.target.value)} style={{ ...inputStyle, width: 180 }}>
            {sets.types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <LinkButton onClick={copyToAll}>Use these for every type</LinkButton>
        </div>
        <textarea aria-label={`Folders for a ${type} matter`} value={draft[type] || ""} rows={9}
          onChange={e => { const v = e.target.value; setDraft(d => ({ ...d, [type]: v })); }}
          style={{ ...inputStyle, height: "auto", padding: "8px 10px", lineHeight: "20px", resize: "vertical" }} />
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 12 }}>
          <Button primary disabled={saving || !(changed || sets.starter)} onClick={save}>{saving ? "Saving…" : "Save"}</Button>
        </div>
      </>}
      {error && <p role="alert" style={{ color: T.danger, fontSize: 12.5, margin: "8px 0 0" }}>{error}</p>}
    </div>
  );
};

// The firm's stages per matter type (`/practice/stages`). A sale and a
// purchase are different journeys; a type the firm never saves keeps the
// product's starting set. One row per stage, not a box of lines, so renaming
// one keeps its key — and every matter on it — where it is.
export const StageSettings = ({ api, onSaved, onError }) => {
  const call = useCall(api);
  const [sets, setSets] = useState(null);
  const [draft, setDraft] = useState({});
  const [type, setType] = useState("Sale");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    call("/practice/stages").then(d => {
      if (!alive) return;
      setSets(d); setDraft(d.byType || {});
      if (d.types && d.types.length && !d.types.includes("Sale")) setType(d.types[0]);
    }).catch(e => { if (alive) setError(e.status === 404 ? "Matter stages need the latest middleware." : e.message); });
    return () => { alive = false; };
  }, [call]);

  const list = draft[type] || [];
  const setList = (next) => setDraft(d => ({ ...d, [type]: next }));
  const move = (i, by) => { const next = [...list]; const [s] = next.splice(i, 1); next.splice(i + by, 0, s); setList(next); };
  const same = (a = [], b = []) => a.length === b.length && a.every((s, i) => s.key === b[i].key && s.label === b[i].label);
  const changed = sets && sets.types.some(t => !same(draft[t], sets.byType[t]));
  const save = async () => {
    setSaving(true); setError("");
    try {
      // Only the types that differ from the starting set are the firm's own.
      const byType = Object.fromEntries(sets.types.map(t => [t, same(draft[t], sets.starting[t]) ? [] : (draft[t] || []).map(({ key, label }) => (key ? { key, label } : { label }))]));
      const d = await call("/practice/stages", { method: "POST", body: { byType } });
      setSets(d); setDraft(d.byType || {});
      onSaved && onSaved("Matter stages saved");
    } catch (e) {
      setError(e.message);
      onError && onError(e.message);
    } finally {
      setSaving(false);
    }
  };
  const small = { background: "none", border: "none", cursor: "pointer", color: T.fg3, padding: 4, borderRadius: R, display: "inline-flex", fontFamily: FONT };

  return (
    <div role="group" aria-label="Matter stages" style={{ background: T.card, border: `1px solid ${T.stroke}`, borderRadius: 10, padding: 16, maxWidth: 620, marginBottom: 20, fontFamily: FONT }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: T.fg1, marginBottom: 3 }}>Matter stages</div>
      <p style={{ fontSize: 12, color: T.fg4, margin: "0 0 12px" }}>
        The stages each matter type moves through, in order, shown under the Matter tab's buttons. Renaming a stage keeps
        its matters on it; removing one moves them to the nearest stage left.
      </p>
      {sets && <>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
          <label htmlFor="stage-set-type" style={labelStyle}>Matter type</label>
          <select id="stage-set-type" value={type} onChange={e => setType(e.target.value)} style={{ ...inputStyle, width: 180 }}>
            {sets.types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          {!same(list, sets.starting[type]) && <LinkButton onClick={() => setList(sets.starting[type])}>Use the starting stages</LinkButton>}
        </div>
        <ol aria-label={`Stages for a ${type} matter`} style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          {list.map((st, i) => (
            <li key={st.key || `new-${i}`} style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <span style={{ width: 20, fontSize: 12, color: T.fg4, textAlign: "right" }}>{i + 1}</span>
              <input aria-label={`Stage ${i + 1}`} value={st.label} maxLength={60}
                onChange={e => { const label = e.target.value; setList(list.map((x, j) => (j === i ? { ...x, label } : x))); }}
                style={{ ...inputStyle, flex: 1 }} />
              <button type="button" aria-label={`Move stage ${i + 1} up`} disabled={i === 0} onClick={() => move(i, -1)} style={small}><Icon d="M6 15l6-6 6 6" size={15} /></button>
              <button type="button" aria-label={`Move stage ${i + 1} down`} disabled={i === list.length - 1} onClick={() => move(i, 1)} style={small}><Icon d={ICON.down} size={15} /></button>
              <button type="button" aria-label={`Remove stage ${i + 1}`} disabled={list.length <= 2} onClick={() => setList(list.filter((_, j) => j !== i))} style={small}><Icon d={ICON.close} size={15} /></button>
            </li>
          ))}
        </ol>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 12 }}>
          <Button disabled={list.length >= 15} onClick={() => setList([...list, { label: "" }])}><Icon d={ICON.plus} size={14} />Add stage</Button>
          <Button primary disabled={saving || !changed || sets.types.some(t => (draft[t] || []).some(s => !String(s.label).trim()))} onClick={save}>{saving ? "Saving…" : "Save"}</Button>
        </div>
      </>}
      {error && <p role="alert" style={{ color: T.danger, fontSize: 12.5, margin: "8px 0 0" }}>{error}</p>}
    </div>
  );
};

export const NumberingSettings = ({ api, onSaved, onError }) => {
  const call = useCall(api);
  const [numbering, setNumbering] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    call("/practice/numbering").then(d => { if (alive) { setNumbering(d.numbering); setDraft(d.numbering); } })
      .catch(e => { if (alive) setError(e.status === 404 ? "Matter numbers need the latest middleware." : e.message); });
    return () => { alive = false; };
  }, [call]);

  const set = (key, value) => setDraft(d => ({ ...d, [key]: value }));
  const save = async () => {
    setSaving(true); setError("");
    try {
      const { typeCode, yearPrefix, restartYearly, startAt, digits } = draft;
      const d = await call("/practice/numbering", { method: "POST", body: { typeCode, yearPrefix, restartYearly, startAt: Number(startAt), digits: Number(digits) } });
      setNumbering(d.numbering); setDraft(d.numbering);
      onSaved && onSaved("Matter numbers saved");
    } catch (e) {
      setError(e.message);
      onError && onError(e.message);
    } finally {
      setSaving(false);
    }
  };
  const check = (key, label) => (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
      <input type="checkbox" checked={Boolean(draft[key])} onChange={e => set(key, e.target.checked)} style={{ accentColor: T.brand }} />{label}
    </label>
  );
  const changed = draft && numbering && ["typeCode", "yearPrefix", "restartYearly", "startAt", "digits"].some(k => String(draft[k]) !== String(numbering[k]));

  return (
    <div role="group" aria-label="Matter numbers" style={{ background: T.card, border: `1px solid ${T.stroke}`, borderRadius: 10, padding: 16, maxWidth: 620, marginBottom: 20, fontFamily: FONT }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: T.fg1, marginBottom: 3 }}>Matter numbers</div>
      <p style={{ fontSize: 12, color: T.fg4, margin: "0 0 12px" }}>
        For matters opened here with New matter. Matters from Triconvey keep their own numbers.
      </p>
      {draft && <>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {check("typeCode", "Start with the matter type — S sale, P purchase, T transfer, O other")}
          {check("yearPrefix", "Then the year, two digits")}
          {draft.yearPrefix && <span style={{ paddingLeft: 22 }}>{check("restartYearly", "Start the count again each 1 January")}</span>}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>Start at
              <input aria-label="Start at" type="number" min={1} value={draft.startAt} onChange={e => set("startAt", e.target.value)} style={{ ...inputStyle, width: 120 }} /></label>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>Digits
              <input aria-label="Digits" type="number" min={1} max={8} value={draft.digits} onChange={e => set("digits", e.target.value)} style={{ ...inputStyle, width: 70 }} /></label>
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: T.fg3 }}>Next: <strong style={{ color: T.fg1 }}>{numbering.nextExample}</strong></span>
          <Button primary disabled={saving || !changed} onClick={save}>{saving ? "Saving…" : "Save"}</Button>
        </div>
      </>}
      {error && <p role="alert" style={{ color: T.danger, fontSize: 12.5, margin: "8px 0 0" }}>{error}</p>}
    </div>
  );
};

export default MatterWorkspace;

// ── Search, in the middle of the title bar ──────────────────────────────────
//
// One box for the matter and the person, the way Outlook's search sits in its
// title bar. Matters are found in the list the portal already holds (by
// reference, client, address or agent — the dashboard's own fields); contacts
// are asked of the card file. Enter shows every matching matter on the list.
const matterHits = (matters = [], q = "") => {
  const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return [];
  return matters.filter((m) => {
    const hay = [m.ref, m.client, m.address, m.agent, m.fileHandlerName].filter(Boolean).join(" ").toLowerCase();
    return terms.every(t => hay.includes(t));
  }).slice(0, 6);
};
export { matterHits };

// `onLight`: in the classic white bar (step 1 of moving across) rather than
// on the brand-coloured title bar.
export const GlobalSearch = ({ api, matters = [], onOpenMatter, onOpenContact, onSearchAll, onLight = false }) => {
  const call = useCall(api);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState(-1);
  const box = useRef(null);
  const query = q.trim();
  const hits = useMemo(() => matterHits(matters, query), [matters, query]);

  useEffect(() => {
    if (query.length < 2) { setContacts([]); return undefined; }
    let live = true;
    const t = setTimeout(() => {
      call(`/practice/contacts?${new URLSearchParams({ q: query, pageSize: "5" })}`)
        .then(d => { if (live) setContacts(d.rows || []); })
        .catch(() => { if (live) setContacts([]); });
    }, 200);
    return () => { live = false; clearTimeout(t); };
  }, [call, query]);

  useEffect(() => {
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const items = [
    ...hits.map(m => ({ key: `m-${m.ref}`, kind: "matter", title: m.client || `Matter ${m.ref}`, sub: [m.ref, m.address].filter(Boolean).join(" · "), go: () => onOpenMatter(m) })),
    ...contacts.map(c => ({ key: `c-${c.id}`, kind: "contact", title: c.name, sub: [c.phone, c.email].filter(Boolean).join(" · "), go: () => onOpenContact && onOpenContact(c.id) })),
  ];
  const choose = (item) => { setOpen(false); setQ(""); setActive(-1); item.go(); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive(a => Math.min(a + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, -1)); }
    else if (e.key === "Escape") { setOpen(false); e.currentTarget.blur(); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0 && items[active]) choose(items[active]);
      else if (query) { setOpen(false); onSearchAll && onSearchAll(query); }
    }
  };

  return (
    <div ref={box} style={{ position: "relative", flex: "1 1 auto", maxWidth: 560, minWidth: 160, alignSelf: "center", WebkitAppRegion: "no-drag", ...(onLight ? { width: 320 } : {}) }}>
      <span style={{ position: "absolute", left: 10, top: 8, color: T.fg3, display: "inline-flex" }}><Icon d={ICON.search} size={16} /></span>
      <input type="search" aria-label="Search matters and contacts" placeholder="Search matters, contacts and addresses"
        value={q} onChange={e => { setQ(e.target.value); setOpen(true); setActive(-1); }} onFocus={() => setOpen(true)} onKeyDown={onKey}
        role="combobox" aria-expanded={open && query.length > 0} aria-controls="global-search-results" aria-autocomplete="list"
        style={{ width: "100%", boxSizing: "border-box", height: 32, padding: "0 10px 0 34px", borderRadius: onLight ? 999 : R, border: "none",
          background: onLight ? T.bg : "rgba(255,255,255,0.92)", color: T.fg1, fontFamily: FONT, fontSize: 14, outline: "none" }} />
      {open && query.length > 0 && (
        <div id="global-search-results" role="listbox" style={{ position: "absolute", top: 36, left: 0, right: 0, zIndex: 500, background: T.card,
          borderRadius: 8, boxShadow: SHADOW, padding: 4, fontFamily: FONT, maxHeight: 420, overflowY: "auto" }}>
          {[["matter", "Matters"], ["contact", "Contacts"]].map(([kind, label]) => {
            const group = items.filter(i => i.kind === kind);
            if (!group.length) return null;
            return (
              <div key={kind}>
                <div style={{ padding: "6px 10px 2px", fontSize: 11.5, fontWeight: 600, color: T.fg3 }}>{label}</div>
                {group.map((item) => {
                  const idx = items.indexOf(item);
                  return (
                    <button key={item.key} type="button" role="option" aria-selected={idx === active}
                      onMouseDown={e => e.preventDefault()} onClick={() => choose(item)} onMouseEnter={() => setActive(idx)}
                      style={{ display: "flex", gap: 10, alignItems: "center", width: "100%", textAlign: "left", padding: "6px 10px",
                        border: "none", borderRadius: R, cursor: "pointer", fontFamily: FONT, background: idx === active ? T.brandTint : "transparent", color: T.fg1 }}>
                      <span style={{ color: T.fg3, display: "inline-flex" }}><Icon d={kind === "matter" ? ICON.folder : ICON.person} size={15} /></span>
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: "block", fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title}</span>
                        {item.sub && <span style={{ display: "block", fontSize: 12, color: T.fg3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.sub}</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })}
          {items.length === 0 && <div style={{ padding: "8px 10px", fontSize: 13, color: T.fg3 }}>Nothing found for “{query}”.</div>}
          {hits.length > 0 && (
            <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => { setOpen(false); onSearchAll && onSearchAll(query); }}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", border: "none", borderTop: `1px solid ${T.strokeSubtle}`,
                background: "none", cursor: "pointer", fontFamily: FONT, fontSize: 13, color: T.brand }}>
              Show every matter matching “{query}”
            </button>
          )}
        </div>
      )}
    </div>
  );
};
